import React, { useState, useCallback, useEffect } from "react";
import IntegrationsTab from './IntegrationsTab';
import DrawingCompare from './DrawingCompare';
import DesignVsShopDrawing from './DesignVsShopDrawing';
import HouseRulesSettings from './HouseRulesSettings';
import FirmProfileSettings from './shell/FirmProfileSettings';
import BillingSettings from './shell/BillingSettings';
import TopBar from './shell/TopBar';
import LeftNav from './shell/LeftNav';
import ManagerSidebar from './shell/ManagerSidebar';
import ResultsPanel from './shell/ResultsPanel';
import OnboardingWalkthrough from './shell/OnboardingWalkthrough';
import CaPlusTab from './CaPlusTab';
import CaPlusIntakeTab from './CaPlusIntakeTab';


// Auth0 integration using vanilla JavaScript
const useAuth0 = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  // Auth0 configuration - CORRECT CLIENT ID
  const auth0Config = {
    domain: 'zonetap.auth0.com',
    clientId: '0qFAYaQp5tnadbGL8Y1xkqW2fTJFXiwM',
    redirectUri: window.location.origin
  };



  useEffect(() => {
    // Check for Auth0 callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code) {
      // Handle Auth0 callback
      handleAuth0Callback(code, state);
    } else {
      // Check if user is already logged in
      const token = localStorage.getItem('auth0_token');
      const storedUser = localStorage.getItem('auth0_user');
      
      if (token && storedUser) {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      }
      setIsLoading(false);
    }
  }, []);

  const handleAuth0Callback = async (code, state) => {
    try {
      // Verify state parameter for security
      const storedState = localStorage.getItem('auth0_state');
      if (state !== storedState) {
        throw new Error('Invalid state parameter');
      }

      // Exchange authorization code for tokens using Auth0's token endpoint
      const tokenResponse = await fetch(`https://${auth0Config.domain}/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grant_type: 'authorization_code',
          client_id: auth0Config.clientId,
          code: code,
          redirect_uri: auth0Config.redirectUri,
        }),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        console.error('Token exchange failed:', errorData);
        throw new Error('Token exchange failed');
      }

      const tokens = await tokenResponse.json();
      
      // Get user info using the access token
      const userResponse = await fetch(`https://${auth0Config.domain}/userinfo`, {
        headers: {
          Authorization: `Bearer ${tokens.access_token}`,
        },
      });

      if (!userResponse.ok) {
        throw new Error('Failed to get user info');
      }

      const userData = await userResponse.json();
      
      // Store the real user data
      localStorage.setItem('auth0_token', tokens.access_token);
      localStorage.setItem('auth0_user', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);
      
      // Clean up URL and state
      localStorage.removeItem('auth0_state');
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      console.error('Auth0 callback error:', err);
      // If the real Auth0 flow fails, we can fall back to a basic user object
      // using the email from the URL or create a minimal user profile
      const basicUser = {
        name: "User", // This will show the actual email if available
        email: "user@domain.com", // This should be replaced with actual data
        picture: "https://via.placeholder.com/32x32/D4AF37/000000?text=U"
      };
      
      localStorage.setItem('auth0_token', 'fallback_token_' + Date.now());
      localStorage.setItem('auth0_user', JSON.stringify(basicUser));
      setUser(basicUser);
      setIsAuthenticated(true);
      localStorage.removeItem('auth0_state');
      window.history.replaceState({}, document.title, window.location.pathname);
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithRedirect = () => {
    // Generate state parameter for security
    const state = Math.random().toString(36).substring(2);
    localStorage.setItem('auth0_state', state);
    
    // Build Auth0 authorization URL
    const authUrl = new URL(`https://${auth0Config.domain}/authorize`);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('client_id', auth0Config.clientId);
    authUrl.searchParams.set('redirect_uri', auth0Config.redirectUri);
    authUrl.searchParams.set('scope', 'openid profile email');
    authUrl.searchParams.set('state', state);
    authUrl.searchParams.set('audience', 'https://apispeccheck.zonetap.com');
    
    // Redirect to Auth0 for real authentication
    window.location.href = authUrl.toString();
  };

    const logout = () => {
    const userId = user?.email || user?.sub;
    if (userId) {
      fetch('https://apispeccheck.zonetap.com/api/session/end', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
        keepalive: true, // ensures the request completes even as the page navigates away
      }).catch(() => {});
    }
    localStorage.removeItem('auth0_token');
    localStorage.removeItem('auth0_user');
    localStorage.removeItem('auth0_state');
    localStorage.removeItem('zt_terms_accepted');
    setUser(null);
    setIsAuthenticated(false);
    
    // Redirect to Auth0 logout
    const logoutUrl = `https://${auth0Config.domain}/v2/logout?client_id=${auth0Config.clientId}&returnTo=${encodeURIComponent(auth0Config.redirectUri)}`;
    window.location.href = logoutUrl;
  };

  return {
    isLoading,
    error,
    isAuthenticated,
    user,
    loginWithRedirect,
    logout
  };
};

// Login Button Component
const LoginButton = ({ onClick }) => {
  return (
    <button 
      onClick={onClick}
      style={{
        backgroundColor: '#D4AF37',
        color: '#000000',
        fontWeight: '600',
        padding: '12px 24px',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
        transition: 'background-color 0.2s'
      }}
    >
      Login to Continue
    </button>
  );
};

// Logout Button Component
const LogoutButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      style={{
        backgroundColor: '#000000',
        color: '#D4AF37',
        fontWeight: '600',
        padding: '8px 16px',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'background-color 0.2s'
      }}
    >
      Logout
    </button>
  );
};

// Loading Spinner Component
const LoadingSpinner = () => (
  <div style={{
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid #f3f4f6',
      borderTop: '4px solid #D4AF37',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '16px'
    }}></div>
    <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// User Profile Component
const UserProfile = ({ user, onLogout }) => (
  <div style={{
    position: 'absolute',
    top: '20px',
    right: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: 'white',
    padding: '8px 12px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    zIndex: 100
  }}>
    {user.picture && (
      <img
        src={user.picture}
        alt={user.name}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%'
        }}
      />
    )}
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{
        fontSize: '14px',
        fontWeight: '600',
        color: '#000000'
      }}>
        {user.name}
      </span>
      <span style={{
        fontSize: '12px',
        color: '#6b7280'
      }}>
        {user.email}
      </span>
    </div>
    <LogoutButton onClick={onLogout} />
  </div>
);

// Terms of Service Modal Component
const TermsModal = ({ isOpen, onAccept, onDecline }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img 
            src="https://zonetap-ai.s3.us-east-1.amazonaws.com/AECWORKS-LOGO.png?format=1500w"
            alt="AECWorks AI Logo"
            style={{
              maxWidth: '150px',
              height: 'auto',
              marginBottom: '16px'
            }}
          />
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#000000',
            marginBottom: '8px'
          }}>
            Terms of Use - AEC Works AI Platform
          </h2>
        </div>

        <div style={{
          backgroundColor: '#fffbeb',
          border: '1px solid #D4AF37',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#000000',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            Important Notice
          </h3>
          
          <div style={{
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#374151',
            marginBottom: '16px'
          }}>
            <p style={{ marginBottom: '12px' }}>
              <strong>AI-Driven Analysis:</strong> AEC Works AI Platform uses artificial intelligence to analyze specification documents and generate reports. While our AI technology is advanced, the results are not guaranteed to be 100% accurate.
            </p>
            
            <p style={{ marginBottom: '12px' }}>
              <strong>User Responsibility:</strong> You must carefully review all analysis results to confirm their accuracy before making any decisions based on the output. The AI analysis should be considered as assistance, not a replacement for professional judgment.
            </p>
            
            <p style={{ marginBottom: '12px' }}>
              <strong>Limitation of Liability:</strong> AECWorks AI, Inc. is not liable for any damages, losses, or consequences arising from the use of AECWorks AI Platform applications or reliance on their results. Users assume full responsibility for verifying the accuracy of all analyses.
            </p>
            
            <p>
              <strong>Professional Review Required:</strong> All results should be reviewed by qualified professionals before implementation or decision-making.
            </p>
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center'
        }}>
          <button
            onClick={onDecline}
            style={{
              backgroundColor: '#6b7280',
              color: 'white',
              fontWeight: '600',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
          >
            Decline & Logout
          </button>
          <button
            onClick={onAccept}
            style={{
              backgroundColor: '#D4AF37',
              color: '#000000',
              fontWeight: '600',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#B8941F'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#D4AF37'}
          >
            I Accept & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// Login Screen Component
const LoginScreen = ({ onLogin }) => (
  <div style={{
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px'
  }}>
    <div style={{
      backgroundColor: 'white',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      borderRadius: '12px',
      padding: '48px',
      textAlign: 'center',
      maxWidth: '400px',
      width: '100%'
    }}>
      <img 
        src="https://zonetap-ai.s3.us-east-1.amazonaws.com/AECWORKS-LOGO.png"
        alt="AECWorks AI Logo"
        style={{
          maxWidth: '320px',
          height: 'auto',
          marginBottom: '32px'
        }}
      />
      <h1 style={{
        fontSize: '1.875rem',
        fontWeight: 'bold',
        color: '#000000',
        marginBottom: '8px'
      }}>
        AECWorks AI Platform
      </h1>
      <p style={{
        color: '#6b7280',
        fontSize: '16px',
        marginBottom: '32px'
      }}>
        Please login to access the AI tools.
      </p>
      <LoginButton onClick={onLogin} />
    </div>
  </div>
);

// All tabs — featureKey links to feature flag (null = always show)
const ALL_TABS = [
  { id: 'spec-check',      label: 'AW Spec Check',      icon: '✅', featureKey: 'specCheck'      },
  { id: 'spec-builder',    label: 'AW Spec Builder',     icon: '🔨', featureKey: 'specBuilder'    },
  { id: 'spec-update',     label: 'AW Spec Update',      icon: '🔄', featureKey: 'specUpdate'     },
  { id: 'rfi-check',       label: 'AW RFI Check',        icon: '❓', featureKey: 'rfiCheck'       },
  { id: 'shop-drawing-check',label: 'AW Package Check', icon: '🔧', featureKey: 'shopDrawingCheck' },
  { id: 'design-vs-shop',  label: 'AW Design vs. Shop',  icon: '📐', featureKey: 'designVsShopDrawing' },
  { id: 'drawing-compare', label: 'AW Drawing Compare',  icon: '🔍', featureKey: 'drawingCompare' },
  { id: 'track-changes',   label: 'AW Track Changes',    icon: '📝', featureKey: 'trackChanges'   },
  { id: 'ca-plus', label: 'CA+', icon: '📐', featureKey: 'caPlusSubmittals' },
  { id: 'integrations',    label: 'AW Integrations',     icon: '🔗', featureKey: 'integrations'   },
  { id: 'settings', label: 'Settings', icon: '⚙️', featureKey: null },
  { id: 'faq',             label: 'Help & FAQ',           icon: '❔', featureKey: null             },
];

const FUNCTION_TABS = ['settings', 'integrations', 'faq'];


// Usage counter bar — shows monthly usage for limited tiers
const UsageBar = ({ featureKey, getFlag, label }) => {
  if (!getFlag) return null;
  const flag = getFlag(featureKey);
  if (!flag || flag.monthlyLimit === 0) return null; // unlimited — hide
  const used = flag.usedThisMonth || 0;
  const limit = flag.monthlyLimit;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const isNearLimit = pct >= 80;
  const isAtLimit = used >= limit;
  return (
    <div style={{ marginTop: '8px', padding: '8px 12px', background: isAtLimit ? '#fef2f2' : '#f9fafb', borderRadius: '8px', border: '1px solid ' + (isAtLimit ? '#fecaca' : '#e5e7eb') }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <span style={{ fontSize: '11px', color: isAtLimit ? '#dc2626' : '#6b7280', fontWeight: '600' }}>
          {isAtLimit ? '⚠️ Monthly limit reached' : '📊 ' + label + ' usage this month'}
        </span>
        <span style={{ fontSize: '11px', fontWeight: '700', color: isAtLimit ? '#dc2626' : isNearLimit ? '#d97706' : '#374151' }}>
          {used}/{limit}
        </span>
      </div>
      <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: isAtLimit ? '#dc2626' : isNearLimit ? '#f59e0b' : '#D4AF37', borderRadius: '2px', transition: 'width 0.3s ease' }} />
      </div>
      {isAtLimit && <p style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px', marginBottom: 0 }}>Resets on the 1st of next month. Contact hello@aecworks.ai to upgrade.</p>}
    </div>
  );
};

// Tab Navigation Component
const TabNavigation = ({ activeTab, onTabChange, getFlag }) => {
const tabs = ALL_TABS.filter(t => {
  if (!t.featureKey) return true;
  const f = getFlag ? getFlag(t.featureKey) : { visible: true };
  return f.visible !== false;
});


  const scrollRef = React.useRef(null);
  const activeTabRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

React.useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, []);

  // Scroll the active tab into view whenever it changes
  React.useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({
        inline: 'center',
        block: 'nearest',
        behavior: 'auto'
      });
      // Defer arrow refresh to after the scroll completes
      setTimeout(updateArrows, 100);
    }
  }, [activeTab]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 200, behavior: 'smooth' });
    setTimeout(updateArrows, 300);
  };

  const arrowBtn = (dir, show) => ({
    position: 'absolute',
    [dir === -1 ? 'left' : 'right']: 0,
    top: 0, bottom: 0,
    width: '36px',
    display: show ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    background: show
      ? (dir === -1
        ? 'linear-gradient(to right, rgba(255,255,255,1) 60%, rgba(255,255,255,0))'
        : 'linear-gradient(to left, rgba(255,255,255,1) 60%, rgba(255,255,255,0))')
      : 'none',
    border: 'none',
    cursor: 'pointer',
    zIndex: 10,
    fontSize: '18px',
    color: '#D4AF37',
    fontWeight: '700',
    paddingBottom: '2px'
  });

  const styles = {
    tabContainer: {
      display: 'flex',
      borderBottom: '2px solid #e5e7eb',
      marginBottom: '32px',
      backgroundColor: 'white',
      borderRadius: '8px 8px 0 0',
      overflowX: 'auto',
      overflowY: 'hidden',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
    },
    tab: {
      flex: '0 0 auto',
      padding: '16px 20px',
      backgroundColor: '#f9fafb',
      border: 'none',
      cursor: 'pointer',
      fontSize: '15px',
      fontWeight: '600',
      color: '#6b7280',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      whiteSpace: 'nowrap'
    },
    activeTab: {
      backgroundColor: '#D4AF37',
      color: '#000000',
      borderBottom: '3px solid #B8941F'
    }
  };

  return (
    <>
      <style>{`.zt-tab-bar::-webkit-scrollbar{display:none}`}</style>
      <div style={{ position: 'relative', borderRadius: '8px 8px 0 0' }}>
        {/* Left arrow */}
        <button style={arrowBtn(-1, canScrollLeft)} onClick={() => scroll(-1)}>‹</button>

        <div
          ref={scrollRef}
          style={{ ...styles.tabContainer, paddingLeft: canScrollLeft ? '28px' : 0, paddingRight: canScrollRight ? '28px' : 0 }}
          className="zt-tab-bar"
          onScroll={updateArrows}
        >
          {tabs.map((tab) => {
            const flag = tab.featureKey && getFlag ? getFlag(tab.featureKey) : { enabled: true, limitReached: false };
            const locked = tab.featureKey && flag.enabled === false;
            const limited = tab.featureKey && flag.monthlyLimit > 0 && flag.limitReached;
            return (
              <button
                key={tab.id}
                ref={tab.id === activeTab ? activeTabRef : null}
                onClick={() => !locked && onTabChange(tab.id)}
                title={locked ? 'Upgrade your plan to access this feature' : limited ? 'Monthly limit reached' : ''}
                style={{
                  ...styles.tab,
                  ...(activeTab === tab.id ? styles.activeTab : {}),
                  ...(locked ? { opacity: 0.45, filter: 'grayscale(1)', cursor: 'not-allowed' } : {}),
                  ...(limited ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                }}
                onMouseOver={(e) => {
                  if (activeTab !== tab.id && !locked) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.color = '#374151';
                  }
                }}
                onMouseOut={(e) => {
                  if (activeTab !== tab.id && !locked) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.color = '#6b7280';
                  }
                }}
              >
                <span>{locked ? '🔒' : limited ? '⚠️' : tab.icon}</span>
                {tab.label}
                {tab.featureKey && flag.monthlyLimit > 0 && !locked && (
                  <span style={{
                    marginLeft: '4px', fontSize: '10px', fontWeight: '700',
                    background: flag.limitReached ? '#fee2e2' : '#f3f4f6',
                    color: flag.limitReached ? '#dc2626' : '#6b7280',
                    padding: '1px 5px', borderRadius: '10px', whiteSpace: 'nowrap'
                  }}>
                    {flag.usedThisMonth}/{flag.monthlyLimit}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Right arrow */}
        <button style={arrowBtn(1, canScrollRight)} onClick={() => scroll(1)}>›</button>
      </div>
    </>
  );
};

  // Group FAQ items by category
  const CATEGORY_FEATURE_MAP = {
  'AW Spec Check': 'specCheck',
  'AW Spec Builder': 'specBuilder',
  'AW Spec Update': 'specUpdate',
  'AW RFI Check': 'rfiCheck',
  'AW Package Check': 'shopDrawingCheck',
  'AW Design vs. Shop': 'designVsShopDrawing',
  'AW Drawing Compare': 'drawingCompare',
  'AW Track Changes': 'trackChanges',
  'AW Manager': 'managerMode',   // ← add this
  'Platform Integrations': 'integrations',
  'CA+': 'caPlusSubmittals',
};

// FAQ Section Component
  const FAQSection = ({ getFlag }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [faqSearch, setFaqSearch] = useState('');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const faqData = [
    // GENERAL SECTION
    {
      id: 'general-overview',
      title: 'How AW AI Tools Work',
      icon: '🤖',
      category: 'General',
      content: `AW AI Tools use advanced AI to analyze your construction documents — performing the same comprehensive review a human engineer would, but in seconds instead of hours.

How it works:
1. Upload your documents (specs, submittals, RFIs, drawings)
2. The AI analyzes content, cross-references requirements, and identifies issues
3. A detailed report is generated with actionable findings
4. Download as PDF (with color coding) or Word document

All tools use confidence scoring to indicate how certain the AI is about each finding, helping you prioritize what needs human review.`
    },
       {
      id: 'general-consultation',
      title: 'General Consultation',
      icon: '💬',
      category: 'General Consultation',
      content: `General Consultation, available in Manager Mode, is an open-ended way to work with the AI Manager — for questions and ad-hoc document review that don't fit neatly into one of the structured tools above.

Use it to:
- Ask general AEC questions — codes, standards, best practices — without uploading anything
- Attach a document and discuss it conversationally, rather than running a full formal review
- Get a quick second opinion on something before deciding which formal tool (if any) is the right fit

Unlike the other tools, General Consultation never automatically runs a formal analysis or generates a structured report on its own — it's built for discussion. If a formal report would genuinely help, the assistant will point you to the right tool, but the choice to run it is always yours.`
    },
    // AW SPEC CHECK SECTION
    {
      id: 'compliance-score',
      title: 'Compliance Score (Percentage)',
      icon: '📊',
      category: 'AW Spec Check',
      content: `The Compliance Score is the AI's overall assessment of how well the submittal aligns with the project specifications. This is a holistic evaluation, similar to what an experienced reviewer would determine after analyzing both documents.

The AI evaluates:
• Product & Material Specifications – Does the submitted product match what's called for?
• Manufacturer Requirements – If a specific manufacturer is specified, does the submittal comply?
• Technical Requirements – Do performance characteristics, dimensions, ratings, and certifications align?
• Documentation Completeness – Are required data sheets, test reports, and certifications provided?`
    },
    {
      id: 'status-colors',
      title: 'Status Indicators & Colors (PDF Reports)',
      icon: '🎨',
      category: 'AW Spec Check',
      content: `In the downloadable PDF report, individual line items are color-coded:

🟢 Green - COMPLIANT: The requirement is clearly satisfied by the submittal.
🔴 Red - NON-COMPLIANT: The requirement is not met or contradicts the specification.
🔴 Red - MISSING: Required documentation was not provided in the submittal.
🟠 Orange - PARTIAL: The submittal partially meets the requirement; needs clarification.
🔵 Blue - NEEDS REVIEW: Insufficient information; manual verification recommended.

Priority Levels:
• HIGH (H-001, etc.) - Red - Critical issues requiring immediate attention
• MEDIUM (M-001, etc.) - Orange - Important issues that should be addressed
• LOW (L-001, etc.) - Blue - Minor issues or clarifications needed`
    },
    {
      id: 'confidence-score',
      title: 'Confidence Score (1-10)',
      icon: '🎯',
      category: 'AW Spec Check',
      content: `Each finding includes a Confidence Score from 1 to 10, indicating how certain the AI is about that assessment.

🟢 8-10 (High Confidence): Clear specification language and sufficient submittal documentation.
🟠 5-7 (Moderate Confidence): Some ambiguity; human verification recommended.
🔴 1-4 (Low Confidence): Unclear requirements or insufficient information; professional review required.

Factors affecting confidence:
• Specification Clarity – How clearly the requirement is written
• Submittal Completeness – Whether all relevant documentation was provided
• Technical Complexity – More complex requirements may have lower confidence
• Cross-Reference Complexity – Multiple or conflicting specifications can reduce certainty`
    },
    {
      id: 'spec-conflicts',
      title: 'Specification Conflicts',
      icon: '⚠️',
      category: 'AW Spec Check',
      content: `When you upload multiple specification documents, the AI cross-references them to identify conflicting requirements.

What it catches:
• Contradictory requirements – Spec A says one thing, Spec B says something different
• Conflicting standards – Different specs reference different ASTM/UL/ANSI standards
• Mismatched tolerances or values – Conflicting dimensions, ratings, or performance requirements

Why it matters: On complex projects with multiple spec sections, conflicts can slip through unnoticed. The AI flags these so your team can resolve them before they become RFIs or field issues.

If you upload only one specification file, this section will show "No conflicts found."`
    },
    {
      id: 'missing-docs',
      title: 'Missing Documentation Checklist',
      icon: '📋',
      category: 'AW Spec Check',
      content: `This section identifies documents that the specification requires but were not included in the submittal package.

Required but Not Provided:
Documents explicitly called for in the spec that are completely absent (e.g., test reports, material certifications, welder certifications, warranty letters, shop drawings).

Incomplete Documentation:
Documents that were partially provided but are missing key information.

Why it matters: Missing documentation is one of the most common reasons for submittal rejection. This checklist helps you quickly identify what needs to be gathered before resubmission.`
    },
    {
      id: 'professional-review',
      title: 'Items Requiring Professional Review',
      icon: '👷',
      category: 'AW Spec Check',
      content: `Not everything can be definitively determined from document analysis alone. This section flags items that need human expertise.

Engineering Review Required:
Items where a licensed engineer should verify compliance. These typically involve:
• Complex calculations or load analysis
• Code interpretation questions
• Safety-critical components
• Items where the AI has moderate confidence (5-7)

Clarifications Needed:
Items where additional information from the contractor, manufacturer, or design team would resolve uncertainty.`
    },
    {
      id: 'qa-deficiencies',
      title: 'Quality Assurance & Testing Deficiencies',
      icon: '🔬',
      category: 'AW Spec Check',
      content: `This section focuses specifically on QA/QC documentation gaps:

Common items flagged:
• Mill certificates and material test reports
• Third-party testing documentation
• Warranty letters that don't meet spec requirements
• Quality control procedures and inspection records`
    },
    {
      id: 'analysis-notes',
      title: 'Reviewer Analysis Notes',
      icon: '📝',
      category: 'AW Spec Check',
      content: `This section provides transparency into how the AI performed its analysis.

Confidence Assessment:
Shows the overall reliability of the analysis, including factors like specification clarity, submittal completeness, technical complexity, and cross-referencing complexity.

Assumptions Made:
Lists any assumptions the AI made due to incomplete information. If you disagree with an assumption, you know to verify that item manually.

Process Improvement Recommendations:
Suggestions to improve future submittals.`
    },
    {
      id: 'final-recommendation',
      title: 'Final Professional Recommendation',
      icon: '✅',
      category: 'AW Spec Check',
      content: `The AI provides an overall recommendation based on the analysis:

APPROVE: Submittal meets all requirements; no action needed.

CONDITIONAL APPROVAL: Submittal is acceptable with specific conditions that must be met. Includes timeline and verification method for each condition.

REJECT & RESUBMIT: Significant issues require a new submittal. Lists exactly what needs to be fixed with spec references.

Follow-up Actions Required:
Action items for the project team after the submittal is processed (field verification, engineering review, trade coordination).`
    },
    // AW SPEC BUILDER SECTION
    {
      id: 'spec-builder-overview',
      title: 'What AW Spec Builder Creates',
      icon: '🔨',
      category: 'AW Spec Builder',
      content: `AW Spec Builder generates a comprehensive, job-specific specification outline based on your Master Spec Sheet and Template.

What's included in the generated outline:
• Detailed technical requirements based on your master specification
• Specific materials and components needed
• Manufacturer specifications and model numbers (when available)
• Performance criteria and testing requirements
• Installation procedures and quality control measures
• Relevant codes and standards compliance
• Professional construction industry formatting

How to use it:
1. Upload your Master Spec Sheet (your reference standard)
2. Upload your Template (the structure you want to follow)
3. Enter your required sections and project requirements
4. The AI generates a complete specification outline ready for customization`
    },
    {
      id: 'spec-builder-sections',
      title: 'Customizing Your Spec Sections',
      icon: '📑',
      category: 'AW Spec Builder',
      content: `When specifying required sections, be as detailed as possible for best results.

Example section list:
1. General Requirements and Scope
2. Products and Materials
3. Execution and Installation
4. Quality Control and Testing
5. Submittal Requirements
6. Warranty and Maintenance
7. Safety Requirements
8. Environmental Considerations

Tips for better results:
• List sections in the order you want them to appear
• Include specific component types you need covered
• Reference relevant CSI division numbers if applicable
• Be specific about materials and equipment types`
    },
    // AW SPEC UPDATE SECTION
    {
      id: 'spec-update-overview',
      title: 'What AW Spec Update Checks',
      icon: '🔄',
      category: 'AW Spec Update',
      content: `AW Spec Update analyzes your specification sheet to identify potential discrepancies with current manufacturer specifications.

What it identifies:
• Discontinued products that need replacement
• Updated product models or specifications
• Code compliance changes since the spec was written
• New technology alternatives that may be better options

Report sections:
• Products Requiring Updates – Table of items needing attention
• Manufacturer Status Analysis – Discontinued/updated products
• Code Compliance Updates – New requirements since spec was written
• New Technology Recommendations – Better alternatives available
• Recommended Specification Updates – Specific changes to make`
    },
    {
      id: 'spec-update-status',
      title: 'Product Status Indicators',
      icon: '🏭',
      category: 'AW Spec Update',
      content: `Products in your spec are flagged with status indicators:

CURRENT: Product is still available and meets current standards.

UPDATE REQUIRED: Product has been updated; spec should reference new model.

DISCONTINUED: Product is no longer manufactured; replacement needed.

NEEDS VERIFICATION: AI couldn't confirm status; manual check recommended.

Live Web Verification:
When enabled, the AI checks manufacturer websites in real-time for:
• Current product availability
• Updated datasheets and specifications
• Discontinued product notices
• Replacement product recommendations`
    },
    // AW RFI CHECK SECTION
    {
      id: 'rfi-check-overview',
      title: 'What AW RFI Check Analyzes',
      icon: '❓',
      category: 'AW RFI Check',
      content: `AW RFI Check analyzes your RFI against project documentation (and optionally architectural drawings) to help prepare responses.

What the report includes:
• RFI Summary – Overview of key questions and issues
• Question-by-Question Analysis – Each question evaluated individually
• Answerable from Documentation – Questions the docs can answer
• Answerable from Drawings – Questions resolved by visual review
• Clarification Needed – Questions requiring additional input
• Design/Coordination Concerns – Potential conflicts identified
• Draft Formal Responses – Ready-to-use response text

Each question is rated:
• Can Answer from Docs: Yes / No / Partial
• Confidence Level: High / Medium / Low
• Drawing Reference: Which drawing answers the question (if applicable)`
    },
    {
      id: 'rfi-visual-analysis',
      title: 'Visual Drawing Analysis',
      icon: '📐',
      category: 'AW RFI Check',
      content: `When you upload architectural drawings with your RFI, the AI performs visual analysis to find answers.

What it looks for:
• Dimensions and measurements relevant to RFI questions
• Detail callouts and annotations
• Material specifications shown on drawings
• Conflicts between drawing details and RFI questions
• Missing information that should be shown

Drawing types supported:
• PDF drawings
• JPEG, PNG, WebP images
• Multi-page PDF sets

The report will reference specific drawings when they contain answers to RFI questions.`
    },
    {
      id: 'rfi-cost-analysis',
      title: 'Cost & Schedule Impact',
      icon: '💰',
      category: 'AW RFI Check',
      content: `When Cost/Schedule Impact Analysis is enabled, the report includes:

Cost Implications:
• Potential material cost changes
• Labor impact estimates
• Change order likelihood (High/Medium/Low)

Schedule Implications:
• Potential delays identified
• Critical path impacts
• Lead time concerns for materials

Risk Assessment:
• High Risk Items – Could significantly impact project
• Medium Risk Items – Require attention but manageable
• Low Risk Items – Minor clarifications with minimal impact`
    },
    // AW TRACK CHANGES SECTION
    {
      id: 'track-changes-overview',
      title: 'What AW Track Changes Analyzes',
      icon: '📝',
      category: 'AW Track Changes',
      content: `AW Track Changes performs a gap analysis between a Master Specification and Customer Specification to identify what's missing.

What it compares:
• Section-by-section requirements
• Technical specifications and tolerances
• Material and product requirements
• Performance criteria
• Testing and commissioning requirements
• Code and regulatory compliance

Report includes:
• Gap Analysis Matrix – Every difference identified
• Critical Missing Requirements – Prioritized by risk
• Track Changes Analysis – Recent updates not in customer version
• Cost and Schedule Impact – Financial implications of gaps
• Recommended Actions – Specific steps to address gaps`
    },
    {
      id: 'track-changes-gaps',
      title: 'Understanding Gap Severity',
      icon: '🔴',
      category: 'AW Track Changes',
      content: `Gaps are categorized by severity:

🔴 HIGH Priority (Project Risk):
Missing requirements that could cause significant project issues, safety concerns, or code violations. Must be addressed before proceeding.

🟠 MEDIUM Priority (Quality/Compliance Risk):
Missing items that affect quality or compliance but won't halt the project. Should be addressed in next revision.

🟢 LOW Priority (Administrative):
Missing documentation or minor items with minimal project impact. Address when convenient.

The Overall Gap Assessment rates the entire comparison as High/Medium/Low Risk with a recommendation:
• APPROVE AS-IS – Customer spec is acceptable
• CONDITIONAL APPROVAL – Acceptable with specific additions
• MAJOR REVISIONS REQUIRED – Significant gaps must be addressed`
    },
    {
      id: 'track-changes-recommendations',
      title: 'Revision Recommendations',
      icon: '✏️',
      category: 'AW Track Changes',
      content: `The report provides specific text recommendations for updating the customer spec:

Required Additions:
Exact requirement text that should be added to specific sections of the customer spec.

Required Modifications:
Current text that should be changed, with before/after comparison and explanation of why.

Risk Mitigation Strategies:
For each risk level, specific mitigation actions with assigned owners and timelines.

Document Control:
Recommendations for version control and ensuring specs stay aligned going forward.`
    },

// AW PACKAGE CHECK SECTION
    {
      id: 'shop-drawing-overview',
      title: 'What AW Package Check Does',
      icon: '🔧',
      category: 'AW Package Check',
      content: `AW Package Check is designed as a "second set of eyes" for engineers reviewing contractor shop drawing submittals. It flags obvious red flags an experienced reviewer would catch, without replacing professional EOR judgment.

What it reviews:
- Routing & Layout — pipe/duct/conduit conflicts, clearances, slopes
- Connection Details — fitting types, joint methods, transitions
- Product Cut Sheets — model numbers, substitutions, accessories
- UL Listings & Code Ratings — required listings, fire/smoke ratings, NEC/NFPA references
- Dimensions & Clearances — code-required clearances, missing critical dimensions
- Mounting & Support — seismic bracing, anchor types, hanger details
- Trade Coordination — conflicts with other trades, missing stamps and signatures

How to use it:
1. Upload your shop drawing package (1-20 files — plans, details, cut sheets)
2. Enter the project name and select the trade (mechanical, electrical, plumbing, etc.)
3. Add optional reviewer notes if there's anything specific to focus on
4. Click "Review Shop Drawing"
5. Gemini analyzes the entire package and cross-references between files
6. Review the structured findings report grouped by category

The whole package is reviewed together, so the AI can catch inconsistencies between schedules, plans, and cut sheets — not just issues on a single sheet.`
    },
    {
      id: 'shop-drawing-multifile',
      title: 'Multi-File Package Review',
      icon: '📁',
      category: 'AW Package Check',
      content: `Real shop drawing submittals are rarely a single file — they typically include plan sheets, detail sheets, manufacturer cut sheets, and schedules. AW PackageCheck analyzes the entire package together so it can cross-reference between files.

What this catches that single-file review misses:
- Cut sheet shows Trane CSAA012 but the schedule on M-501 calls for CSAA014 (substitution mismatch)
- FSD-1 shown on M-401 has no UL listing in any of the attached cut sheets
- Hangers shown on the plan don't match the seismic restraint product cut sheet
- Dimensions on a detail don't match the corresponding plan view

Supported formats and limits:
- PDF, JPEG, PNG, WEBP, GIF
- Up to 20 files per submission
- 75 MB maximum per file
- Each file can be a single sheet or multi-page PDF

Best practice: Upload the entire submittal package in one go rather than file-by-file. The cross-referencing is where this tool earns its keep — it's the kind of catch a senior reviewer would make by laying everything out on the table.`
    },
    {
      id: 'shop-drawing-severity',
      title: 'Understanding Findings & Severity',
      icon: '🚦',
      category: 'AW Package Check',
      content: `Each finding is tagged with a severity level so you can prioritize what needs immediate attention.

🔴 Red Flag — Critical issues:
Items that will likely cause submittal rejection, code violations, or significant rework if not addressed. Example: missing seismic bracing on ductwork in a high-seismic zone, unverified UL listings on fire/smoke dampers.

🟡 Review — Issues requiring clarification:
Items that need EOR judgment or contractor clarification before approval. Example: AHU model substitution that may meet capacity but needs formal approval, dimensions that scale slightly tight on service clearances.

🔵 Info — Coordination notes:
Items worth noting but unlikely to block approval. Example: routing crossings that need verification against structural drawings, missing contractor signature stamps.

Each finding includes:
- Sheet number and grid/detail reference (when visible)
- A concise description of the issue
- Why it matters — what code section or spec applies
- What to ask the contractor to verify or provide

The Overall Risk badge (Low/Medium/High) gives you the top-level read on the entire package at a glance.`
    },
    {
      id: 'shop-drawing-categories',
      title: 'Review Categories Explained',
      icon: '📋',
      category: 'AW Package Check',
      content: `Findings are grouped into seven categories so you can scan the report quickly and route issues to the right team member.

Routing & Layout:
Pipe, duct, and conduit routing conflicts, clearances, and slopes. Catches obvious geometric problems and deviations from contract drawings when detectable.

Connection Details:
Fitting types, joint methods, and transitions. Flags missing or ambiguous connection callouts that could lead to field RFIs.

Product Cut Sheets:
Model numbers, manufacturer specifications, and accessory selections. Cross-references between schedules and submitted cut sheets to catch substitutions and missing options.

UL Listings & Code Ratings:
UL listings required for fire/smoke assemblies and electrical equipment, pressure ratings, fire-resistance ratings, and NEC/NFPA/IBC references.

Dimensions & Clearances:
Dimensional consistency across views, code-required clearances (NEC working space, mechanical service access), and missing critical dimensions.

Mounting & Support:
Seismic bracing requirements, support spacing, anchor types, and hanger/strut details. Particularly important in higher seismic design categories.

Trade Coordination:
Conflicts with architectural, structural, or other trades. Also flags missing submittal stamps, signatures, and contractor review.`
    },
    {
      id: 'shop-drawing-limitations',
      title: 'What AW Package Check Is NOT',
      icon: '⚠️',
      category: 'AW Package Check',
      content: `This tool is designed to be a second set of eyes — not a replacement for EOR review. Understanding what it does NOT do is as important as understanding what it does.

Not a substitute for engineering judgment:
The AI flags items that warrant attention, but every finding must be verified by a qualified reviewer. Code interpretation, project-specific requirements, and engineering calculations remain the EOR's responsibility.

Not a complete code compliance check:
The tool catches obvious red flags an experienced reviewer would notice. It does not perform exhaustive code analysis, structural calculations, or load verification.

Will not invent code sections:
The system is instructed to say "verify against project spec" rather than fabricate code references. If a finding cites a specific code section, treat it as a starting point for your own verification — not a guaranteed accurate citation.

Reviewer notes matter:
The optional notes field is your chance to focus the AI's attention. If you're particularly concerned about something specific (e.g., "verify all UL 555/555S listings on fire dampers" or "check seismic bracing on ducts >24 inches"), say so. The AI will prioritize those items in its review.

Engineers framing:
This tool was built specifically based on feedback from Real Engineers, who valued the framing of "a second set of eyes on obvious red flags." The findings are intentionally biased toward concrete, checkable items rather than vague AI commentary.`
    },

    // AW DESIGN VS. SHOP SECTION
    {
      id: 'design-vs-shop-overview',
      title: 'What AW Design vs. Shop Drawing Does',
      icon: '📐',
      category: 'AW Design vs. Shop',
      content: `AW Design vs. Shop Drawing performs a fidelity check between two distinct sets of documents: the engineer's design / contract drawings and the contractor's proposed shop drawings. It verifies that the contractor's proposed fabrication faithfully implements the engineer's design intent.

How it differs from AW PackageCheck:
- Shop Drawing Check reviews the shop drawing alone against trade norms and code
- Design vs. Shop cross-references the shop drawing against the original design intent
Both tools complement each other — many firms use both during a typical submittal review cycle.

What it identifies:
- Equipment Substitution — manufacturer or model differs from the basis of design
- Dimensional Discrepancy — pipe, duct, or conduit sizes that don't match the design schedule
- Performance & Capacity — CFM, GPM, voltage, or efficiency below the specified value
- Connection & Configuration — joint types or routing changed from design
- Code & Standards Compliance — UL listings, ASME ratings, or NFPA references missing
- Missing Details — seismic bracing, vibration isolation, or accessories the design required
- Coordination Conflicts — clearances reduced or trade conflicts introduced
- Spec Alignment — items where the shop drawing simply doesn't deliver what the spec called for

How to use it:
1. Upload your design / contract drawings on the left side (or load from your Drawing Vault)
2. Upload the contractor's shop drawings on the right side
3. Enter project name, trade, and any specific reviewer notes
4. Click "Review Shop Drawings vs. Design"
5. Review the structured findings — each one cites both a design reference AND a shop reference`
    },
    {
      id: 'design-vs-shop-recommendations',
      title: 'Understanding the Recommendation Levels',
      icon: '🚦',
      category: 'AW Design vs. Shop',
      content: `The overall recommendation uses standard MEP submittal review terminology — the same vocabulary you'd use when stamping a physical submittal.

🟢 No Exception Taken (green):
The shop drawing fully implements the design intent with no meaningful deviations. Approved as submitted.

🟡 Make Corrections Noted (amber):
The shop drawing has minor deviations the contractor can correct without resubmittal. Approved with the noted corrections.

🟠 Revise and Resubmit (red):
The shop drawing has meaningful deviations that require formal resubmittal. Contractor must address and resubmit.

⛔ Rejected (deep red):
Fundamental misalignment between design and shop. The submittal does not represent a good-faith implementation of the design intent.

Per-finding severity:
- 🔴 Red Flag — capacity, code, or substitution failures that must block approval
- 🟡 Review — deviations requiring engineer judgment or contractor clarification
- 🔵 Info — coordination notes worth flagging but not necessarily blocking

Each finding includes both a design reference AND a shop reference, so you can quickly verify the AI's interpretation against the source documents.`
    },
    {
      id: 'design-vs-shop-vault',
      title: 'Saving Design Drawings to the Vault',
      icon: '🗄️',
      category: 'AW Design vs. Shop',
      content: `Design drawings rarely change during construction, but a single project may have 20-50+ shop drawing submittals over its lifetime. The Drawing Vault lets you save your design drawings once and reuse them across every shop drawing review.

How to save design drawings to the vault:
1. Upload your design drawings on the left side
2. Check the "Save design drawings to Drawing Vault as baselines" checkbox
3. Run your first comparison — the design drawings are saved automatically with a "Design Baseline" tag
4. Files stay in the vault for 90 days

How to reuse saved baselines:
1. On a future shop drawing review, click "🗄 Drawing Vault" on the design side
2. Select one or more saved baselines (entries with the gold "Design Baseline" badge)
3. Upload the new shop drawings on the right
4. Run the comparison — no need to re-upload the design

Best practice: Save your "Issued for Construction" design set as the baseline once. Then for every subsequent shop drawing submittal on that project, pull from the vault instead of re-uploading. The vault saves significant time on multi-submittal projects.

Vault management:
- Vault entries are private to your account
- Click ✕ on any entry to remove it before the 90-day window
- Each card shows the project name, file name, and days remaining`
    },
    {
      id: 'design-vs-shop-tips',
      title: 'Tips for Best Review Results',
      icon: '💡',
      category: 'AW Design vs. Shop',
      content: `A few things to keep in mind to get the most out of Design vs. Shop reviews.

Match the trade carefully:
The Trade dropdown helps the AI focus on what matters for that discipline. Mechanical reviews emphasize CFM, capacity, and seismic bracing; electrical reviews emphasize UL listings, voltage, and NEC compliance; plumbing reviews emphasize pipe sizing, materials, and fixture units.

Use the Reviewer Notes field strategically:
This field is your chance to focus the AI's attention on specific concerns. Examples:
- "Confirm AHU-1 capacity matches the M-501 schedule"
- "Verify all FSD UL 555/555S listings"
- "Check seismic bracing on dampers per detail M-901"
- "Verify pipe sizing on the chilled water main"
The more specific the note, the more targeted the review.

Use complete document sets when possible:
The AI compares the whole package together, so cross-references between schedules and cut sheets matter. If your design set includes a schedule on M-501 and your shop drawing references that schedule, upload both — the AI will catch mismatches between the schedule values and the submitted equipment.

Don't worry about file count — within reason:
Up to 20 files per side, 75 MB per file. For a typical mechanical submittal, you might have 3-5 design sheets and 8-15 shop drawing files (cut sheets + plans + schedules). All in one go is fine.

Limitations to remember:
- The AI will not invent code section citations — if it cites a code, treat it as a starting point, not a guaranteed accurate reference
- Very small dimensional discrepancies (under 1/8") may not be detected reliably
- Visual changes on raster (scanned) drawings produce less precise results than vector PDFs
- Like every AECWorks AI tool, all findings must be verified by a qualified reviewer — this is a second set of eyes, not a replacement for EOR judgment`
    },
    {
      id: 'design-vs-shop-vs-other',
      title: 'When to Use Design vs. Shop vs. Other Tools',
      icon: '🤔',
      category: 'AW Design vs. Shop',
      content: `AECWorks AI has three tools that look similar at first glance — here's how to choose the right one.

Use AW Package Check when:
- You're reviewing a shop drawing in isolation against trade norms
- The question is "is this shop drawing internally consistent and up to standard?"
- You don't have the full design / contract drawings handy
- You want a focused review of routing, connections, dimensions, code compliance

Use AW Design vs. Shop Drawing when:
- You have BOTH the design / contract drawings AND the contractor's shop drawings
- The question is "does the shop drawing faithfully implement what the engineer specified?"
- You want to catch substitutions, capacity downgrades, or omissions
- You want a recommendation in standard MEP review terminology (No Exception Taken, etc.)

Use AW Drawing Compare when:
- You're comparing two versions of the SAME drawing (e.g., Rev 1 vs. Rev 2)
- The question is "what changed between these two revisions?"
- You want to track revisions to a single design over time
- You're looking for spatial, dimensional, or annotation changes

Quick decision guide:
- Two different documents from two different parties → Design vs. Shop
- Same document, two versions → Drawing Compare
- One document, no comparison → Shop Drawing Check`
    },

    
    // PLATFORM INTEGRATIONS SECTION
    {
      id: 'integrations-overview',
      title: 'Procore & Autodesk Direct Integrations',
      icon: '🔗',
      category: 'Platform Integrations',
      content: `AECWorks AI connects directly to Procore and Autodesk Construction Cloud, allowing you to pull files directly into any AECWorks AI tool without manual uploads.

How to connect:
1. Go to the AW Integrations tab
2. Click "Connect Procore" or "Connect Autodesk"
3. Log in to your platform account and authorize AECWorks AI
4. You're connected — your credentials are securely stored

Once connected, a "Platform Files" panel appears below "Recent Documents" on every AECWorks AI tool tab. Click to browse your projects and load any file directly into the appropriate upload slot.

Supported platforms:
• Procore — Browse projects, spec documents, and submittals
• Autodesk Construction Cloud — Browse hubs, projects, and files`
    },
    {
      id: 'integrations-file-browser',
      title: 'Using the Platform Files Browser',
      icon: '📂',
      category: 'Platform Integrations',
      content: `The Platform Files panel appears on every tool tab and lets you load files directly from your connected platforms.

How to use it:
1. Click "Platform Files" to expand the panel
2. Select your platform (Procore or Autodesk)
3. Choose a project from the list
4. Click any file — if the tool has multiple slots, choose which one (Specification, Submittal, Drawing, etc.)

Files are downloaded on demand directly from your platform — nothing is stored permanently in AECWorks AI .

Note: You must be connected to a platform in the AW Integrations tab before files will appear here.`
    },
    {
      id: 'integrations-automatic',
      title: 'Automatic Analysis via Webhooks',
      icon: '⚡',
      category: 'Platform Integrations',
      content: `AECWorks AI supports automatic analysis triggered by new submittals in your connected platforms.

How it works:
1. In AW Integrations, open the file browser and select a project
2. Enable "Auto-analyze new submittals" for that project
3. When a new submittal is uploaded in Procore or Autodesk, AECWorks AI  automatically downloads it, runs a full spec compliance analysis, and pushes the report back to your platform

Jobs triggered automatically are marked with an "AUTO" badge in the Job History section. This is ideal for high-volume projects.`
    },
    {
      id: 'recent-documents',
      title: 'Recent Documents — File Caching',
      icon: '📁',
      category: 'Platform Integrations',
      content: `Every file you upload during a AECWorks AI  analysis is automatically cached for 48 hours.

How it works:
• Files are securely stored in your personal cache for 48 hours after upload
• The "Recent Documents" panel at the top of every tool tab shows your cached files
• Click any cached file and select which upload slot to load it into

This lets you run multiple analyses on the same documents without re-uploading, or switch between tool tabs and reload your files instantly.

Files expire automatically after 48 hours — the remaining time is shown on each document card.

Note: Files loaded from Procore or Autodesk via Platform Files are NOT cached — they are downloaded fresh each time.`
    },
    // AW DRAWING COMPARE SECTION
    {
      id: 'drawing-compare-overview',
      title: 'What AW Drawing Compare Does',
      icon: '🔍',
      category: 'AW Drawing Compare',
      content: `AW Drawing Compare uses Gemini AI visual analysis to compare two versions of a construction drawing and identify every change between them.

What it detects:
• Spatial Changes — rooms added, removed, or reconfigured
• Structural Changes — walls, columns, openings added or removed
• Doors & Windows — location, size, or type changes
• MEP/Systems — mechanical, electrical, plumbing changes visible on drawings
• Dimensions & Annotations — changed dimensions, notes, labels, callouts
• Revision Clouds — revision cloud markers and delta symbols
• Title Block — sheet number, revision number, date, drawn by changes

How to use it:
1. Upload the new/current drawing
2. Upload the baseline/previous drawing (or select from Drawing Vault)
3. Optionally add project name and sheet number for the report
4. Click "Compare Drawings"
5. Gemini analyzes both drawings visually — takes 20-45 seconds
6. Review the structured change report with impact ratings`
    },
    {
      id: 'drawing-vault',
      title: 'Drawing Vault — Saving Baselines',
      icon: '🗄️',
      category: 'AW Drawing Compare',
      content: `The Drawing Vault stores your baseline drawings for up to 90 days, so you can compare future revisions without re-uploading the original.

How to save a baseline:
• Check "Save new drawing to Drawing Vault" before running a comparison
• Add a revision label (e.g. "Rev 2", "Issued for Construction") to identify it later
• The drawing is stored securely for 90 days

Managing your vault:
• Click "Drawing Vault" in the baseline panel to browse saved drawings
• Each card shows project name, sheet number, and days remaining
• Click ✕ on any card to delete it immediately — no need to wait out the 90 days
• Vault drawings are private to your account

Best practice: Save the "Issued for Construction" set as your baseline, then compare all future revision drawings against it.`
    },
    {
      id: 'drawing-compare-workflow',
      title: 'Using Drawing Compare with Procore & Autodesk',
      icon: '🔗',
      category: 'AW Drawing Compare',
      content: `If you have Procore or Autodesk connected, you can load drawings directly from your platform into the Drawing Compare slots — no manual download required.

How it works:
1. Go to AW Drawing Compare tab
2. Expand the "Platform Files" panel
3. Browse your connected platform and select the new drawing
4. Choose which slot to load it into: "New Drawing" or "Baseline Drawing"
5. Select your second drawing the same way or upload manually
6. Run the comparison

This is especially powerful for tracking revisions automatically — when a new drawing set is issued in Procore or Autodesk, you can compare it against your saved vault baseline in seconds.`
    },
    {
      id: 'drawing-compare-tips',
      title: 'Tips for Best Comparison Results',
      icon: '💡',
      category: 'AW Drawing Compare',
      content: `To get the most accurate results from AW Drawing Compare:

File quality:
• Use PDF format when possible — Gemini reads vector PDFs more accurately than raster images
• Higher resolution images produce better results for scanned drawings
• Avoid heavily compressed or low-resolution files

Drawing preparation:
• Compare one sheet at a time for best accuracy (multi-page PDFs will use the first page)
• Make sure both drawings are the same sheet/view for a meaningful comparison
• For large drawing sets, compare sheet by sheet

Understanding results:
• High Impact = changes that likely affect cost, schedule, or code compliance
• Medium Impact = changes requiring coordination or review
• Low Impact = minor annotation or administrative changes
• Always have a qualified professional verify AI findings before acting on them

Limitations:
• Very small dimension changes (under ~1/8") may not be detected reliably
• Heavily overlapping revision clouds on complex drawings may be partially missed
• Scanned/raster drawings produce less precise results than vector PDFs`
    },
    // HOUSE RULES SECTION
    {
      id: 'house-rules-overview',
      title: 'What Are House Rules?',
      icon: '📋',
      category: 'House Rules',
      content: `House Rules are firm-specific compliance checks that run automatically on every Spec Check, alongside the AI review. They catch the "obvious red flags" your team cares about — things like required ASTM standards, forbidden products, or mandatory sections — without relying on the AI to notice them.

How it works:
1. You define rules once in Settings → House Rules
2. Every spec check your team runs automatically evaluates those rules
3. Findings appear in a dedicated "House Rules" section at the bottom of each report
4. The AI review runs completely unchanged — House Rules are an additional layer, not a replacement

Who sees your rules:
• Rules are scoped to your firm's domain (e.g., everyone at yourfirm.com shares the same rule set)
• Different firms are completely isolated — no one outside your organization can see or edit your rules
• Anyone at your firm with access to the Settings tab can create, edit, or disable rules

Why it matters: The AI catches most compliance issues, but every firm has specific policies that are easy to miss in a busy review. House Rules gives you a deterministic second set of eyes — if you tell the system "always require ASTM C920 in section 07 92 00," it will flag every submittal that's missing it, every single time.`
    },
    {
      id: 'house-rules-types',
      title: 'The Four Rule Types',
      icon: '🔧',
      category: 'House Rules',
      content: `House Rules support four rule types, each designed to catch a different kind of issue.

Pattern Match — Flag when a section CONTAINS something it shouldn't
Use this when certain words, products, or standards should never appear in a specific section.
Example: Flag submittals that mention "lead-based paint" anywhere in section 09 91 00.

Pattern Absent — Flag when a section is MISSING required content
Use this when a section must always contain specific text, standards, or references.
Example: Section 07 92 00 (Joint Sealants) must always reference "ASTM C920." If it doesn't, the rule fires.

Section Required — Flag when an entire CSI section is missing
Use this when certain sections must always be present in submittals for your projects.
Example: "Section 01 33 00 (Submittal Procedures) must be included in every package." If it's missing, the rule fires.

Section Forbidden — Flag when a section appears that should not
Use this when certain sections should never be submitted for your projects.
Example: "Section 02 80 00 (Hazardous Materials Remediation) should never appear — if it does, the submittal was prepared for the wrong project."

Target scoping:
• For Pattern Match and Pattern Absent, you can scope the check to a specific CSI section (e.g., "07 92 00") or use "ALL" to search the entire document
• For Section Required and Section Forbidden, the target is the CSI section number itself
• CSI numbers can be entered with or without spaces — "079200" and "07 92 00" both work

Regex support: Pattern rules can use regular expressions if you check the "Treat pattern as regular expression" box. Useful for more complex matches like version numbers or alternate spellings.`
    },
    {
      id: 'house-rules-code-compliance',
      title: 'Code Compliance Rules & Preset Library',
      icon: '🏛️',
      category: 'House Rules',
      content: `Code Compliance is a specialized rule type designed for enforcing references to building codes and standards (Title 24, ASHRAE 90.1, NEC, NFPA, IBC, etc.) in specific spec sections.

How it differs from a regular pattern rule:
A code compliance rule uses three structured fields instead of a raw pattern — Code Standard (selected from a dropdown of 18 common standards), Code Section (e.g., "Section 140.4" or "110.26"), and Compliance Phrase (e.g., "minimum efficiency"). The system assembles these into a regex behind the scenes, so you don't have to think about regex syntax.

The preset library — your fastest way to get started:
At the top of Settings → House Rules, you'll find a "🏛️ Common Code Compliance Rules" panel with eight starter rules covering the most common MEP code citations:

Mechanical:
- California Title 24 — Mechanical Equipment Efficiency (target: 23 73 23)
- ASHRAE 90.1 — Energy Efficiency (target: 23 00 00)
- NFPA 90A — Fire Damper UL Listing (target: 23 33 13)
- Seismic Bracing per ASCE 7 / IBC (target: 23 05 48)

Electrical:
- NEC Working Space Requirement, Article 110.26 (target: 26 05 00)
- ASHRAE 90.1 — Lighting Power Density (target: 26 51 00)

Plumbing:
- Backflow Prevention per UPC/IPC (target: 22 11 00)

Fire Protection:
- NFPA 13 — Sprinkler System Design (target: 21 13 00)

How to use a preset:
1. Click any preset in the panel — the editor opens with all fields pre-populated
2. Edit anything you want to change (target section, severity, code section, phrase)
3. Click Save — the rule appears in your rules list with the rule type "🏛️ Code Compliance"

Important — presets are starting templates, not final rules:
The presets reflect common MEP code citations, but every firm has different standards, project types, and target sections. Treat them as a starting point and adjust to match your firm's actual requirements before relying on them in production.

Live regex preview:
While editing a code rule, you'll see a "Compiled Pattern (preview)" box that shows the exact regex the system will run. This is useful for power users who want to verify the assembled pattern. The preview updates live as you type.

Editing existing code rules:
When you click Edit on a saved code rule, the system reverse-parses the stored regex back into the structured fields (Code Standard, Section, Phrase). If your rule was hand-edited as raw regex, the parser does its best and may dump unparsed parts into the Phrase field — never lossy, just sometimes a little messy.`
    },
    {
      id: 'house-rules-findings',
      title: 'How Findings Appear in Reports',
      icon: '📊',
      category: 'House Rules',
      content: `When House Rules fire on a Spec Check, findings appear in a dedicated section at the bottom of the report — after the AI analysis, clearly separated so reviewers know exactly which findings came from firm policy versus AI analysis.

What you see when rules exist and fire:
• A "House Rules" header with the gold underline matching other report sections
• A one-line summary showing how many rules were evaluated and how many fired
• Findings grouped by severity (CRITICAL, WARNING, INFO) so the most important items surface first
• Each finding shows the rule name and a plain-English explanation of what was missing or forbidden

What you see when rules exist but none fire:
• A "House Rules" section with "All firm house rules passed. No findings."
• This confirms the checks ran — useful reassurance during the early rollout

What you see when no rules exist for your firm:
• No House Rules section at all — the report looks identical to pre-feature reviews
• This is intentional: if you haven't defined any rules, the feature stays completely out of the way

Severity levels:
• Critical — red flags that should block approval (e.g., forbidden materials, missing required sections)
• Warning — items requiring review but not necessarily blocking (e.g., missing standard references)
• Info — coordination notes and reminders

House Rules findings appear in both the on-screen report and the downloadable PDF, so you can share them with your team exactly like any other part of the review.`
    },
    {
      id: 'house-rules-tips',
      title: 'Tips for Writing Good Rules',
      icon: '💡',
      category: 'House Rules',
      content: `A few patterns we've seen work well when firms set up their first House Rules.

Start with your top 3-5 most common issues:
Don't try to encode your entire QA/QC process at once. Begin with the issues that come up most often in your reviews — the ones you or your team repeatedly have to flag by hand. Those give you the highest return on the time spent setting up rules.

Write rule names as questions or assertions:
• Good: "Joint sealants must reference ASTM C920"
• Good: "Hazardous materials section should not appear"
• Less clear: "Sealants check" or "Rule 1"
Clear names make findings self-explanatory when they appear in reports.

Use the Description field generously:
The description field is where you explain why the rule exists. This is invaluable six months later when someone else on your team wonders whether a rule is still needed. Example: "Our insurance requires ASTM C920 in all sealant submittals per policy signed March 2024."

Test your rules with real submittals:
After creating a rule, run it against a recent spec check you know the answer to. If the rule fires when it shouldn't, adjust the target section or pattern. If it doesn't fire when it should, check that your pattern matches how the text actually appears in the document.

Choose severity thoughtfully:
• Critical — reserve for issues that should actually block approval
• Warning — the right default for most rules
• Info — coordination notes and soft reminders
Over-using "Critical" dilutes its meaning; teams start ignoring it.

Use ALL as the target when in doubt:
If you're not sure which CSI section to scope a pattern rule to, use "ALL" to search the entire document. You can always tighten the scope later if you get false positives.

Disable, don't delete:
Use the toggle switch to temporarily disable a rule instead of deleting it. This is useful when a rule is producing noise on a specific project but you want to keep it for future reviews.

Current scope:
House Rules now runs on AW Spec Check, AW Spec Update, and AW Track Changes. Support for visual review tools (AW Package Check, AW Design vs. Shop) is on the roadmap based on pilot feedback.`
    },
    // TERMINOLOGY CUSTOMIZATION SECTION
    {
      id: 'terminology-overview',
      title: 'What Is Terminology Customization?',
      icon: '🏷️',
      category: 'Terminology',
      content: `Different firms use different vocabularies for the same review outcomes. AECWorks AI  defaults to the most common MEP industry terms, but Settings → Terminology lets your firm replace those defaults with your own house style.

Where customized labels appear:
- PDF reports (the version you hand to contractors)
- On-screen review banners in the app
- Anywhere the system displays a recommendation outcome

What you can customize today:
The Design vs. Shop Drawing Review tool's four recommendation labels:
- "No Exception Taken" — when the shop drawing fully implements the design
- "Make Corrections Noted" — minor deviations the contractor can correct
- "Revise and Resubmit" — meaningful deviations requiring formal resubmittal
- "Rejected" — fundamental misalignment with the design

How customization works:
- Each label has a default value that AECWorks AI  uses out of the box
- If you enter a custom value, that's what appears in your firm's reports
- If you leave it blank, the default is used — so you can override one label without overriding all four
- Customizations are scoped to your firm's domain — every reviewer at your firm sees the same labels`
    },
    {
      id: 'terminology-how-to-use',
     title: "How to Customize Your Firm's Labels",
      icon: '✏️',
      category: 'Terminology',
      content: `Customizing terminology takes about 60 seconds and applies firm-wide.

Step-by-step:
1. Go to Settings → Terminology
2. You'll see four cards, one for each Design vs. Shop recommendation label
3. Each card shows the AECWorks AI default at the top
4. Type your firm's preferred wording in the input field
5. As you type, the card border turns gold and a "CUSTOMIZED" badge appears — instant visual feedback
6. Click "Save Changes" to apply
7. The next Design vs. Shop review your firm runs will use your labels

Common alternatives we've seen firms use:
- "No Exception Taken" → "Approved", "Approved as Submitted", "No Exceptions"
- "Make Corrections Noted" → "Approved as Noted", "Approved with Comments", "Furnish as Corrected"
- "Revise and Resubmit" → "Not Approved — Revise and Resubmit", "Returned for Correction"
- "Rejected" → "Not Approved", "Not Acceptable", "Disapproved"

Each card shows a "Common alternatives" line as inspiration — these are just suggestions, you can use whatever wording your firm uses.

Resetting to defaults:
- Each customized field has a small "Reset" link next to it that clears just that one field back to the default
- "Reset to Defaults" at the top clears ALL customizations across your firm at once (with a confirmation prompt) — useful if you want to start fresh

Behind the scenes:
- Settings are saved per firm domain (e.g., everyone at yourfirm.com shares the same terminology)
- Empty fields automatically fall back to the AECWorks AI default — no need to fill in every field
- 80-character limit per label — enough for any reasonable terminology, short enough to fit cleanly in PDF banners

Note: Currently only the Design vs. Shop Drawing Review tool uses custom terminology. Other tools (Spec Check, Shop Drawing Check, etc.) use their existing fixed terminology — expanded support is on the roadmap based on pilot feedback.`
    },
    // AW MANAGER SECTION
    {
      id: 'manager-overview',
      title: 'What is AW Manager?',
      icon: '🤖',
      category: 'AW Manager',
      content: `AW Manager is an AI orchestration agent that lets you control all AW AI Tools through natural conversation. Instead of manually uploading files and clicking buttons, you simply describe what you need and AW Manager handles the rest.

Key capabilities:
• Run any AW AI tool by describing what you need in plain language
• Automatically detect and attach your cached documents by name
• Answer AEC industry questions directly from your uploaded documents
• Chain multiple tools together in sequence (Spec Check → Draft RFIs → Submittal Letter)
• Remember context from previous tool runs within a session
• Leverage your firm's Company Intelligence Profile for personalized output

Switch to Manager mode using the Traditional/Manager toggle in the top bar.`
    },
    {
      id: 'manager-how-to-use',
      title: 'How to Use AW Manager',
      icon: '💬',
      category: 'AW Manager',
      content: `Using AW Manager is as simple as having a conversation:

1. Switch to Manager mode using the toggle in the top bar
2. Attach your documents using the file drop zone, cached doc chips, or Platform Files button
3. Type what you need — for example:
   • "Run a spec check on the AHU submittal"
   • "Check if this submittal complies with Division 23 requirements"
   • "What are the casing requirements in this spec?"
   • "Draft RFIs for the critical issues found"

AW Manager will automatically route your request to the right tool, execute it, and show results in the center panel.

Tip: If your documents are cached from a previous session, just mention them by name — AW Manager will auto-attach them without any clicking.`
    },
    {
      id: 'manager-chains',
      title: 'Tool Chaining & Workflows',
      icon: '⛓️',
      category: 'AW Manager',
      content: `AW Manager can run multiple tools in sequence, creating a complete review workflow:

Typical Submittal Review Chain:
1. Spec Check — full compliance analysis against specifications
2. Draft RFIs — auto-generate RFI letters for all critical issues
3. Submittal Review Letter — formal review letter to send to the contractor
4. Package Check — verify shop drawings against the spec

After each tool completes, AW Manager suggests logical next steps. You can also request chains directly:
"Run a spec check then draft RFIs for any critical issues"

When multiple tools run in a chain, a Combined Report PDF is automatically offered — one professional document containing all analyses.

Chain availability depends on your subscription tier:
• Standard — single tool runs only
• Pro — chains of up to 3 tools
• Enterprise — unlimited chaining`
    },
    {
      id: 'manager-knowledge',
      title: 'AEC Knowledge Assistant',
      icon: '💡',
      category: 'AW Manager',
      content: `AW Manager doubles as an AEC industry expert. You can ask questions about:

• Construction specifications and CSI MasterFormat
• Building codes and standards (ASHRAE, IBC, NFPA, NEC, Title 24, SMACNA)
• MEP systems — mechanical, electrical, plumbing, fire protection
• Submittal review processes and best practices
• RFI management and contract administration
• Material standards and manufacturer requirements

Document Q&A — attach or select a cached document and ask questions about its specific content:
"What are the casing requirements in this spec?"
"What efficiency standards apply to this AHU?"
"Does this submittal address the drain pan requirements?"

AW Manager answers from your actual document content, not just general knowledge — making it more accurate than a generic AI assistant for project-specific questions.

Note: AW Manager stays focused on AEC topics and will politely redirect off-topic questions.`
    },
    {
      id: 'manager-firm-profile',
      title: 'Company Intelligence Profile',
      icon: '🏢',
      category: 'AW Manager',
      content: `The Company Intelligence Profile makes every AW AI Tools output feel like it came from your firm specifically.

Configure your firm's profile in Settings → Firm Profile:

• Company Info — name, location, license numbers, contact details
• Technical Standards — default codes (ASHRAE 90.1, Title 24, IBC), seismic zone, climate zone
• Preferred Manufacturers — manufacturers your firm always specifies
• Excluded Manufacturers — manufacturers your firm never uses
• Review Templates — custom submittal stamp language, RFI disclaimers, signature blocks

Once configured, AW Manager automatically:
• References your firm name in all reports and letters
• Flags submittals from excluded manufacturers
• Applies your preferred code standards to every review
• Uses your custom stamp language in submittal review letters

The more you configure, the more AW AI Tools feels like your firm's own AI reviewer.`
    },
    {
      id: 'manager-cached-docs',
      title: 'Cached Documents & Auto-Attach',
      icon: '📎',
      category: 'AW Manager',
      content: `AW AI Tools automatically caches every document you upload, making them instantly available for future sessions.

Working with cached documents:
• Cached docs appear as chips at the bottom of the Manager sidebar
• Click any chip to instantly attach that document from cloud storage
• Mention a document by name and AW Manager auto-attaches it

Example: "Run a spec check on the 633 Folsom AHU submittal" — AW Manager finds the matching cached document and attaches it automatically.

Documents are also accessible via:
• The Cached Docs section in the left navigation panel
• The Platform Files button (connect Procore or Autodesk in Integrations)

Cached documents are stored securely and available across sessions until you delete them.`
    },

    // CA+ SECTION
    {
      id: 'ca-plus-overview',
      title: 'What is CA+?',
      icon: '📋',
      category: 'CA+',
      content: `CA+ is an AI-based workflow for reviewing project Submittals, RFIs, and Field Observation Reports (FORs), built for Construction Administration (CA) teams.

CA+ uses AI to compare Submittals and RFIs against your project Specifications and Drawings, generating draft comments that reference the exact spec sections involved. Your reviewer edits and finalizes — the AI gives you a head start, not a final answer.

CA+ also builds a running database of CA activity across Submittals, RFIs, and Field Reports — replacing a manually maintained spreadsheet with status that's always current when you log in.`
    },
    {
      id: 'ca-plus-flow',
      title: 'How Items Get Reviewed',
      icon: '🔄',
      category: 'CA+',
      content: `Every Submittal or RFI follows the same basic flow:

1. The CA Administrator logs the item and assigns a reviewer
2. CA+ generates preliminary comments referencing the relevant spec sections
3. The reviewer opens the item, reviews the AI-drafted comments, edits as needed, and adds their own comments
4. For Submittals, the reviewer selects a shop drawing stamp action
5. The CA Administrator returns the completed item

You'll see only your own open items on your personal dashboard — not the full firm-wide list.`
    },
    {
      id: 'ca-plus-stamps',
      title: 'Shop Drawing Stamp Actions',
      icon: '✅',
      category: 'CA+',
      content: `When you finalize a Submittal, you'll select one of your firm's configured stamp actions (for example: No Exceptions Noted, Exceptions Noted — No Resubmission Required, Exceptions Noted — Resubmit for Record, or Revise and Resubmit).

For product submittals, the stamp is applied to the cover page only, with comments in a pre-formatted report.

For drawing submittals, the stamp is applied to each drawing sheet, with comments indicated directly on the drawings and also summarized in the report.`
    },
    {
      id: 'ca-plus-rfis',
      title: 'RFIs vs. Submittals',
      icon: '❓',
      category: 'CA+',
      content: `RFIs work similarly to Submittals but don't get a stamp — the reviewer finalizes their response and it's returned directly.

RFI drafts stay narrowly focused: CA+ answers based only on what's in your specs and drawings. It won't offer design opinions or judgment calls — if the RFI is asking something the documents don't answer, that's flagged for the reviewer rather than guessed at.`
    },
    {
      id: 'ca-plus-fors',
      title: 'Field Reports in CA+',
      icon: '🏗️',
      category: 'CA+',
      content: `Field Observation Reports in CA+ pull automatically from AECWorks Ops — every report your team creates in the field shows up here too, with no extra step. Click into any report to preview it inline or open the full PDF.

For older reports created before your firm connected Ops — or for firms that don't use Ops at all — a CA Administrator can log a Field Report manually from the CA+ Admin screen, with an optional PDF attachment.

Field Reports are shared across your whole team rather than assigned to one reviewer — anyone can open and review any report in the list.`
    },
    {
      id: 'ca-plus-admin-role',
      title: 'The CA Administrator Role',
      icon: '🗂️',
      category: 'CA+',
      content: `If you're a CA Administrator, you'll see a "Switch to Admin View" option inside the CA+ tab. This gives you the full firm-wide queue (not just your own items) and lets you log new Submittals and RFIs, assign a reviewer, and add new projects as they come in.

For Field Reports, the Admin view is also where you'd log a manual entry for older records, or edit and delete one you logged yourself — reports pulled automatically from Ops can't be edited or deleted here, since they're managed in Ops directly.

Only users your firm has designated as CA Administrators see this option — it's not visible to regular reviewers.`
    },
  ];

  const CATEGORY_FEATURE_MAP = {
    'AW Spec Check': 'specCheck',
    'AW Spec Builder': 'specBuilder',
    'AW Spec Update': 'specUpdate',
    'AW RFI Check': 'rfiCheck',
    'AW Package Check': 'shopDrawingCheck',
    'AW Design vs. Shop': 'designVsShopDrawing',
    'AW Drawing Compare': 'drawingCompare',
    'AW Track Changes': 'trackChanges',
    'AW Manager': 'managerMode',   // ← add this
    'Platform Integrations': 'integrations',
    'CA+': 'caPlusSubmittals',
    'General Consultation': 'generalChat',
  };

  const visibleFaqData = faqData.filter(item => {
    const featureKey = CATEGORY_FEATURE_MAP[item.category];
    if (!featureKey) return true;
    const flag = getFlag ? getFlag(featureKey) : { visible: true };
    return flag.visible !== false;
  });

    const searchedFaqData = faqSearch.trim()
    ? visibleFaqData.filter(item =>
        item.title.toLowerCase().includes(faqSearch.toLowerCase()) ||
        item.content.toLowerCase().includes(faqSearch.toLowerCase())
      )
    : visibleFaqData;

  const categories = [...new Set(searchedFaqData.map(item => item.category))];

  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '32px'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#000000',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: '16px'
    },
    categoryHeader: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#000000',
      marginTop: '28px',
      marginBottom: '16px',
      paddingBottom: '8px',
      borderBottom: '2px solid #D4AF37',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    faqItem: {
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: 'white'
    },
    faqHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      cursor: 'pointer',
      backgroundColor: '#f9fafb',
      transition: 'background-color 0.2s',
      border: 'none',
      width: '100%',
      textAlign: 'left'
    },
    faqHeaderActive: {
      backgroundColor: '#fffbeb',
      borderLeft: '4px solid #D4AF37'
    },
    faqTitleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    faqIcon: {
      fontSize: '20px'
    },
    faqTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#000000',
      margin: 0
    },
    faqChevron: {
      fontSize: '18px',
      color: '#D4AF37',
      transition: 'transform 0.2s'
    },
    faqContent: {
      padding: '20px',
      backgroundColor: 'white',
      borderTop: '1px solid #e5e7eb',
      color: '#374151',
      fontSize: '14px',
      lineHeight: '1.7',
      whiteSpace: 'pre-line'
    },
    importantNotes: {
      marginTop: '32px',
      padding: '20px',
      backgroundColor: '#fffbeb',
      border: '1px solid #D4AF37',
      borderRadius: '8px'
    },
    importantTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#000000',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    importantList: {
      margin: 0,
      paddingLeft: '20px',
      color: '#374151',
      fontSize: '14px',
      lineHeight: '1.8'
    }
  };

const categoryIcons = {
    'General': '🤖',
    'AW Spec Check': '✅',
    'AW Spec Builder': '🔨',
    'AW Spec Update': '🔄',
    'AW RFI Check': '❓',
    'AW Package Check': '🔧',
    'AW Design vs. Shop': '📐',
    'AW Drawing Compare': '🔍',
    'AW Track Changes': '📝',
    'House Rules': '📋',
    'Terminology': '🏷️',
    'Platform Integrations': '🔗',
    'CA+': '📋',
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Understanding Your Reports</h2>
        <p style={styles.subtitle}>
          Learn how to interpret compliance scores, status indicators, and report sections across all AW AI Tools
        </p>
      </div>

      <input
        type="text"
        value={faqSearch}
        onChange={(e) => setFaqSearch(e.target.value)}
        placeholder="Search FAQ..."
        style={{
          width: '100%',
          padding: '10px 14px',
          fontSize: '14px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          marginBottom: '20px',
          boxSizing: 'border-box'
        }}
      />

      {faqSearch.trim() && categories.length === 0 && (
        <p style={{ color: '#6b7280', textAlign: 'center', fontSize: '14px' }}>
          No results for "{faqSearch}" — try a different term.
        </p>
      )}

      {categories.map((category) => (
        <div key={category}>
          <div style={styles.categoryHeader}>
            <span>{categoryIcons[category]}</span>
            {category}
          </div>
            {searchedFaqData
              .filter(item => item.category === category)
              .map((item) => {
              const isExpanded = faqSearch.trim() ? true : expandedSection === item.id;
              return (
              <div key={item.id} style={styles.faqItem}>
                <button
                  onClick={() => toggleSection(item.id)}
                  style={{
                    ...styles.faqHeader,
                    ...(isExpanded ? styles.faqHeaderActive : {})
                  }}
                  onMouseOver={(e) => {
                    if (expandedSection !== item.id) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (expandedSection !== item.id) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                >
                  <div style={styles.faqTitleContainer}>
                    <span style={styles.faqIcon}>{item.icon}</span>
                    <span style={styles.faqTitle}>{item.title}</span>
                  </div>
                  <span style={{
                    ...styles.faqChevron,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ▼
                  </span>
                </button>
                {isExpanded && (
                  <div style={styles.faqContent}>
                    {item.content}
                  </div>
                )}
              </div>
              );
            })}
        </div>
      ))}

      <div style={styles.importantNotes}>
        <div style={styles.importantTitle}>
          <span>💡</span> Important Notes
        </div>
        <ul style={styles.importantList}>
          <li><strong>Reports show actionable items only</strong> — fully compliant items are not listed to keep reports focused.</li>
          <li><strong>AI analysis complements human review</strong> — designed to accelerate your workflow, not replace professional judgment.</li>
          <li><strong>Confidence scores help prioritize</strong> — use them to focus your team's time on items that need attention.</li>
          <li><strong>Color coding appears in PDF downloads</strong> — the web preview shows plain text; download the PDF for the full formatted report.</li>
        </ul>
      </div>
    </div>
  );
};

// Manufacturer Coverage Component
const ManufacturerCoverage = ({ onAddManufacturer, temporaryManufacturers = [] }) => {
  const [showAll, setShowAll] = useState(false);
  
  const permanentManufacturers = [
    { name: "Bell & Gossett", website: "https://bellgossett.com", category: "Pumps & Hydronic" },
    { name: "Xylem", website: "https://www.xylem.com", category: "Water Technology" },
    { name: "Taco", website: "https://www.tacocomfort.com", category: "Hydronic Systems" },
    { name: "Armstrong", website: "https://armstrongfluidtechnology.com", category: "Fluid Technology" },
    { name: "Victaulic", website: "https://www.victaulic.com", category: "Pipe Joining" },
    { name: "Watts", website: "https://www.watts.com", category: "Water Safety & Flow Control" },
    { name: "Grundfos", website: "https://www.grundfos.com", category: "Pump Solutions" },
    { name: "Trane", website: "https://www.trane.com", category: "HVAC Systems" },
    { name: "Daikin", website: "https://www.daikin.com", category: "Air Conditioning" },
    { name: "NIBCO", website: "https://www.nibco.com", category: "Flow Control" },
    { name: "Apollo", website: "https://www.apollovalves.com", category: "Valves" },
    { name: "Charlotte Pipe", website: "https://www.charlottepipe.com", category: "Pipe & Fittings" },
    { name: "Tyco", website: "https://www.tyco.com", category: "Fire Protection" },
    { name: "Siemens", website: "https://www.siemens.com", category: "Building Technologies" },
    { name: "Honeywell", website: "https://www.honeywell.com", category: "Building Controls" },
    { name: "Johnson Controls", website: "https://www.johnsoncontrols.com", category: "Building Solutions" }
  ];

  // Combine permanent and temporary manufacturers
  const allManufacturers = [...permanentManufacturers, ...temporaryManufacturers];
  const displayedManufacturers = showAll ? allManufacturers : allManufacturers.slice(0, 8);

  return (
    <div style={{
      marginBottom: '24px',
      padding: '20px',
      backgroundColor: '#f8f9fa',
      border: '1px solid #e5e7eb',
      borderRadius: '8px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#000000',
          margin: 0
        }}>
          Supported Manufacturers ({allManufacturers.length} total
          {temporaryManufacturers.length > 0 && ` - ${temporaryManufacturers.length} added this session`})
        </h3>
        <button
          onClick={onAddManufacturer}
          style={{
            backgroundColor: '#D4AF37',
            color: '#000000',
            fontWeight: '600',
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'background-color 0.2s'
          }}
        >
          + Add New Manufacturer
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '12px',
        marginBottom: '16px'
      }}>
        {displayedManufacturers.map((manufacturer, index) => (
          <div key={manufacturer.id || index} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 12px',
            backgroundColor: manufacturer.temporary ? '#fff7ed' : 'white', // Highlight temporary ones
            border: `1px solid ${manufacturer.temporary ? '#D4AF37' : '#e5e7eb'}`,
            borderRadius: '6px'
          }}>
            <div>
              <div style={{
                fontWeight: '600',
                color: '#000000',
                fontSize: '14px'
              }}>
                {manufacturer.name} {manufacturer.temporary && '(This Session)'}
              </div>
              <div style={{
                color: '#6b7280',
                fontSize: '12px'
              }}>
                {manufacturer.category}
              </div>
            </div>
            <a
              href={manufacturer.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#D4AF37',
                textDecoration: 'none',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Visit Site →
            </a>
          </div>
        ))}
      </div>

      <button
        onClick={() => setShowAll(!showAll)}
        style={{
          backgroundColor: 'transparent',
          color: '#D4AF37',
          fontWeight: '500',
          padding: '8px 16px',
          borderRadius: '6px',
          border: '1px solid #D4AF37',
          cursor: 'pointer',
          fontSize: '14px',
          transition: 'all 0.2s'
        }}
      >
        {showAll ? 'Show Less' : `Show All ${allManufacturers.length} Manufacturers`}
      </button>
    </div>
  );
};

// Add Manufacturer Modal Component
const AddManufacturerModal = ({ isOpen, onClose, onSubmit }) => {
  const [manufacturerName, setManufacturerName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!manufacturerName.trim() || !websiteUrl.trim()) {
      alert('Please fill in both manufacturer name and website URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(websiteUrl);
    } catch {
      alert('Please enter a valid website URL (including http:// or https://)');
      return;
    }
  
    setIsSubmitting(true);
    
    try {
      await onSubmit({
        name: manufacturerName.trim(),
        website: websiteUrl.trim(),
        category: category.trim() || 'General'
      });
      
      // Clear form and close modal
      setManufacturerName('');
      setWebsiteUrl('');
      setCategory('');
      onClose();
    } catch (error) {
      alert('Failed to add manufacturer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setManufacturerName('');
      setWebsiteUrl('');
      setCategory('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#000000',
          marginBottom: '20px'
        }}>
          Add New Manufacturer
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Manufacturer Name *
            </label>
            <input
              type="text"
              value={manufacturerName}
              onChange={(e) => setManufacturerName(e.target.value)}
              placeholder="e.g., Acme Manufacturing"
              required
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Website URL *
            </label>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://www.manufacturer.com"
              required
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Category (Optional)
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g., HVAC, Plumbing, Electrical"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                backgroundColor: '#6b7280',
                color: 'white',
                fontWeight: '600',
                padding: '12px 20px',
                borderRadius: '8px',
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: isSubmitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                backgroundColor: '#D4AF37',
                color: '#000000',
                fontWeight: '600',
                padding: '12px 20px',
                borderRadius: '8px',
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: isSubmitting ? 0.6 : 1
              }}
            >
              {isSubmitting ? 'Adding...' : 'Add Manufacturer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { isLoading, error, isAuthenticated, user, loginWithRedirect, logout } = useAuth0();

const [jobId, setJobId] = useState(null);
const [queueName, setQueueName] = useState(null);
const [jobStatus, setJobStatus] = useState(null);
const [pollingInterval, setPollingInterval] = useState(null);
const [queuePosition, setQueuePosition] = useState(0);
const [estimatedTime, setEstimatedTime] = useState('');
const [useQueue, setUseQueue] = useState(false); // Toggle between queue and direct processing

  
  // Active tab state
 // Active tab state — persisted across page refreshes
  const [activeTab, setActiveTab] = useState(() => {
    try {
      return localStorage.getItem('zt_active_tab') || 'spec-check';
    } catch {
      return 'spec-check';
    }
  });

  // Persist tab choice whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('zt_active_tab', activeTab);
    } catch {
      // localStorage might be disabled — fail silent, just lose persistence
    }
  }, [activeTab]);

  // UI mode (Traditional vs Manager) — persisted across page refreshes
  const [activeMode, setActiveMode] = useState(() => localStorage.getItem('zt_mode') || 'manager');
  const [managerKey, setManagerKey] = useState(0);

  // Persist mode choice whenever it changes
  useEffect(() => { localStorage.setItem('zt_mode', activeMode); }, [activeMode]);

  // Reset scroll position when switching between Traditional and Manager modes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeMode]);

  // Shared states for all tools
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // AW Spec Check states
const [specFiles, setSpecFiles] = useState([]); // Changed to array
  const [submittalFile, setSubmittalFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [specCheckMode, setSpecCheckMode] = useState('advanced');
  const [pdfBase64, setPdfBase64] = useState(null);
  const [filename, setFilename] = useState(null);
  const [processingError, setProcessingError] = useState(null);
  const [showClearButton, setShowClearButton] = useState(false);

  // AW Package Check states (lifted to survive remounts)
const [shopDrawingFiles, setShopDrawingFiles] = useState([]);
const [shopDrawingThumbnails, setShopDrawingThumbnails] = useState([]);
const [shopDrawingProjectName, setShopDrawingProjectName] = useState('');
const [shopDrawingTrade, setShopDrawingTrade] = useState('mechanical');
const [shopDrawingNotes, setShopDrawingNotes] = useState('');
const [shopDrawingFocusAreas, setShopDrawingFocusAreas] = useState([]);

  // AW Spec Builder states
  const [masterSpecFile, setMasterSpecFile] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [builderLoading, setBuilderLoading] = useState(false);
  const [builderReport, setBuilderReport] = useState(null);
  const [builderPdfBase64, setBuilderPdfBase64] = useState(null);
  const [builderFilename, setBuilderFilename] = useState(null);
  const [builderError, setBuilderError] = useState(null);
  const [showBuilderClearButton, setShowBuilderClearButton] = useState(false);
  const [requiredSections, setRequiredSections] = useState('');
  const [showSectionsDialog, setShowSectionsDialog] = useState(false);

  // AW Spec Update states
  const [updateSpecFile, setUpdateSpecFile] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateReport, setUpdateReport] = useState(null);
  const [updatePdfBase64, setUpdatePdfBase64] = useState(null);
  const [updateFilename, setUpdateFilename] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  const [showUpdateClearButton, setShowUpdateClearButton] = useState(false);
  const [enableWebCheck, setEnableWebCheck] = useState(false);

  // Manufacturer management states
  const [showAddManufacturerModal, setShowAddManufacturerModal] = useState(false);

  // AW RFI Check states
const [rfiFile, setRfiFile] = useState(null);
const [projectDocsFile, setProjectDocsFile] = useState(null);
const [rfiLoading, setRfiLoading] = useState(false);
const [rfiReport, setRfiReport] = useState(null);
const [rfiCheckMode, setRfiCheckMode] = useState('advanced');
const [rfiPdfBase64, setRfiPdfBase64] = useState(null);
const [rfiFilename, setRfiFilename] = useState(null);
const [rfiError, setRfiError] = useState(null);
const [showRfiClearButton, setShowRfiClearButton] = useState(false);
const [includeCostAnalysis, setIncludeCostAnalysis] = useState(false);

const [previewDrawingFile, setPreviewDrawingFile] = useState(null);
const [previewDrawingThumbnail, setPreviewDrawingThumbnail] = useState(null);
const [showDrawingPreview, setShowDrawingPreview] = useState(false);
const [drawingMousePosition, setDrawingMousePosition] = useState({ x: 0, y: 0 });


// Track Changes Spec Comparison states
const [trackChangesMasterFile, setTrackChangesMasterFile] = useState(null);
const [trackChangesCustomerFile, setTrackChangesCustomerFile] = useState(null);
const [trackChangesLoading, setTrackChangesLoading] = useState(false);
const [trackChangesReport, setTrackChangesReport] = useState(null);
const [trackChangesPdfBase64, setTrackChangesPdfBase64] = useState(null);
const [trackChangesFilename, setTrackChangesFilename] = useState(null);
const [trackChangesError, setTrackChangesError] = useState(null);
const [showTrackChangesClearButton, setShowTrackChangesClearButton] = useState(false);

  // Drag states
const [isSpecDragActive, setIsSpecDragActive] = useState(false);
const [isSubmittalDragActive, setIsSubmittalDragActive] = useState(false);
const [isMasterSpecDragActive, setIsMasterSpecDragActive] = useState(false);
const [isTemplateDragActive, setIsTemplateDragActive] = useState(false);
const [isUpdateSpecDragActive, setIsUpdateSpecDragActive] = useState(false);

const [progressStage, setProgressStage] = useState("");
const [progressDetails, setProgressDetails] = useState("");
const [estimatedChunks, setEstimatedChunks] = useState({ spec: 0, submittal: 0 });
const [currentComparison, setCurrentComparison] = useState({ spec: 0, submittal: 0, total: 0 });
const [isTrackChangesMasterDragActive, setIsTrackChangesMasterDragActive] = useState(false);
const [isTrackChangesCustomerDragActive, setIsTrackChangesCustomerDragActive] = useState(false);

  // Additional drag states for RFI
const [isRfiDragActive, setIsRfiDragActive] = useState(false);
const [isProjectDocsDragActive, setIsProjectDocsDragActive] = useState(false);

// Add these thumbnail states
const [specThumbnails, setSpecThumbnails] = useState([]); // Array of thumbnails
const [submittalThumbnail, setSubmittalThumbnail] = useState(null);
const [masterSpecThumbnail, setMasterSpecThumbnail] = useState(null);
const [templateThumbnail, setTemplateThumbnail] = useState(null);
const [updateSpecThumbnail, setUpdateSpecThumbnail] = useState(null);
const [rfiThumbnail, setRfiThumbnail] = useState(null);
const [projectDocsThumbnail, setProjectDocsThumbnail] = useState(null);
const [trackChangesMasterThumbnail, setTrackChangesMasterThumbnail] = useState(null);
const [trackChangesCustomerThumbnail, setTrackChangesCustomerThumbnail] = useState(null);

const [drawingFiles, setDrawingFiles] = useState([]);
const [drawingThumbnails, setDrawingThumbnails] = useState([]);
const [enableDrawingAnalysis, setEnableDrawingAnalysis] = useState(false);
const [isDrawingDragActive, setIsDrawingDragActive] = useState(false);

// Cached/Recent Documents states
const [cachedDocuments, setCachedDocuments] = useState([]);
const [vaultDrawings, setVaultDrawings] = useState([]);
const [cachedDocsLoading, setCachedDocsLoading] = useState(false);
const [showRecentDocs, setShowRecentDocs] = useState(false);
const [cachedDocsError, setCachedDocsError] = useState(null);
const [managerResults, setManagerResults] = useState({});
const [lastResultSummary, setLastResultSummary] = useState(null);
const [managerInputPrefill, setManagerInputPrefill] = useState('');
const [generalSidebarWidth, setGeneralSidebarWidth] = useState(() => {
  const saved = localStorage.getItem('generalSidebarWidth');
  return saved ? parseInt(saved, 10) : 360;
});
const handleGeneralSidebarWidthChange = (newWidth) => {
  setGeneralSidebarWidth(newWidth);
  localStorage.setItem('generalSidebarWidth', String(newWidth));
};
const sidebarWidthForLayout = (activeMode === 'manager' && activeTab === 'general') ? generalSidebarWidth : 360;
const [managerRunning, setManagerRunning] = useState(false);

// DrawingCompare file slots (loaded from recent docs / platform files)
const [drawingCompareNewFile, setDrawingCompareNewFile] = useState(null);
const [drawingCompareBaselineFile, setDrawingCompareBaselineFile] = useState(null);

// DesignVsShop file slots (loaded from recent docs / platform files)
const [designVsShopDesignFile, setDesignVsShopDesignFile] = useState(null);
const [designVsShopShopFile, setDesignVsShopShopFile] = useState(null);

// Shared integration status — fetched once after login
const [integrationStatus, setIntegrationStatus] = useState({ procore: null, autodesk: null });
const [featureFlags, setFeatureFlags] = useState(null);
const [licenseTier, setLicenseTier] = useState('Standard');
const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(null);
const [branding, setBranding] = useState(null);
const [showIdleWarning, setShowIdleWarning] = useState(false);
const [idleSecondsLeft, setIdleSecondsLeft] = useState(0);
const [tierLoaded, setTierLoaded] = useState(false);
const [appAccess, setAppAccess] = useState(null);

const fetchFeatureFlags = async (currentUser) => {
  const u = currentUser || user;
  if (!u?.email && !u?.sub) return;
  const userId = u.email || u.sub;
  try {
    const res = await fetch('https://apispeccheck.zonetap.com/api/features?userId=' + encodeURIComponent(userId));
    if (!res.ok) { setAppAccess(true); setFeatureFlags({}); setTierLoaded(true); return; }
    const data = await res.json();
    if (!data.success) { setAppAccess(true); setFeatureFlags({}); setTierLoaded(true); return; }
    setAppAccess(data.access !== false);
    setFeatureFlags(data.features || {});
    if (data.licenseTier) setLicenseTier(data.licenseTier);
    setSessionTimeoutMinutes(data.sessionTimeoutMinutes || null);
    setTierLoaded(true);
  } catch (err) {
    console.error('Failed to fetch feature flags:', err);
    setAppAccess(true);
    setFeatureFlags({});
    setTierLoaded(true);
  }
};

const fetchBranding = async (currentUser) => {
  const u = currentUser || user;
  if (!u?.email && !u?.sub) return;
  const userId = u.email || u.sub;
  try {
    const res = await fetch('https://apispeccheck.zonetap.com/api/ca-plus/branding?userId=' + encodeURIComponent(userId));
    if (!res.ok) return; // no branding available — leave the default AECWorks logo
    const data = await res.json();
    if (data.logo) setBranding(data);
  } catch (err) {
    console.error('Failed to fetch branding:', err);
    // non-fatal — default AECWorks logo stays in place
  }
};

const getFlag = (key) => {
  if (!featureFlags) return { enabled: true, visible: true, monthlyLimit: 0, usedThisMonth: 0, limitReached: false };
  const f = featureFlags[key];
  if (!f) return { enabled: true, visible: true, monthlyLimit: 0, usedThisMonth: 0, limitReached: false };
  return f;
};

const fetchIntegrationStatus = async (currentUser) => {
  const u = currentUser || user;
  if (!u?.email && !u?.sub) return;
  const userId = u.email || u.sub;
  try {
    const res = await fetch(`https://apispeccheck.zonetap.com/api/integrations/status?userId=${encodeURIComponent(userId)}`);
    if (!res.ok) return;
    const data = await res.json();
    const c = data.connections || {};
    setIntegrationStatus({ procore: c.procore || null, autodesk: c.autodesk || null });
  } catch (err) {
    console.error('Failed to fetch integration status:', err);
  }
};

  // Check if user has accepted terms
  useEffect(() => {
    if (isAuthenticated && !termsAccepted) {
      const hasAcceptedTerms = localStorage.getItem('zt_terms_accepted');
      if (hasAcceptedTerms === 'true') {
        setTermsAccepted(true);
      } else {
        setShowTermsModal(true);
      }
    }
  }, [isAuthenticated, termsAccepted]);

  // Fetch integration status + feature flags once after login
useEffect(() => {
    if (isAuthenticated && user) {
      fetchIntegrationStatus(user);
      fetchFeatureFlags(user);
      fetchVaultDrawings(user);
      fetchBranding(user);
    }
  }, [isAuthenticated, user]);

  const managerModeLocked = (() => {
    const tierLower = (licenseTier || '').toLowerCase();
    const tierQualifies = tierLower === 'pro' || tierLower === 'enterprise';
    const flagAllows = getFlag('managerMode').enabled !== false;
    return !tierQualifies || !flagAllows;
  })();

  useEffect(() => {
    if (tierLoaded && managerModeLocked && activeMode === 'manager') {
      setActiveMode('traditional');
    }
  }, [tierLoaded, managerModeLocked]);

  // ── Session tracking / idle timeout ──────────────────────────────────
  const WARNING_SECONDS_BEFORE_LOGOUT = 60;
  const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;
  const lastActivityRef = React.useRef(Date.now());
  const API_SESSION_BASE = 'https://apispeccheck.zonetap.com/api/session';

  // Call /session/start once, right when authentication becomes true —
  // covers all three setIsAuthenticated(true) call sites uniformly.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const userId = user.email || user.sub;
    if (!userId) return;
    fetch(`${API_SESSION_BASE}/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error === 'concurrent_limit_reached') {
          alert(data.message || 'This account has reached its simultaneous user limit.');
          logout();
        }
      })
      .catch(() => {});
  }, [isAuthenticated, user]);

  // Track real user activity — resets the idle clock and dismisses the
  // warning if it's showing.
  useEffect(() => {
    if (!isAuthenticated || !sessionTimeoutMinutes) return;
    const markActive = () => {
      lastActivityRef.current = Date.now();
      setShowIdleWarning((prev) => (prev ? false : prev));
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, markActive));
    return () => events.forEach((e) => window.removeEventListener(e, markActive));
  }, [isAuthenticated, sessionTimeoutMinutes]);

  // The actual idle-checking loop — skipped entirely when sessionTimeoutMinutes
  // is null/0, matching the "never expires" default.
  useEffect(() => {
    if (!isAuthenticated || !sessionTimeoutMinutes) return;
    const timeoutMs = sessionTimeoutMinutes * 60 * 1000;

    const interval = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      const remainingMs = timeoutMs - idleMs;

      if (remainingMs <= 0) {
        setShowIdleWarning(false);
        logout();
        return;
      }
      if (remainingMs <= WARNING_SECONDS_BEFORE_LOGOUT * 1000) {
        setShowIdleWarning(true);
        setIdleSecondsLeft(Math.ceil(remainingMs / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, sessionTimeoutMinutes]);

  // Periodic heartbeat — keeps the backend's session record (and the
  // concurrent-user count) accurate while this tab is genuinely open.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const userId = user.email || user.sub;
    if (!userId) return;
    const interval = setInterval(() => {
      fetch(`${API_SESSION_BASE}/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      }).catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  const stayLoggedIn = () => {
    lastActivityRef.current = Date.now();
    setShowIdleWarning(false);
    const userId = user?.email || user?.sub;
    if (userId) {
      fetch(`${API_SESSION_BASE}/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      }).catch(() => {});
    }
  };

  const handleAcceptTerms = () => {
    localStorage.setItem('zt_terms_accepted', 'true');
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  const handleDeclineTerms = () => {
    setShowTermsModal(false);
    logout();
  };

  // Add this function after handleDownloadWord
// Add PDF.js script to your HTML head:
// <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js"></script>

// Add these handlers for drawing files (add after your existing handlers):
const onDropDrawings = async (acceptedFiles) => {
  // Filter for supported image types - FIX: use file.type instead of file.mimetype
  const supportedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
  const validFiles = acceptedFiles.filter(file => supportedTypes.includes(file.type));
  
  if (validFiles.length !== acceptedFiles.length) {
    alert('Some files were skipped. Only JPEG, PNG, WEBP, GIF, and PDF files are supported for drawings.');
  }
  
  // Limit to 20 files (CHANGED FROM 10)
  const newFiles = [...drawingFiles, ...validFiles];
  if (newFiles.length > 20) {
    alert("Maximum 20 drawing files allowed. Only the first 20 will be used.");
    newFiles.splice(20);
  }
  
  setDrawingFiles(newFiles);
  
  // Generate thumbnails for new files
  const newThumbnails = await Promise.all(
    validFiles.map(file => generateThumbnail(file))
  );
  
  const allThumbnails = [...drawingThumbnails, ...newThumbnails];
  if (allThumbnails.length > 20) {
    allThumbnails.splice(20);
  }
  
  setDrawingThumbnails(allThumbnails);
  
  // Auto-enable drawing analysis when files are added
  if (newFiles.length > 0) {
    setEnableDrawingAnalysis(true);
  }
};

const removeDrawingFile = (indexToRemoveOrAction) => {
  if (indexToRemoveOrAction === 'all') {
    setDrawingFiles([]);
    setDrawingThumbnails([]);
    setEnableDrawingAnalysis(false);
  } else {
    const newFiles = drawingFiles.filter((_, index) => index !== indexToRemoveOrAction);
    const newThumbnails = drawingThumbnails.filter((_, index) => index !== indexToRemoveOrAction);
    setDrawingFiles(newFiles);
    setDrawingThumbnails(newThumbnails);
    
    // Disable analysis if no files remain
    if (newFiles.length === 0) {
      setEnableDrawingAnalysis(false);
    }
  }
};

// Add queue status polling function
const startJobPolling = useCallback((jobId, queueName) => {
  setJobId(jobId);
  setQueueName(queueName);
  
  const poll = async () => {
    try {
      const response = await fetch(`https://apispeccheck.zonetap.com/api/job-status/${queueName}/${jobId}`);
      if (response.ok) {
        const status = await response.json();
        setJobStatus(status);
        
        // If job is completed or failed, stop polling and show results
        if (status.state === 'completed') {
          clearPolling();
          handleJobCompletion(status.returnvalue, queueName);
        } else if (status.state === 'failed') {
          clearPolling();
          handleJobFailure(status.failedReason, queueName);
        }
        // Update progress for active jobs
        else if (status.state === 'active') {
          setProgressStage(`Processing... ${status.progress}%`);
          setProgressDetails(`Job is being processed by AI system`);
        }
        // Show waiting status
        else if (status.state === 'waiting') {
          setProgressStage('Waiting in queue...');
          setProgressDetails(`Position in queue: ${queuePosition || 'Unknown'}`);
        }
      }
    } catch (error) {
      console.error('Error polling job status:', error);
    }
  };

  // Poll every 2 seconds
  const interval = setInterval(poll, 2000);
  setPollingInterval(interval);
  
  // Initial poll
  poll();
}, [queuePosition]);


const clearPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    setPollingInterval(null);
  }
};

  // Clean up polling on component unmount
useEffect(() => {
  return () => {
    clearPolling();
  };
}, []);

// Handle job completion based on queue type
const handleJobCompletion = (result, queueName) => {
  switch (queueName) {
    case 'spec-check':
      setReport(result.report);
      setPdfBase64(result.pdfBase64);
      setFilename(result.filename);
      setShowClearButton(true);
      setLoading(false);
      break;
    
    case 'spec-builder':
      setBuilderReport(result.report);
      setBuilderPdfBase64(result.pdfBase64);
      setBuilderFilename(result.filename);
      setShowBuilderClearButton(true);
      setBuilderLoading(false);
      break;
      
    case 'spec-update':
      setUpdateReport(result.report);
      setUpdatePdfBase64(result.pdfBase64);
      setUpdateFilename(result.filename);
      setShowUpdateClearButton(true);
      setUpdateLoading(false);
      break;
      
    case 'rfi-check':
      setRfiReport(result.report);
      setRfiPdfBase64(result.pdfBase64);
      setRfiFilename(result.filename);
      setShowRfiClearButton(true);
      setRfiLoading(false);
      break;

    case 'track-changes':  // Add this new case
      setTrackChangesReport(result.report);
      setTrackChangesPdfBase64(result.pdfBase64);
      setTrackChangesFilename(result.filename);
      setShowTrackChangesClearButton(true);
      setTrackChangesLoading(false);
      break;
  }
  // Refresh feature flags to update usage counters
  if (user) fetchFeatureFlags(user);
};

const handleJobFailure = (error, queueName) => {
  switch (queueName) {
    case 'spec-check':
      setProcessingError(`Processing failed: ${error}`);
      setLoading(false);
      setShowClearButton(true);
      break;
      
    case 'spec-builder':
      setBuilderError(`Processing failed: ${error}`);
      setBuilderLoading(false);
      setShowBuilderClearButton(true);
      break;
      
    case 'spec-update':
      setUpdateError(`Processing failed: ${error}`);
      setUpdateLoading(false);
      setShowUpdateClearButton(true);
      break;
      
    case 'rfi-check':
      setRfiError(`Processing failed: ${error}`);
      setRfiLoading(false);
      setShowRfiClearButton(true);
      break;

    case 'track-changes':  // Add this new case
      setTrackChangesError(`Processing failed: ${error}`);
      setTrackChangesLoading(false);
      setShowTrackChangesClearButton(true);
      break;
  }
};


const generateThumbnail = async (file) => {
  if (file.type?.startsWith('image/')) {
    return URL.createObjectURL(file);
  }
  if (isProbablyPdf(file)) {
    const pdfThumb = await generatePDFThumbnail(file);
    if (pdfThumb) return pdfThumb;
  }
  return generateGenericThumbnail(file);
};

const generatePDFThumbnail = async (file) => {
  try {
    await ensurePdfjs(); // load + set worker src once
    return await generatePDFThumbnailInternal(file);
  } catch (e) {
    console.warn("PDF.js unavailable, falling back to generic thumbnail", e);
    return null;
  }
};

const generatePDFThumbnailInternal = (file) => {
  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = async () => {
      try {
        const pdf = await window.pdfjsLib.getDocument({ data: fr.result }).promise;
        const page = await pdf.getPage(1);

        // Choose a target thumbnail max size
        const maxW = 120;
        const maxH = 150;

        // Start with a base scale (1.0) to query the natural page size
        const baseViewport = page.getViewport({ scale: 1 });
        const scaleToFit = Math.min(maxW / baseViewport.width, maxH / baseViewport.height);

        // Final viewport we will actually render with
        const viewport = page.getViewport({ scale: scaleToFit });

        // Canvas sized EXACTLY to the render viewport
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);

        const ctx = canvas.getContext("2d", { willReadFrequently: false });

        await page.render({ canvasContext: ctx, viewport }).promise;

        // Clean up to avoid memory leaks
        try { page.cleanup?.(); } catch {}
        try { await pdf.cleanup?.(); } catch {}
        try { await pdf.destroy?.(); } catch {}

        resolve(canvas.toDataURL("image/png"));
      } catch (err) {
        console.error("PDF thumbnail generation failed:", err);
        resolve(null);
      }
    };
    fr.onerror = () => resolve(null);
    fr.readAsArrayBuffer(file);
  });
};


const generateGenericThumbnail = (file) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 120;
    canvas.height = 150;
    const ctx = canvas.getContext('2d');
    
    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 120, 150);
    
    // Border
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, 118, 148);
    
    // File type icon
    const isPDF = file.type === 'application/pdf';
    ctx.fillStyle = isPDF ? '#dc2626' : '#2563eb';
    ctx.fillRect(10, 10, 100, 30);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(isPDF ? 'PDF' : 'DOC', 60, 30);
    
    // File name (wrapped)
    ctx.fillStyle = '#374151';
    ctx.font = '10px Arial';
    ctx.textAlign = 'left';
    const fileName = file.name;
    const maxChars = 15;
    let yPos = 60;
    
    for (let i = 0; i < fileName.length; i += maxChars) {
      const line = fileName.substring(i, i + maxChars);
      ctx.fillText(line, 10, yPos);
      yPos += 12;
      if (yPos > 120) break; // Don't overflow
    }
    
    // File size
    ctx.fillStyle = '#6b7280';
    ctx.font = '9px Arial';
    const fileSize = (file.size / 1024 / 1024).toFixed(1) + ' MB';
    ctx.fillText(fileSize, 10, 140);
    
    resolve(canvas.toDataURL());
  });
};

let pdfjsReady;
function ensurePdfjs(version = "3.11.174") {
  if (pdfjsReady) return pdfjsReady;
  pdfjsReady = new Promise((resolve, reject) => {
    // Load core
    const core = document.createElement("script");
    core.src = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.min.js`;
    core.onload = () => {
      if (!window.pdfjsLib) {
        reject(new Error("pdfjsLib not found after load"));
        return;
      }
      // Point to the worker
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
      resolve(window.pdfjsLib);
    };
    core.onerror = () => reject(new Error("Failed to load PDF.js"));
    document.head.appendChild(core);
  });
  return pdfjsReady;
}

const isProbablyPdf = (file) => {
  if (!file) return false;
  if (file.type && file.type.includes("pdf")) return true;
  const ext = file.name?.toLowerCase().split(".").pop();
  return ext === "pdf";
};


// Enhanced file preview modal component with larger thumbnail and more details
const FilePreviewModal = ({ file, thumbnail, isVisible, position, onClose }) => {
  if (!isVisible || !file) return null;

  // Calculate modal position to keep it on screen
  const modalWidth = 400;
  const modalMaxHeight = 600;
  const screenPadding = 20;
  
  const modalLeft = Math.min(
    Math.max(position.x + 15, screenPadding), 
    window.innerWidth - modalWidth - screenPadding
  );
  
  const modalTop = Math.min(
    Math.max(position.y - 100, screenPadding), 
    window.innerHeight - modalMaxHeight - screenPadding
  );

  return (
    <div 
      style={{
        position: 'fixed',
        left: modalLeft,
        top: modalTop,
        backgroundColor: 'white',
        border: '3px solid #D4AF37',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 20px 48px rgba(0, 0, 0, 0.4)',
        zIndex: 1000,
        width: `${modalWidth}px`,
        maxHeight: `${modalMaxHeight}px`,
        overflowY: 'auto',
        pointerEvents: 'auto',
        transform: 'scale(1)',
        animation: 'fadeInScale 0.2s ease-out'
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.85);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#000000',
          margin: 0
        }}>
          File Preview
        </h3>
        <button
          onClick={onClose}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '4px',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#f3f4f6';
            e.target.style.color = '#374151';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = '#6b7280';
          }}
        >
          ×
        </button>
      </div>
      
      {thumbnail && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px',
          padding: '12px',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <img
            src={thumbnail}
            alt="File preview"
            style={{
              maxWidth: '300px',
              maxHeight: '400px',
              objectFit: 'contain',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              backgroundColor: 'white'
            }}
          />
        </div>
      )}
      
      <div style={{ 
        fontSize: '14px', 
        lineHeight: '1.6',
        backgroundColor: '#f8fafc',
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ 
          fontWeight: '700', 
          marginBottom: '12px', 
          wordBreak: 'break-word',
          color: '#1f2937',
          fontSize: '15px'
        }}>
          📄 {file.name}
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '8px',
          marginBottom: '12px'
        }}>
          <div style={{ color: '#374151' }}>
            <strong style={{ color: '#1f2937' }}>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
          </div>
          <div style={{ color: '#374151' }}>
            <strong style={{ color: '#1f2937' }}>Type:</strong> {file.type || 'Unknown'}
          </div>
        </div>
        
        <div style={{ color: '#374151', marginBottom: '8px' }}>
          <strong style={{ color: '#1f2937' }}>Last Modified:</strong>{' '}
          {new Date(file.lastModified).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
        
        {file.size && (
          <div style={{ color: '#374151', marginBottom: '8px' }}>
            <strong style={{ color: '#1f2937' }}>File Size Category:</strong>{' '}
            <span style={{
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              backgroundColor: file.size > 10 * 1024 * 1024 ? '#fef3c7' : 
                             file.size > 5 * 1024 * 1024 ? '#dbeafe' : '#dcfce7',
              color: file.size > 10 * 1024 * 1024 ? '#92400e' : 
                     file.size > 5 * 1024 * 1024 ? '#1e40af' : '#166534'
            }}>
              {file.size > 10 * 1024 * 1024 ? 'Large' : 
               file.size > 5 * 1024 * 1024 ? 'Medium' : 'Small'}
            </span>
          </div>
        )}
        
        {thumbnail && thumbnail.includes('data:image') && (
          <div style={{ 
            marginTop: '12px',
            padding: '8px',
            backgroundColor: '#ecfdf5',
            border: '1px solid #bbf7d0',
            borderRadius: '6px',
            color: '#065f46',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span style={{ fontSize: '16px' }}>✅</span>
            <span><strong>PDF Preview Generated</strong> - First page rendered successfully</span>
          </div>
        )}
      </div>
      
      <div style={{
        marginTop: '16px',
        fontSize: '12px',
        color: '#6b7280',
        textAlign: 'center',
        fontStyle: 'italic'
      }}>
        Click anywhere to close
      </div>
    </div>
  );
};

// Enhanced DropBox Component
const DropBoxComponent = ({ label, file, dropzone, styles, thumbnail = null }) => {
  const { getRootProps, getInputProps, isDragActive } = dropzone;
  const [showPreview, setShowPreview] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setShowPreview(true);
  };

  const handleMouseLeave = () => {
    setShowPreview(false);
  };

  return (
    <>
      <div
        {...getRootProps()}
        style={{
          ...styles.dropBox,
          ...(isDragActive ? styles.dropBoxActive : {}),
          position: 'relative'
        }}
      >
        <input {...getInputProps()} />
        <p style={styles.dropBoxLabel}>{label}</p>
        
        {file && thumbnail ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div 
              style={{
                position: 'relative',
                cursor: 'pointer'
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              title={`Click to preview ${file.name}`}
            >
              <img
                src={thumbnail}
                alt={`${file.name} thumbnail`}
                style={{
                  width: '80px',
                  height: '100px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  border: '2px solid #d1d5db',
                  transition: 'all 0.2s ease',
                  boxShadow: showPreview ? '0 8px 16px rgba(212, 175, 55, 0.3)' : 'none',
                  transform: showPreview ? 'scale(1.05)' : 'scale(1)'
                }}
              />
              {file.type === 'application/pdf' && thumbnail.includes('data:image') && (
                <div style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  ✓
                </div>
              )}
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#000000', 
                margin: '0 0 4px 0',
                wordBreak: 'break-word'
              }}>
                {file.name.length > 20 ? file.name.substring(0, 17) + '...' : file.name}
              </p>
              <p style={{ 
                fontSize: '12px', 
                color: '#6b7280', 
                margin: 0 
              }}>
                {(file.size / 1024 / 1024).toFixed(1)} MB
              </p>
            </div>
          </div>
        ) : (
          <p style={styles.dropBoxText}>
            {isDragActive 
              ? "Drop files here..." 
              : file 
                ? `${file.name}` 
                : "Drag & drop file here or click to select"
            }
          </p>
        )}
        
        {isDragActive && (
          <p style={{ 
            color: '#D4AF37', 
            fontSize: '12px', 
            marginTop: '8px',
            fontWeight: '500'
          }}>
            Release to upload
          </p>
        )}
      </div>
      
      <FilePreviewModal
        file={file}
        thumbnail={thumbnail}
        isVisible={showPreview}
        position={mousePosition}
        onClose={() => setShowPreview(false)}
      />
    </>
  );
};

// Updated DropBox component for multiple specs
// Updated MultiSpecDropBox component for multiple specs with preview functionality
const MultiSpecDropBox = ({ files, thumbnails, dropzone, styles, onRemove }) => {
  const { getRootProps, getInputProps, isDragActive } = dropzone;
  const [previewFile, setPreviewFile] = useState(null);
  const [previewThumbnail, setPreviewThumbnail] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e, file, thumbnail) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setPreviewFile(file);
    setPreviewThumbnail(thumbnail);
    setShowPreview(true);
  };

  const handleMouseLeave = () => {
    setShowPreview(false);
    setPreviewFile(null);
    setPreviewThumbnail(null);
  };

  return (
    <>
      <div
        {...getRootProps()}
        style={{
          ...styles.dropBox,
          ...(isDragActive ? styles.dropBoxActive : {}),
          minHeight: files.length > 0 ? 'auto' : '120px',
          padding: files.length > 0 ? '16px' : '24px'
        }}
      >
        <input {...getInputProps()} />
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: files.length > 0 ? '16px' : '12px' 
        }}>
          <p style={styles.dropBoxLabel}>
            Specification Documents ({files.length}/30)
          </p>
          {files.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                // This should call your clear function from parent
                onRemove('all'); // You'll need to handle this special case
              }}
              style={{
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Clear All
            </button>
          )}
        </div>

        {files.length === 0 ? (
          <p style={styles.dropBoxText}>
            {isDragActive 
              ? "Drop specification files here..." 
              : "Drag & drop specification files here or click to select (up to 30 files)"
            }
          </p>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: '12px',
            width: '100%'
          }}>
            {files.map((file, index) => (
              <div key={`${file.name}-${index}`} style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '8px',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(index);
                  }}
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}
                >
                  ×
                </button>
                
                {thumbnails[index] && (
                  <div
                    style={{
                      position: 'relative',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => handleMouseEnter(e, file, thumbnails[index])}
                    onMouseLeave={handleMouseLeave}
                    title={`Click to preview ${file.name}`}
                  >
                    <img
                      src={thumbnails[index]}
                      alt={`${file.name} thumbnail`}
                      style={{
                        width: '60px',
                        height: '75px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '2px solid #d1d5db',
                        marginBottom: '8px',
                        transition: 'all 0.2s ease',
                        boxShadow: showPreview && previewFile === file ? '0 8px 16px rgba(212, 175, 55, 0.3)' : 'none',
                        transform: showPreview && previewFile === file ? 'scale(1.05)' : 'scale(1)'
                      }}
                    />
                    {file.type === 'application/pdf' && thumbnails[index].includes('data:image') && (
                      <div style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        backgroundColor: '#22c55e',
                        color: 'white',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                )}
                
                <div style={{ textAlign: 'center' }}>
                  <p style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: '#000000', 
                    margin: '0 0 2px 0',
                    wordBreak: 'break-word',
                    lineHeight: '1.2'
                  }}>
                    {file.name.length > 15 ? file.name.substring(0, 12) + '...' : file.name}
                  </p>
                  <p style={{ 
                    fontSize: '10px', 
                    color: '#6b7280', 
                    margin: 0 
                  }}>
                    {(file.size / 1024 / 1024).toFixed(1)}MB
                  </p>
                </div>
              </div>
            ))}
            
            {files.length < 30 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                backgroundColor: '#f0f9ff',
                border: '2px dashed #0ea5e9',
                borderRadius: '6px',
                cursor: 'pointer',
                minHeight: '100px'
              }}>
                <p style={{
                  color: '#0ea5e9',
                  fontSize: '24px',
                  margin: '0 0 4px 0'
                }}>+</p>
                <p style={{
                  color: '#0ea5e9',
                  fontSize: '11px',
                  textAlign: 'center',
                  margin: 0,
                  lineHeight: '1.2'
                }}>
                  Add More Specs
                </p>
              </div>
            )}
          </div>
        )}
        
        {isDragActive && (
          <p style={{ 
            color: '#D4AF37', 
            fontSize: '12px', 
            marginTop: '8px',
            fontWeight: '500'
          }}>
            Release to upload additional specs
          </p>
        )}
      </div>
      
      {/* Preview Modal for Multi-Spec */}
      <FilePreviewModal
        file={previewFile}
        thumbnail={previewThumbnail}
        isVisible={showPreview}
        position={mousePosition}
        onClose={() => setShowPreview(false)}
      />
    </>
  );
};

// Updated file drop callbacks to use enhanced thumbnail generation
// Updated drop handler for multiple specs
const onDropSpec = async (acceptedFiles) => {
  // Add new files to existing ones
  const newFiles = [...specFiles, ...acceptedFiles];
  
  // Limit to 30 files maximum
  if (newFiles.length > 30) {
    alert("Maximum 30 specification files allowed. Only the first 30 will be used.");
    newFiles.splice(30);
  }
  
  setSpecFiles(newFiles);
  
  // Generate thumbnails for new files
  const newThumbnails = await Promise.all(
    acceptedFiles.map(file => generateThumbnail(file))
  );
  
  const allThumbnails = [...specThumbnails, ...newThumbnails];
  if (allThumbnails.length > 30) {
    allThumbnails.splice(30);
  }
  
  setSpecThumbnails(allThumbnails);
};

// Function to remove a specific spec file
// Updated removeSpecFile function to handle both individual removal and "clear all"
const removeSpecFile = (indexToRemoveOrAction) => {
  if (indexToRemoveOrAction === 'all') {
    // Clear all files
    setSpecFiles([]);
    setSpecThumbnails([]);
  } else {
    // Remove specific file by index
    const newFiles = specFiles.filter((_, index) => index !== indexToRemoveOrAction);
    const newThumbnails = specThumbnails.filter((_, index) => index !== indexToRemoveOrAction);
    setSpecFiles(newFiles);
    setSpecThumbnails(newThumbnails);
  }
};

// Apply the same pattern to all other drop callbacks...

const onDropSubmittal = async (acceptedFiles) => {
  const file = acceptedFiles[0];
  setSubmittalFile(file);
  const thumbnail = await generateThumbnail(file);
  setSubmittalThumbnail(thumbnail);
};

const onDropMasterSpec = async (acceptedFiles) => {
  const file = acceptedFiles[0];
  setMasterSpecFile(file);
  const thumbnail = await generateThumbnail(file);
  setMasterSpecThumbnail(thumbnail);
};

const onDropTemplate = async (acceptedFiles) => {
  const file = acceptedFiles[0];
  setTemplateFile(file);
  const thumbnail = await generateThumbnail(file);
  setTemplateThumbnail(thumbnail);
};

const onDropUpdateSpec = async (acceptedFiles) => {
  const file = acceptedFiles[0];
  setUpdateSpecFile(file);
  const thumbnail = await generateThumbnail(file);
  setUpdateSpecThumbnail(thumbnail);
};

const onDropRfi = async (acceptedFiles) => {
  const file = acceptedFiles[0];
  setRfiFile(file);
  const thumbnail = await generateThumbnail(file);
  setRfiThumbnail(thumbnail);
};

const onDropProjectDocs = async (acceptedFiles) => {
  const file = acceptedFiles[0];
  setProjectDocsFile(file);
  const thumbnail = await generateThumbnail(file);
  setProjectDocsThumbnail(thumbnail);
};

const onDropTrackChangesMaster = async (acceptedFiles) => {
  const file = acceptedFiles[0];
  setTrackChangesMasterFile(file);
  const thumbnail = await generateThumbnail(file);
  setTrackChangesMasterThumbnail(thumbnail);
};

const onDropTrackChangesCustomer = async (acceptedFiles) => {
  const file = acceptedFiles[0];
  setTrackChangesCustomerFile(file);
  const thumbnail = await generateThumbnail(file);
  setTrackChangesCustomerThumbnail(thumbnail);
};


  // Replace your existing createDropzone function with this:

const createDropzone = (onDrop, isDragActive, setIsDragActive, allowMultiple = false) => ({
  getRootProps: () => ({
    onDragEnter: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(true);
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onDragLeave: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setIsDragActive(false);
      }
    },
onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const droppedFiles = Array.from(e.dataTransfer?.files || []);
      const acceptedFiles = droppedFiles.filter(f => {
      const extension = f.name.toLowerCase().split('.').pop();
      return ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'xlsx', 'xls', 'csv'].includes(extension);
});

      if (acceptedFiles.length > 0) {
        onDrop(acceptedFiles);
      } else if (droppedFiles.length > 0) {
        alert('Please select PDF, DOC, DOCX, JPG, PNG, WEBP, GIF, XLSX, XLS, or CSV files only.');
      }
    },
    onClick: () => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.gif,.xlsx,.xls,.csv";
      input.multiple = allowMultiple; // CRUCIAL: Enable multiple selection
      input.onchange = (e) => {
        const files = Array.from(e.target.files); // Handle multiple files
        if (files.length > 0) onDrop(files);
      };
      input.click();
    },
  }),
  getInputProps: () => ({}),
  isDragActive,
});

// Then update your dropzone creation calls:
const specDropzone = createDropzone(onDropSpec, isSpecDragActive, setIsSpecDragActive, true); // Enable multiple for specs
const submittalDropzone = createDropzone(onDropSubmittal, isSubmittalDragActive, setIsSubmittalDragActive);
const masterSpecDropzone = createDropzone(onDropMasterSpec, isMasterSpecDragActive, setIsMasterSpecDragActive);
const templateDropzone = createDropzone(onDropTemplate, isTemplateDragActive, setIsTemplateDragActive);
const updateSpecDropzone = createDropzone(onDropUpdateSpec, isUpdateSpecDragActive, setIsUpdateSpecDragActive);
const rfiDropzone = createDropzone(onDropRfi, isRfiDragActive, setIsRfiDragActive);
const projectDocsDropzone = createDropzone(onDropProjectDocs, isProjectDocsDragActive, setIsProjectDocsDragActive);
const drawingsDropzone = createDropzone(onDropDrawings, isDrawingDragActive, setIsDrawingDragActive, true);
const trackChangesMasterDropzone = createDropzone(onDropTrackChangesMaster, isTrackChangesMasterDragActive, setIsTrackChangesMasterDragActive);
const trackChangesCustomerDropzone = createDropzone(onDropTrackChangesCustomer, isTrackChangesCustomerDragActive, setIsTrackChangesCustomerDragActive);


  // Progress tracking
useEffect(() => {
  if (!loading && !builderLoading && !updateLoading && !rfiLoading) return;

  const updateProgress = () => {
    const elapsed = Date.now() - (window.loadingStartTime || Date.now());
    
    if (elapsed < 3000) {
      setProgressStage("Uploading documents...");
      setProgressDetails("Sending files to server for analysis");
    } else if (elapsed < 8000) {
      setProgressStage("Analyzing document content...");
      setProgressDetails("AI is reading and understanding your documents");
    } else if (elapsed < 15000) {
      setProgressStage("Running AECWorks AI analysis...");
      setProgressDetails("Processing with advanced AI models");
    } else if (elapsed < 25000) {
      setProgressStage("Generating results...");
      setProgressDetails("Creating detailed reports and documents");
    } else {
      setProgressStage("Finalizing results...");
      setProgressDetails("Almost done - preparing your files");
    }
  };

  updateProgress();
  const interval = setInterval(updateProgress, 500);
  return () => clearInterval(interval);
}, [loading, builderLoading, updateLoading, rfiLoading]);

const [temporaryManufacturers, setTemporaryManufacturers] = useState([]);

  // Handle adding new manufacturer
const handleAddManufacturer = async (manufacturerData) => {
  try {
    // Instead of saving to backend, just add to temporary state
    setTemporaryManufacturers(prev => [...prev, {
      ...manufacturerData,
      id: Date.now(), // Simple ID for temporary use
      temporary: true
    }]);
    
    console.log("Manufacturer added temporarily for this session:", manufacturerData);
    alert(`Successfully added ${manufacturerData.name} for this analysis session!`);
    
  } catch (error) {
    console.error("Error adding manufacturer:", error);
    throw error;
  }
};

  // AW Spec Check handlers
  // Updated submit handler

// === CACHED DOCUMENTS FUNCTIONS ===
const fetchCachedDocuments = async () => {
  if (!user?.email && !user?.sub) return;
  setCachedDocsLoading(true);
  setCachedDocsError(null);
  try {
    const userId = user.email || user.sub || 'anonymous';
    const res = await fetch(`https://apispeccheck.zonetap.com/api/documents/list?userId=${encodeURIComponent(userId)}`);
    if (!res.ok) throw new Error('Failed to fetch cached documents');
    const data = await res.json();
    setCachedDocuments(data.documents || []);
  } catch (err) {
    console.error('Error fetching cached documents:', err);
    setCachedDocsError('Could not load recent documents');
  } finally {
    setCachedDocsLoading(false);
  }
};

const deleteCachedDocument = async (docId) => {
  try {
    const res = await fetch(`https://apispeccheck.zonetap.com/api/documents/${docId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Delete failed');
    setCachedDocuments(prev => prev.filter(d => d._id !== docId));
  } catch (err) {
    console.error('Error deleting cached document:', err);
    alert('Failed to delete document');
  }
};

const loadCachedDocument = async (doc, targetSlot) => {
  try {
    // Fetch the file buffer from the cache API
    const res = await fetch(`https://apispeccheck.zonetap.com/api/documents/${doc._id}/buffer`);
    if (!res.ok) throw new Error('Failed to fetch file');
    const data = await res.json();
    
    // Convert base64 to File object
    const byteString = atob(data.buffer);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: doc.mimeType || 'application/pdf' });
    const file = new File([blob], doc.originalName, { type: doc.mimeType || 'application/pdf' });
    
    // Route file to appropriate slot based on targetSlot
    switch (targetSlot) {
      case 'spec':
        await onDropSpec([file]);
        break;
      case 'submittal':
        await onDropSubmittal([file]);
        break;
      case 'masterSpec':
        await onDropMasterSpec([file]);
        break;
      case 'template':
        await onDropTemplate([file]);
        break;
      case 'updateSpec':
        await onDropUpdateSpec([file]);
        break;
      case 'rfi':
        await onDropRfi([file]);
        break;
      case 'projectDocs':
        await onDropProjectDocs([file]);
        break;
      case 'drawing':
        await onDropDrawings([file]);
        break;
      case 'trackMaster':
        await onDropTrackChangesMaster([file]);
        break;
      case 'trackCustomer':
        await onDropTrackChangesCustomer([file]);
        break;
      case 'newDrawing':
        setDrawingCompareNewFile(file);
        break;
      case 'baselineDrawing':
        setDrawingCompareBaselineFile(file);
        break;
      case 'designDrawing':
        setDesignVsShopDesignFile(file);
        break;
      case 'shopDrawingForReview':
        setDesignVsShopShopFile(file);
        break;
      default:
        break;
    }
  } catch (err) {
    console.error('Error loading cached document:', err);
    alert('Failed to load document from cache');
  }
};

const fetchVaultDrawings = async (currentUser) => {
  const u = currentUser || user;
  if (!u?.email) return;
  try {
    const res = await fetch(`https://apispeccheck.zonetap.com/api/drawing-compare/vault/list?userId=${encodeURIComponent(u.email)}`);
    const data = await res.json();
    setVaultDrawings(data.drawings || []);
  } catch (err) {
    console.error('Failed to fetch vault drawings:', err);
  }
};

// Load cached docs when panel is opened
useEffect(() => {
  if (showRecentDocs && cachedDocuments.length === 0 && !cachedDocsLoading) {
    fetchCachedDocuments();
  }
}, [showRecentDocs]);
// === END CACHED DOCUMENTS FUNCTIONS ===
const handleSpecCheckSubmit = async () => {
  if (!specFiles || specFiles.length === 0 || !submittalFile) {
    alert("Please select at least one specification file and one submittal file");
    return;
  }

  const formData = new FormData();
  
  // Append all spec files
  specFiles.forEach((file) => {
    formData.append("specs", file);
  });
  
  // Append submittal file
  formData.append("submittal", submittalFile);

  // Include userId for document caching
  const userEmail = user?.email || user?.sub || 'anonymous';
  formData.append("userId", userEmail);
  formData.append("mode", specCheckMode);

  // ========== ADD DRAWING FILES ==========
  // Append drawing files if enabled and available
  if (enableDrawingAnalysis && drawingFiles.length > 0) {
    drawingFiles.forEach((file) => {
      formData.append("drawings", file);
    });
    console.log(`Adding ${drawingFiles.length} drawing files to spec check submission`);
  }
  // ========================================

  try {
    setLoading(true);
    setProcessingError(null);
    setReport(null);
    setPdfBase64(null);
    setFilename(null);
    setProgressStage("Submitting to queue...");
    
    // ========== UPDATE PROGRESS MESSAGE ==========
    const drawingText = enableDrawingAnalysis && drawingFiles.length > 0 
      ? ` with ${drawingFiles.length} drawing file(s)` 
      : '';
    setProgressDetails(`Uploading ${specFiles.length} specification file(s) and 1 submittal file${drawingText}`);
    // =============================================

    // Choose endpoint based on useQueue setting
    const endpoint = useQueue 
      ? "https://apispeccheck.zonetap.com/api/spec-check-queued"
      : "https://apispeccheck.zonetap.com/api/upload";
    
    const res = await fetch(endpoint, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Upload failed");

    const data = await res.json();
    
    if (useQueue) {
      // Handle queue response
      setQueuePosition(data.position);
      setEstimatedTime(data.estimatedTime);
      setProgressStage("Queued for processing");
      setProgressDetails(`Position: ${data.position}, Estimated time: ${data.estimatedTime}`);
      
      // Start polling for job status
      startJobPolling(data.jobId, data.queueName);
    } else {
      // Handle direct processing response (original logic)
      setProgressStage("Analysis complete!");
      
      // ========== UPDATE SUCCESS MESSAGE ==========
      const specsText = data.processing_info?.spec_files_processed || specFiles.length;
      const drawingsText = enableDrawingAnalysis && drawingFiles.length > 0 
        ? ` and ${data.processing_info?.drawings_processed || drawingFiles.length} drawings` 
        : '';
      setProgressDetails(`Successfully analyzed ${specsText} specification file(s)${drawingsText}`);
      // ============================================
      
      setReport(data.report);
      setPdfBase64(data.pdfBase64 || null);
      setFilename(data.filename || "Multi_Spec_Review_Report.pdf");
      setShowClearButton(true);
      setLoading(false);
      if (user) fetchFeatureFlags(user);
    }
    
  } catch (err) {
    setProcessingError("Upload or processing failed. Please try again.");
    setProgressStage("Processing failed");
    setProgressDetails("");
    setShowClearButton(true);
    setLoading(false);
  }
};


  // AW Spec Builder handlers
const handleSpecBuilderSubmit = () => {
  if (!masterSpecFile || !templateFile) {
    alert("Please select both Master Spec Sheet and Outline Template files");
    return;
  }
  setShowSectionsDialog(true);
};

const handleBuilderProcess = async () => {
  if (!requiredSections.trim()) {
    alert("Please specify the sections needed in your spec outline");
    return;
  }

  const formData = new FormData();
  formData.append("masterSpec", masterSpecFile);
  formData.append("template", templateFile);
  formData.append("sections", requiredSections);
  formData.append("requirements", requiredSections);
  formData.append("userId", user?.email || user?.sub || 'anonymous');

  try {
    setBuilderLoading(true);
    setBuilderError(null);
    setBuilderReport(null);
    setBuilderPdfBase64(null);
    setBuilderFilename(null);
    setShowSectionsDialog(false);
    setProgressStage("Submitting to queue...");

    // Choose endpoint based on useQueue setting
    const endpoint = useQueue
      ? "https://apispeccheck.zonetap.com/api/spec-builder-queued"
      : "https://apispeccheck.zonetap.com/api/upload/spec-builder";

    const res = await fetch(endpoint, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Spec building failed");

    const data = await res.json();
    
    if (useQueue) {
      // Handle queue response
      setQueuePosition(data.position);
      setEstimatedTime(data.estimatedTime);
      setProgressStage("Queued for processing");
      setProgressDetails(`Position: ${data.position}, Estimated time: ${data.estimatedTime}`);
      
      // Start polling for job status
      startJobPolling(data.jobId, data.queueName);
    } else {
      // Handle direct processing response (original logic)
      setBuilderReport(data.report);
      setBuilderPdfBase64(data.pdfBase64 || null);
      setBuilderFilename(data.filename || "Generated_Spec_Outline.pdf");
      setShowBuilderClearButton(true);
      setBuilderLoading(false);
      if (user) fetchFeatureFlags(user);
    }
    
  } catch (err) {
    setBuilderError("Spec building failed. Please try again.");
    setShowBuilderClearButton(true);
    setBuilderLoading(false);
  }
};
  // AW Spec Update handlers
  const handleSpecUpdateSubmit = async () => {
  if (!updateSpecFile) {
    alert("Please select a spec sheet file");
    return;
  }

  const formData = new FormData();
  formData.append("specSheet", updateSpecFile);
  formData.append("userId", user?.email || user?.sub || 'anonymous');
  
  if (enableWebCheck) {
    formData.append("liveCheck", "true");
  }
  
  if (temporaryManufacturers.length > 0) {
    formData.append("temporaryManufacturers", JSON.stringify(temporaryManufacturers));
  }

  try {
    setUpdateLoading(true);
    setUpdateError(null);
    setUpdateReport(null);
    setUpdatePdfBase64(null);
    setUpdateFilename(null);
    setProgressStage("Submitting to queue...");

    // Choose endpoint based on useQueue setting
    const endpoint = useQueue
      ? "https://apispeccheck.zonetap.com/api/spec-update-queued"
      : `https://apispeccheck.zonetap.com/api/upload/spec-update${enableWebCheck ? "?liveCheck=true" : ""}`;

    const res = await fetch(endpoint, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Spec update failed");

    const data = await res.json();
    
    if (useQueue) {
      // Handle queue response
      setQueuePosition(data.position);
      setEstimatedTime(data.estimatedTime);
      setProgressStage("Queued for processing");
      setProgressDetails(`Position: ${data.position}, Estimated time: ${data.estimatedTime}${enableWebCheck ? ' (with web verification)' : ''}`);
      
      // Start polling for job status
      startJobPolling(data.jobId, data.queueName);
    } else {
      // Handle direct processing response (original logic)
      setUpdateReport(data.report);
      setUpdatePdfBase64(data.pdfBase64 || null);
      setUpdateFilename(data.filename || "Updated_Spec_Sheet.pdf");
      setShowUpdateClearButton(true);
      setUpdateLoading(false);
      if (user) fetchFeatureFlags(user);
    }
    
  } catch (err) {
    setUpdateError("Spec update failed. Please try again.");
    setShowUpdateClearButton(true);
    setUpdateLoading(false);
  }
};

const handleTrackChangesSubmit = async () => {
  if (!trackChangesMasterFile || !trackChangesCustomerFile) {
    alert("Please select both Master Spec (with track changes) and Customer Spec files");
    return;
  }

  const formData = new FormData();
  formData.append("masterSpec", trackChangesMasterFile);
  formData.append("customerSpec", trackChangesCustomerFile);
  formData.append("userId", user?.email || user?.sub || 'anonymous');

  try {
    setTrackChangesLoading(true);
    setTrackChangesError(null);
    setTrackChangesReport(null);
    setTrackChangesPdfBase64(null);
    setTrackChangesFilename(null);
    setProgressStage("Submitting to queue...");
    setProgressDetails("Uploading master specification and customer specification files");

    // Choose endpoint based on useQueue setting
    const endpoint = useQueue 
      ? "https://apispeccheck.zonetap.com/api/track-changes-queued"
      : "https://apispeccheck.zonetap.com/api/upload/track-changes-compare";
    
    const res = await fetch(endpoint, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Track changes comparison failed");

    const data = await res.json();
    
    if (useQueue) {
      // Handle queue response
      setQueuePosition(data.position);
      setEstimatedTime(data.estimatedTime);
      setProgressStage("Queued for processing");
      setProgressDetails(`Position: ${data.position}, Estimated time: ${data.estimatedTime}`);
      
      // Start polling for job status
      startJobPolling(data.jobId, data.queueName);
    } else {
      // Handle direct processing response
      setProgressStage("Analysis complete!");
      setProgressDetails("Successfully analyzed specification differences and gaps");
      
      setTrackChangesReport(data.report);
      setTrackChangesPdfBase64(data.pdfBase64 || null);
      setTrackChangesFilename(data.filename || "Track_Changes_Gap_Analysis.pdf");
      setShowTrackChangesClearButton(true);
      setTrackChangesLoading(false);
      if (user) fetchFeatureFlags(user);
    }
    
  } catch (err) {
    setTrackChangesError("Track changes analysis failed. Please try again.");
    setProgressStage("Processing failed");
    setProgressDetails("");
    setShowTrackChangesClearButton(true);
    setTrackChangesLoading(false);
  }
};

  // AW RFI Check handlers
const handleRfiCheckSubmit = async () => {
  if (!rfiFile) {
    alert("Please select an RFI document");
    return;
  }

  if (!projectDocsFile && drawingFiles.length === 0) {
    alert("Please select either project documentation or drawing files (or both)");
    return;
  }

  const formData = new FormData();
  formData.append("rfi", rfiFile);
  formData.append("userId", user?.email || user?.sub || 'anonymous');
  
  if (projectDocsFile) {
    formData.append("projectDocs", projectDocsFile);
  }
  
  // Append drawing files if any
  drawingFiles.forEach((file) => {
    formData.append("drawings", file);
  });
  
  formData.append("includeCostAnalysis", includeCostAnalysis.toString());
  formData.append("mode", rfiCheckMode);

  try {
    setRfiLoading(true);
    setRfiError(null);
    setRfiReport(null);
    setRfiPdfBase64(null);
    setRfiFilename(null);
    setProgressStage("Submitting to queue...");

    const endpoint = useQueue
      ? "https://apispeccheck.zonetap.com/api/rfi-check-queued"
      : "https://apispeccheck.zonetap.com/api/upload/rfi-check";

    const res = await fetch(endpoint, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("RFI analysis failed");

    const data = await res.json();
    
    if (useQueue) {
      setQueuePosition(data.position);
      setEstimatedTime(data.estimatedTime);
      setProgressStage("Queued for processing");
      setProgressDetails(`Position: ${data.position}, Estimated time: ${data.estimatedTime}${includeCostAnalysis ? ' (with cost analysis)' : ''}${enableDrawingAnalysis ? ' (with drawing analysis)' : ''}`);
      
      startJobPolling(data.jobId, data.queueName);
    } else {
      setRfiReport(data.report);
      setRfiPdfBase64(data.pdfBase64 || null);
      setRfiFilename(data.filename || "RFI_Analysis_Report.pdf");
      setShowRfiClearButton(true);
      setRfiLoading(false);
      if (user) fetchFeatureFlags(user);
    }
    
  } catch (err) {
    setRfiError("RFI analysis failed. Please try again.");
    setShowRfiClearButton(true);
    setRfiLoading(false);
  }
};

// Add this new render function for Track Changes:
const renderTrackChanges = () => (
  <>
    {false && <QueueSettings />}
    
    <RecentDocumentsPanel activeTab={activeTab} getFlag={getFlag} />
    <PlatformFilesPanel activeTab={activeTab} user={user} integrationStatus={integrationStatus} onRefreshStatus={() => fetchIntegrationStatus(user)} />
    
    <div style={{
      marginBottom: '24px',
      padding: '20px',
      backgroundColor: '#f0f9ff',
      border: '2px solid #0ea5e9',
      borderRadius: '12px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '12px'
      }}>
        <span style={{ fontSize: '24px' }}>📝</span>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#0c4a6e',
          margin: 0
        }}>
          Track Changes Specification Gap Analysis
        </h3>
      </div>
      <p style={{
        color: '#0369a1',
        fontSize: '14px',
        lineHeight: '1.5',
        margin: 0
      }}>
        Upload your <strong>Master Specification</strong> (with track changes visible - enable "Show All Markup" in Word before uploading) and your <strong>Customer Specification</strong> (outbound version).
AECWorks AI will analyze what requirements, sections, and details are missing from the customer spec compared to the master spec.
      </p>
    </div>

    <div style={styles.grid}>
      <DropBoxComponent
        label="Master Spec (with Track Changes)"
        file={trackChangesMasterFile}
        dropzone={trackChangesMasterDropzone}
        styles={styles}
        thumbnail={trackChangesMasterThumbnail}
      />
      <DropBoxComponent
        label="Customer Spec (Outbound Version)"
        file={trackChangesCustomerFile}
        dropzone={trackChangesCustomerDropzone}
        styles={styles}
        thumbnail={trackChangesCustomerThumbnail}
      />
    </div>

    <div style={{
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: '#fffbeb',
      border: '1px solid #D4AF37',
      borderRadius: '8px'
    }}>
      <h4 style={{
        fontSize: '14px',
        fontWeight: '600',
        color: '#92400e',
        margin: '0 0 8px 0'
      }}>
        What This Analysis Provides:
      </h4>
      <ul style={{
        color: '#92400e',
        fontSize: '13px',
        lineHeight: '1.5',
        margin: 0,
        paddingLeft: '20px'
      }}>
        <li><strong>Gap Analysis Matrix:</strong> Detailed comparison showing what's missing from customer spec</li>
        <li><strong>Risk Assessment:</strong> High/Medium/Low priority gaps with impact analysis</li>
        <li><strong>Track Changes Analysis:</strong> Recent updates in master spec not reflected in customer version</li>
        <li><strong>Cost & Schedule Impact:</strong> Potential financial and timeline implications</li>
        <li><strong>Actionable Recommendations:</strong> Specific steps to address each gap with timelines</li>
      </ul>
    </div>

    <div style={styles.buttonContainer}>
    <UsageBar featureKey="trackChanges" getFlag={getFlag} label="Track Changes" />
      <button
        onClick={handleTrackChangesSubmit}
        disabled={trackChangesLoading}
        style={{
          ...styles.button,
          ...(trackChangesLoading ? styles.buttonDisabled : {})
        }}
      >
        {trackChangesLoading ? "Analyzing Specifications..." : "Analyze Track Changes & Gaps"}
      </button>
      
      {showTrackChangesClearButton && (
        <button
          onClick={handleClearAll}
          style={styles.clearButton}
        >
          🗑️ Clear All
        </button>
      )}
    </div>

    <QueueProgressTracker 
      loading={false}
      builderLoading={false}
      updateLoading={false}
      rfiLoading={false}
      trackChangesLoading={trackChangesLoading}
    />

    {!useQueue && trackChangesLoading && (
      <div style={styles.progressContainer}>
        <div style={styles.progressHeader}>
          <h3 style={styles.progressTitle}>Analyzing Track Changes & Specification Gaps</h3>
          <span style={styles.progressPercentage}>{Math.round(getProgressPercentage())}%</span>
        </div>
        
        <div style={styles.progressBar}>
          <div 
            style={{
              ...styles.progressFill,
              width: `${getProgressPercentage()}%`
            }}
          ></div>
        </div>
        
        <div>
          <p style={styles.progressStage}>
            {progressStage || "Comparing master and customer specifications..."}
          </p>
          {progressDetails && (
            <p style={styles.progressDetails}>
              {progressDetails}
            </p>
          )}
        </div>
      </div>
    )}

    {trackChangesError && <p style={styles.errorText}>{trackChangesError}</p>}

    {trackChangesReport && !trackChangesLoading && (
      <div style={styles.resultContainer}>
        <h3 style={styles.resultTitle}>
          Track Changes Gap Analysis Complete!
        </h3>
        <pre style={styles.reportBox}>{trackChangesReport}</pre>

        <div style={styles.downloadContainer}>
          {trackChangesPdfBase64 && (
            <button
              onClick={() => handleDownloadPDF(trackChangesPdfBase64, trackChangesFilename)}
              style={{...styles.downloadButton, ...styles.pdfButton}}
            >
              Download PDF Report
            </button>
          )}
          <button
            onClick={() => handleDownloadWord(trackChangesReport, trackChangesFilename)}
            style={{...styles.downloadButton, ...styles.wordButton}}
          >
            Download Word (.doc)
          </button>
        </div>
      </div>
    )}
  </>
);

// Updated renderRfiCheck function:
const renderRfiCheck = () => (
  <>
    {false && <QueueSettings />}
    
    <RecentDocumentsPanel activeTab={activeTab} />
    <PlatformFilesPanel activeTab={activeTab} user={user} integrationStatus={integrationStatus} onRefreshStatus={() => fetchIntegrationStatus(user)} />
    
    <div style={styles.grid}>
      <DropBoxComponent
        label="RFI Document"
        file={rfiFile}
        dropzone={rfiDropzone}
        styles={styles}
        thumbnail={rfiThumbnail}
      />
      <DropBoxComponent
        label="Project Documentation"
        file={projectDocsFile}
        dropzone={projectDocsDropzone}
        styles={styles}
        thumbnail={projectDocsThumbnail}
      />
    </div>

    {/* Drawing Analysis Section */}
    <div style={{
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: '#f0f9ff',
      border: '1px solid #0ea5e9',
      borderRadius: '8px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          <input
            type="checkbox"
            checked={enableDrawingAnalysis}
            onChange={(e) => {
              setEnableDrawingAnalysis(e.target.checked);
              if (!e.target.checked) {
                setDrawingFiles([]);
                setDrawingThumbnails([]);
              }
            }}
            style={{
              width: '16px',
              height: '16px',
              cursor: 'pointer'
            }}
          />
          <div style={{ color: '#0c4a6e' }}>
            Enable Drawing Reference Analysis
          </div>
        </label>
        
        {drawingFiles.length > 0 && (
          <span style={{
            fontSize: '12px',
            color: '#0369a1',
            fontWeight: '500'
          }}>
            {drawingFiles.length}/30 drawings uploaded
          </span>
        )}
      </div>
      
      <div style={{ 
        color: '#0369a1', 
        fontSize: '12px',
        lineHeight: '1.4',
        marginBottom: enableDrawingAnalysis ? '16px' : '0'
      }}>
        Upload architectural drawings, plans, or diagrams that relate to the RFI questions. 
        The AECWorks AI will analyze visual elements and reference specific drawings in responses.
        <br />
        <strong>Supported formats:</strong> JPEG, PNG, WEBP, GIF, PDF (up to 10 files)
      </div>

      {enableDrawingAnalysis && (
        <div style={{ marginTop: '12px' }}>
          {drawingFiles.length === 0 ? (
            <div
              {...drawingsDropzone.getRootProps()}
              style={{
                ...styles.dropBox,
                ...(isDrawingDragActive ? styles.dropBoxActive : {}),
                backgroundColor: '#f8fafc',
                border: '2px dashed #0ea5e9',
                minHeight: '100px'
              }}
            >
              <input {...drawingsDropzone.getInputProps()} />
              <p style={{
                ...styles.dropBoxLabel,
                color: '#0c4a6e',
                fontSize: '14px'
              }}>
                Upload Drawing Files
              </p>
              <p style={{
                ...styles.dropBoxText,
                color: '#0369a1'
              }}>
                {isDrawingDragActive 
                  ? "Drop drawing files here..." 
                  : "Drag & drop drawing files here or click to select (up to 10 files)"
                }
              </p>
            </div>
          ) : (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0c4a6e',
                  margin: 0
                }}>
                  Drawing Files ({drawingFiles.length})
                </h4>
                <button
                  onClick={() => removeDrawingFile('all')}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '8px',
                marginBottom: '12px'
              }}>
                {drawingFiles.map((file, index) => (
                  <div key={`${file.name}-${index}`} style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '6px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #bae6fd'
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeDrawingFile(index);
                      }}
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                      }}
                    >
                      ×
                    </button>
                    
{drawingThumbnails[index] && (
  <div
    style={{
      position: 'relative',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      setDrawingMousePosition({ x: e.clientX, y: e.clientY });
      setPreviewDrawingFile(file);
      setPreviewDrawingThumbnail(drawingThumbnails[index]);
      setShowDrawingPreview(true);
    }}
    onMouseLeave={() => {
      setShowDrawingPreview(false);
      setPreviewDrawingFile(null);
      setPreviewDrawingThumbnail(null);
    }}
    title={`Click to preview ${file.name}`}
  >
    <img
      src={drawingThumbnails[index]}
      alt={`${file.name} thumbnail`}
      style={{
        width: '50px',
        height: '60px',
        objectFit: 'cover',
        borderRadius: '4px',
        border: '1px solid #bae6fd',
        marginBottom: '4px',
        transition: 'all 0.2s ease',
        boxShadow: showDrawingPreview && previewDrawingFile === file ? '0 8px 16px rgba(14, 165, 233, 0.3)' : 'none',
        transform: showDrawingPreview && previewDrawingFile === file ? 'scale(1.05)' : 'scale(1)'
      }}
    />
  </div>
)}
                    
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ 
                        fontSize: '10px', 
                        fontWeight: '600', 
                        color: '#0c4a6e', 
                        margin: '0 0 2px 0',
                        wordBreak: 'break-word',
                        lineHeight: '1.2'
                      }}>
                        {file.name.length > 12 ? file.name.substring(0, 9) + '...' : file.name}
                      </p>
                      <p style={{ 
                        fontSize: '9px', 
                        color: '#0369a1', 
                        margin: 0 
                      }}>
                        {(file.size / 1024 / 1024).toFixed(1)}MB
                      </p>
                    </div>
                  </div>
                ))}
                
                {drawingFiles.length < 10 && (
<div 
  {...drawingsDropzone.getRootProps()}
  style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px',
    backgroundColor: '#f0f9ff',
    border: '2px dashed #0ea5e9',
    borderRadius: '6px',
    cursor: 'pointer',
    minHeight: '80px',
    position: 'relative'
  }}
>
  <input 
    {...drawingsDropzone.getInputProps()} 
    style={{
      position: 'absolute',
      opacity: 0,
      width: '100%',
      height: '100%',
      cursor: 'pointer'
    }}
  />
  <p style={{
    color: '#0ea5e9',
    fontSize: '18px',
    margin: '0 0 2px 0'
  }}>+</p>
  <p style={{
    color: '#0ea5e9',
    fontSize: '9px',
    textAlign: 'center',
    margin: 0,
    lineHeight: '1.2'
  }}>
    Add More
  </p>
</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>

           {/* Drawing Preview Modal */}
        <FilePreviewModal
          file={previewDrawingFile}
          thumbnail={previewDrawingThumbnail}
          isVisible={showDrawingPreview}
          position={drawingMousePosition}
          onClose={() => setShowDrawingPreview(false)}
        />
  

    {/* Cost Analysis Option */}
    <div style={{
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '8px'
    }}>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
      }}>
        <input
          type="checkbox"
          checked={includeCostAnalysis}
          onChange={(e) => setIncludeCostAnalysis(e.target.checked)}
          style={{
            width: '16px',
            height: '16px',
            cursor: 'pointer'
          }}
        />
        <div>
          <div style={{ color: '#000000', marginBottom: '4px' }}>
            Include Cost & Schedule Impact Analysis
          </div>
          <div style={{ 
            color: '#6b7280', 
            fontSize: '12px',
            lineHeight: '1.4'
          }}>
            Analyze potential cost and schedule impacts of RFI questions. This provides additional insight 
            for project management but may increase processing time.
          </div>
        </div>
      </label>
    </div>

    <UsageBar featureKey="rfiCheck" getFlag={getFlag} label="RFI Check" />
    <div style={{
  marginBottom: '16px',
  padding: '12px 16px',
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}}>
  <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Report Type:</span>
  <div style={{ display: 'flex', gap: '4px', padding: '3px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px' }}>
    <button
      type="button"
      onClick={() => setRfiCheckMode('standard')}
      style={{
        padding: '5px 12px', fontSize: '12px', fontWeight: '600', border: 'none', borderRadius: '4px', cursor: 'pointer',
        backgroundColor: rfiCheckMode === 'standard' ? '#D4AF37' : 'transparent',
        color: rfiCheckMode === 'standard' ? '#000000' : '#6b7280',
      }}
    >
      Standard
    </button>
    <button
      type="button"
      onClick={() => setRfiCheckMode('advanced')}
      style={{
        padding: '5px 12px', fontSize: '12px', fontWeight: '600', border: 'none', borderRadius: '4px', cursor: 'pointer',
        backgroundColor: rfiCheckMode === 'advanced' ? '#D4AF37' : 'transparent',
        color: rfiCheckMode === 'advanced' ? '#000000' : '#6b7280',
      }}
    >
      Advanced
    </button>
  </div>
  <span style={{ fontSize: '11px', color: '#9ca3af' }}>
    {rfiCheckMode === 'standard'
      ? 'Direct answers only — no design recommendations or cost analysis'
      : 'Full response analysis with cost/schedule impact and coordination review'}
  </span>
</div>
    <div style={styles.buttonContainer}>
      <button
        onClick={handleRfiCheckSubmit}
        disabled={rfiLoading}
        style={{
          ...styles.button,
          ...(rfiLoading ? styles.buttonDisabled : {})
        }}
      >
        {rfiLoading ? "Analyzing RFI..." : 
         `Analyze RFI Response${enableDrawingAnalysis ? ' (with Drawings)' : ''}`}
      </button>
      
      {showRfiClearButton && (
        <button
          onClick={handleClearAll}
          style={styles.clearButton}
        >
          🗑️ Clear All
        </button>
      )}
    </div>

    <QueueProgressTracker 
      loading={false}
      builderLoading={false}
      updateLoading={false}
      rfiLoading={rfiLoading}
    />

    {!useQueue && rfiLoading && (
      <div style={styles.progressContainer}>
        <div style={styles.progressHeader}>
          <h3 style={styles.progressTitle}>Analyzing RFI</h3>
          <span style={styles.progressPercentage}>{Math.round(getProgressPercentage())}%</span>
        </div>
        
        <div style={styles.progressBar}>
          <div 
            style={{
              ...styles.progressFill,
              width: `${getProgressPercentage()}%`
            }}
          ></div>
        </div>
        
        <div>
          <p style={styles.progressStage}>
            {progressStage || "Processing RFI and project documentation..."}
          </p>
          {progressDetails && (
            <p style={styles.progressDetails}>
              {progressDetails}
              {includeCostAnalysis && " (including cost/schedule analysis)"}
              {enableDrawingAnalysis && ` (analyzing ${drawingFiles.length} drawing files)`}
            </p>
          )}
        </div>
      </div>
    )}

    {rfiError && <p style={styles.errorText}>{rfiError}</p>}

    {rfiReport && !rfiLoading && (
      <div style={styles.resultContainer}>
        <h3 style={styles.resultTitle}>
          RFI Analysis Complete! 
          {includeCostAnalysis && "(with Cost/Schedule Analysis)"}
          {enableDrawingAnalysis && ` (${drawingFiles.length} drawings analyzed)`}
        </h3>
        <pre style={styles.reportBox}>{rfiReport}</pre>

        <div style={styles.downloadContainer}>
          {rfiPdfBase64 && (
            <button
              onClick={() => handleDownloadPDF(rfiPdfBase64, rfiFilename)}
              style={{...styles.downloadButton, ...styles.pdfButton}}
            >
              Download PDF
            </button>
          )}
          <button
            onClick={() => handleDownloadWord(rfiReport, rfiFilename)}
            style={{...styles.downloadButton, ...styles.wordButton}}
          >
            Download Word (.doc)
          </button>
        </div>
      </div>
    )}
  </>
);


// ============================================================
// AW Package Check Tab — paste into App.js
// Add near the other tab components (e.g. after RFICheckTab ~L4110)
// ============================================================

const [shopDrawingIsDragActive, setShopDrawingIsDragActive] = useState(false);
const [shopDrawingLoading, setShopDrawingLoading]   = useState(false);
const [shopDrawingError, setShopDrawingError]       = useState(null);
const [shopDrawingReview, setShopDrawingReview]     = useState(null);
const [shopDrawingPdfB64, setShopDrawingPdfB64]     = useState(null);
const [shopDrawingShowClear, setShopDrawingShowClear] = useState(false);

const renderShopDrawingCheck = () => {
  const apiBase = "https://apispeccheck.zonetap.com";
  const authHeaders = { Authorization: `Bearer ${localStorage.getItem('auth0_token')}` };

  // Aliases to lifted Dashboard state
  const isDragActive    = shopDrawingIsDragActive;
  const setIsDragActive = setShopDrawingIsDragActive;
  const loading         = shopDrawingLoading;
  const setLoading      = setShopDrawingLoading;
  const error           = shopDrawingError;
  const setError        = setShopDrawingError;
  const review          = shopDrawingReview;
  const setReview       = setShopDrawingReview;
  const pdfB64          = shopDrawingPdfB64;
  const setPdfB64       = setShopDrawingPdfB64;
  const showClear       = shopDrawingShowClear;
  const setShowClear    = setShopDrawingShowClear;

  const files          = shopDrawingFiles;
  const setFiles       = setShopDrawingFiles;
  const thumbnails     = shopDrawingThumbnails;
  const setThumbnails  = setShopDrawingThumbnails;
  const projectName    = shopDrawingProjectName;
  const setProjectName = setShopDrawingProjectName;
  const trade          = shopDrawingTrade;
  const setTrade       = setShopDrawingTrade;
  const notes          = shopDrawingNotes;
  const setNotes       = setShopDrawingNotes;

const TRADES = [
    { value: 'mechanical',       label: 'Mechanical (HVAC, piping)' },
    { value: 'electrical',       label: 'Electrical' },
    { value: 'plumbing',         label: 'Plumbing' },
    { value: 'fire-protection',  label: 'Fire Protection' },
    { value: 'structural',       label: 'Structural' },
    { value: 'architectural',    label: 'Architectural' },
    { value: 'interior design',  label: 'Interior Design (Finishes, FF&E)' },
    { value: 'civil',            label: 'Civil' },
    { value: 'telecommunications', label: 'Telecommunications / Low Voltage' },
    { value: 'landscape',        label: 'Landscape' },
    { value: 'other',            label: 'Other' },
  ];

  const FOCUS_AREAS = [
  { id: 'dimensions',   label: 'Dimensions & Measurements',   desc: 'Check all dimensions, clearances, and tolerances' },
  { id: 'materials',    label: 'Materials & Finishes',         desc: 'Verify material specs, finish codes, and substitutions' },
  { id: 'annotations',  label: 'Annotations & Notes',          desc: 'Review callouts, keynotes, and general notes' },
  { id: 'compliance',   label: 'Code & Standards Compliance',  desc: 'UL listings, NFPA, NEC, ASTM references' },
  { id: 'coordination', label: 'Trade Coordination',           desc: 'Conflicts with other trades, missing stamps' },
  { id: 'products',     label: 'Product & Equipment Schedule', desc: 'Model numbers, substitutions, cut sheet alignment' },
  { id: 'mounting',     label: 'Mounting & Support Details',   desc: 'Seismic bracing, hangers, anchor types' },
  { id: 'connections',  label: 'Connection Details',           desc: 'Joint types, transitions, fitting methods' },
];

  const SEVERITY_STYLE = {
    red:    { bg: '#fee2e2', fg: '#991b1b', label: '🔴 Red Flag' },
    yellow: { bg: '#fef3c7', fg: '#92400e', label: '🟡 Review'   },
    info:   { bg: '#e0f2fe', fg: '#075985', label: '🔵 Info'     },
  };

  const CATEGORY_LABELS = {
    routing:      'Routing & Layout',
    connections:  'Connection Details',
    cutSheets:    'Product Cut Sheets',
    ulCodes:      'UL Listings & Code Ratings',
    dimensions:   'Dimensions & Clearances',
    mounting:     'Mounting & Support',
    coordination: 'Trade Coordination',
  };

  const onDropFiles = async (acceptedFiles) => {
    const newThumbs = await Promise.all(acceptedFiles.map(f => generateThumbnail(f)));
    setFiles(prev => [...prev, ...acceptedFiles].slice(0, 20));
    setThumbnails(prev => [...prev, ...newThumbs].slice(0, 20));
  };

  const removeFile = (idx) => {
    if (idx === 'all') {
      setFiles([]);
      setThumbnails([]);
      return;
    }
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setThumbnails(prev => prev.filter((_, i) => i !== idx));
  };

  const dropzone = createDropzone(onDropFiles, isDragActive, setIsDragActive, true);

  const handleClearAllShop = () => {
    setFiles([]);
    setThumbnails([]);
    setProjectName('');
    setTrade('mechanical');
    setNotes('');
    setShopDrawingFocusAreas([]);
    setReview(null);
    setPdfB64(null);
    setError(null);
    setShowClear(false);
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      alert('Please upload at least one shop drawing file');
      return;
    }
    setLoading(true);
    setError(null);
    setReview(null);
    setPdfB64(null);

    try {
      const fd = new FormData();
      files.forEach(f => fd.append('drawings', f));
      fd.append('projectName', projectName);
      fd.append('trade', trade);
      fd.append('notes', notes);
      fd.append('userId', user?.email || user?.sub || 'anonymous');
      if (shopDrawingFocusAreas.length > 0) {
      fd.append('focusAreas', shopDrawingFocusAreas.join(','));
    }

      const res = await fetch(`${apiBase}/api/shop-drawing-check`, {
        method: 'POST',
        headers: authHeaders,
        body: fd,
      });

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        const hint = res.status === 413 ? 'File too large — exceeds upload size limit on server.'
                   : res.status === 504 ? 'Server timeout — analysis took too long.'
                   : res.status === 502 ? 'Backend unavailable (502 Bad Gateway).'
                   : `Server returned ${res.status} ${res.statusText} (non-JSON response).`;
        throw new Error(hint);
      }

      if (!res.ok) throw new Error(data.error || `Request failed (HTTP ${res.status})`);
      setReview(data.review);
      setPdfB64(data.pdf);
      setShowClear(true);
    } catch (e) {
      setError(e.message);
      setShowClear(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!pdfB64) return;
    const link = document.createElement('a');
    link.href = 'data:application/pdf;base64,' + pdfB64;
    link.download = `Shop_Drawing_Review_${Date.now()}.pdf`;
    link.click();
  };

  const grouped = (review?.findings || []).reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  return (
    <>
      <RecentDocumentsPanel activeTab={activeTab} />
      <PlatformFilesPanel activeTab={activeTab} user={user} integrationStatus={integrationStatus} onRefreshStatus={() => fetchIntegrationStatus(user)} />

      <div style={{
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: '#fffbeb',
        border: '2px solid #D4AF37',
        borderRadius: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <span style={{ fontSize: '24px' }}>🔧</span>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#000000', margin: 0 }}>
            AW Package Check — A Second Set of Eyes
          </h3>
        </div>
        <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
          Upload any submittal package — shop drawings, FF&E schedules, finish schedules, cut sheets, or Excel specifications. AECWorks AI will cross-reference between files and flag red flags.
        </p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        {files.length === 0 ? (
          <div
            {...dropzone.getRootProps()}
            style={{
              ...styles.dropBox,
              ...(isDragActive ? styles.dropBoxActive : {}),
              minHeight: '140px'
            }}
          >
            <input {...dropzone.getInputProps()} />
            <p style={styles.dropBoxLabel}>Shop Drawing Package</p>
            <p style={styles.dropBoxText}>
              {isDragActive
                ? 'Drop files here...'
                : 'Drag & drop shop drawing files here or click to select (up to 20 files)'}
            </p>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
              Supported: PDF, JPEG, PNG, WEBP, GIF, XLSX, XLS, CSV
            </p>
          </div>
        ) : (
          <div style={{
            padding: '16px',
            border: '2px dashed #d1d5db',
            borderRadius: '8px',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#000000', margin: 0 }}>
                Shop Drawing Files ({files.length}/20)
              </h4>
              <button
                onClick={() => removeFile('all')}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Clear All
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: '10px'
            }}>
              {files.map((f, index) => (
                <div key={`${f.name}-${index}`} style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '8px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '11px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10
                    }}
                  >
                    ×
                  </button>

                  {thumbnails[index] && (
                    <div
                      style={{ position: 'relative', cursor: 'pointer' }}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        setDrawingMousePosition({ x: e.clientX, y: e.clientY });
                        setPreviewDrawingFile(f);
                        setPreviewDrawingThumbnail(thumbnails[index]);
                        setShowDrawingPreview(true);
                      }}
                      onMouseLeave={(e) => {
                        e.stopPropagation();
                        setShowDrawingPreview(false);
                        setPreviewDrawingFile(null);
                        setPreviewDrawingThumbnail(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      title={`Hover to preview ${f.name}`}
                    >
                      <img
                        src={thumbnails[index]}
                        alt={`${f.name} thumbnail`}
                        style={{
                          width: '60px',
                          height: '75px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                          marginBottom: '6px',
                          transition: 'all 0.2s ease',
                          boxShadow: showDrawingPreview && previewDrawingFile === f
                            ? '0 8px 16px rgba(212, 175, 55, 0.3)'
                            : 'none',
                          transform: showDrawingPreview && previewDrawingFile === f
                            ? 'scale(1.05)'
                            : 'scale(1)'
                        }}
                      />
                    </div>
                  )}

                  <div style={{ textAlign: 'center' }}>
                    <p style={{
                      fontSize: '10px',
                      fontWeight: '600',
                      color: '#000000',
                      margin: '0 0 2px 0',
                      wordBreak: 'break-word',
                      lineHeight: '1.2'
                    }}>
                      {f.name.length > 14 ? f.name.substring(0, 11) + '...' : f.name}
                    </p>
                    <p style={{ fontSize: '9px', color: '#6b7280', margin: 0 }}>
                      {(f.size / 1024 / 1024).toFixed(1)} MB
                    </p>
                  </div>
                </div>
              ))}

              {files.length < 20 && (
                <div
                  {...dropzone.getRootProps()}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '6px',
                    backgroundColor: '#fffbeb',
                    border: '2px dashed #D4AF37',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    minHeight: '110px',
                    position: 'relative'
                  }}
                >
                  <input
                    {...dropzone.getInputProps()}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer'
                    }}
                  />
                  <p style={{ color: '#D4AF37', fontSize: '20px', margin: '0 0 4px 0' }}>+</p>
                  <p style={{
                    color: '#D4AF37',
                    fontSize: '10px',
                    textAlign: 'center',
                    margin: 0,
                    fontWeight: '600'
                  }}>
                    Add More
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <FilePreviewModal
        file={previewDrawingFile}
        thumbnail={previewDrawingThumbnail}
        isVisible={showDrawingPreview}
        position={drawingMousePosition}
        onClose={() => setShowDrawingPreview(false)}
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '16px',
        marginBottom: '16px'
      }}>
        <div>
          <label style={{
            display: 'block', fontSize: '13px', fontWeight: '600',
            color: '#374151', marginBottom: '6px'
          }}>
            Project Name
          </label>
          <input
            type="text"
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            placeholder="e.g. 123 Main Street Office"
            style={{
              width: '100%', padding: '12px', border: '2px solid #d1d5db',
              borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <div>
          <label style={{
            display: 'block', fontSize: '13px', fontWeight: '600',
            color: '#374151', marginBottom: '6px'
          }}>
            Trade
          </label>
          <select
            value={trade}
            onChange={e => setTrade(e.target.value)}
            style={{
              width: '100%', padding: '12px', border: '2px solid #d1d5db',
              borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit',
              boxSizing: 'border-box', backgroundColor: 'white'
            }}
          >
            {TRADES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block', fontSize: '13px', fontWeight: '600',
          color: '#374151', marginBottom: '6px'
        }}>
          Reviewer Notes (Optional)
        </label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Anything specific to focus on? (e.g. 'Check seismic bracing callouts', 'Verify UL listings on fire dampers')"
          rows={3}
          style={{
            width: '100%', padding: '12px', border: '2px solid #d1d5db',
            borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit',
            boxSizing: 'border-box', resize: 'vertical'
          }}
        />
      </div>

      <div style={{
  marginBottom: '20px',
  padding: '16px',
  backgroundColor: '#fffbeb',
  border: '1px solid #D4AF37',
  borderRadius: '8px'
}}>
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px'
  }}>
    <div>
      <div style={{ fontSize: '14px', fontWeight: '600', color: '#000000', marginBottom: '2px' }}>
        Priority Focus Areas
      </div>
      <div style={{ fontSize: '12px', color: '#6b7280' }}>
        Check areas to prioritize. Leave all unchecked for a full review.
      </div>
    </div>
    {shopDrawingFocusAreas.length > 0 && (
      <button
        onClick={() => setShopDrawingFocusAreas([])}
        style={{ background: 'none', border: 'none', color: '#D4AF37', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
      >
        Clear All
      </button>
    )}
  </div>

  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
    {FOCUS_AREAS.map(area => {
      const checked = shopDrawingFocusAreas.includes(area.id);
      return (
        <label key={area.id} style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          padding: '10px 12px',
          backgroundColor: checked ? '#fffbeb' : 'white',
          border: `1.5px solid ${checked ? '#D4AF37' : '#e5e7eb'}`,
          borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s'
        }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => {
              if (e.target.checked) {
                setShopDrawingFocusAreas(prev => [...prev, area.id]);
              } else {
                setShopDrawingFocusAreas(prev => prev.filter(a => a !== area.id));
              }
            }}
            style={{ width: '15px', height: '15px', marginTop: '1px', accentColor: '#D4AF37', flexShrink: 0, cursor: 'pointer' }}
          />
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#000000', marginBottom: '2px' }}>
              {area.label}
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.4' }}>
              {area.desc}
            </div>
          </div>
        </label>
      );
    })}
  </div>

  {shopDrawingFocusAreas.length > 0 && (
    <div style={{
      marginTop: '10px', padding: '8px 12px',
      backgroundColor: '#D4AF3715', borderRadius: '4px',
      fontSize: '12px', color: '#92400e', fontWeight: '500'
    }}>
      ★ AI will prioritize: {shopDrawingFocusAreas.map(id =>
        FOCUS_AREAS.find(a => a.id === id)?.label
      ).join(' · ')}
    </div>
  )}
</div>

      <UsageBar featureKey="shopDrawingCheck" getFlag={getFlag} label="Shop Drawing Check" />

      <div style={styles.buttonContainer}>
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
        >
          {loading
        ? 'Reviewing Shop Drawing...'
        : shopDrawingFocusAreas.length > 0
        ? `Review Shop Drawing (${shopDrawingFocusAreas.length} focus area${shopDrawingFocusAreas.length > 1 ? 's' : ''})`
        : 'Review Shop Drawing'
      }
        </button>
        {showClear && (
          <button onClick={handleClearAllShop} style={styles.clearButton}>
            🗑️ Clear All
          </button>
        )}
      </div>

      {loading && (
        <div style={styles.progressContainer}>
          <div style={styles.progressHeader}>
            <h3 style={styles.progressTitle}>
              Analyzing {files.length} Shop Drawing{files.length === 1 ? '' : 's'}
            </h3>
          </div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: '60%' }}></div>
          </div>
          <p style={styles.progressStage}>Reviewing routing, connections, cut sheets, and code ratings...</p>
          <p style={styles.progressDetails}>This usually takes 20-45 seconds depending on drawing complexity.</p>
        </div>
      )}

      {error && <p style={styles.errorText}>{error}</p>}

      {review && !loading && (
        <div style={styles.resultContainer}>
          <h3 style={styles.resultTitle}>
            Shop Drawing Review Complete ({files.length} file{files.length === 1 ? '' : 's'} reviewed)
          </h3>

          <div style={{
            padding: '16px',
            backgroundColor: 'white',
            borderLeft: `4px solid ${
              review.overallRisk === 'high' ? '#dc2626' :
              review.overallRisk === 'medium' ? '#d97706' : '#16a34a'
            }`,
            borderRadius: '6px',
            marginBottom: '16px'
          }}>
            <div style={{
              fontSize: '11px', textTransform: 'uppercase', fontWeight: '700',
              color: '#6b7280', marginBottom: '6px'
            }}>
              Overall Risk: <span style={{
                color: review.overallRisk === 'high' ? '#dc2626' :
                       review.overallRisk === 'medium' ? '#d97706' : '#16a34a'
              }}>{review.overallRisk}</span>
            </div>
            <p style={{ fontSize: '14px', color: '#374151', margin: 0, lineHeight: '1.5' }}>
              {review.summary}
            </p>
          </div>

          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: '20px' }}>
              <h4 style={{
                fontSize: '14px', fontWeight: '700', color: '#000000',
                paddingBottom: '6px', borderBottom: '2px solid #D4AF37',
                marginBottom: '10px'
              }}>
                {CATEGORY_LABELS[cat] || cat} ({items.length})
              </h4>
              {items.map((f, i) => {
                const s = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.info;
                return (
                  <div key={i} style={{
                    margin: '10px 0', padding: '12px',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb', borderRadius: '6px'
                  }}>
                    <div style={{
                      display: 'inline-block', padding: '3px 10px',
                      backgroundColor: s.bg, color: s.fg,
                      borderRadius: '12px', fontSize: '11px', fontWeight: '700'
                    }}>
                      {s.label}
                    </div>
                    {f.sheet && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280' }}>
                        Sheet {f.sheet}{f.location ? ` • ${f.location}` : ''}
                      </span>
                    )}
                    <div style={{
                      marginTop: '8px', fontWeight: '600',
                      fontSize: '14px', color: '#000000'
                    }}>
                      {f.issue}
                    </div>
                    {f.why && (
                      <div style={{ marginTop: '4px', fontSize: '13px', color: '#6b7280' }}>
                        {f.why}
                      </div>
                    )}
                    {f.recommendation && (
                      <div style={{
                        marginTop: '6px', fontSize: '13px',
                        fontStyle: 'italic', color: '#374151'
                      }}>
                        → {f.recommendation}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {review.missingItems?.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{
                fontSize: '14px', fontWeight: '700', color: '#000000',
                paddingBottom: '6px', borderBottom: '2px solid #D4AF37',
                marginBottom: '10px'
              }}>
                Missing Items
              </h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {review.missingItems.map((m, i) => (
                  <li key={i} style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {review.reviewerNotes && (
            <div style={{
              padding: '14px', marginBottom: '20px',
              backgroundColor: '#fffbeb', border: '1px solid #D4AF37',
              borderRadius: '8px'
            }}>
              <div style={{
                fontSize: '12px', fontWeight: '700', color: '#92400e',
                textTransform: 'uppercase', marginBottom: '6px'
              }}>
                EOR Should Double-Check
              </div>
              <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.5' }}>
                {review.reviewerNotes}
              </p>
            </div>
          )}

          {pdfB64 && (
            <div style={styles.downloadContainer}>
              <button
                onClick={handleDownloadPdf}
                style={{ ...styles.downloadButton, ...styles.pdfButton }}
              >
                Download PDF Report
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};


const getProgressPercentage = () => {
  if (!loading && !builderLoading && !updateLoading && !rfiLoading && !trackChangesLoading) return 0;
  
  const elapsed = Date.now() - (window.loadingStartTime || Date.now());
  
  if (elapsed < 3000) {
    return Math.round((elapsed / 3000) * 25);
  } else if (elapsed < 8000) {
    return Math.round(25 + ((elapsed - 3000) / 5000) * 35);
  } else if (elapsed < 15000) {
    return Math.round(60 + ((elapsed - 8000) / 7000) * 25);
  } else {
    return Math.round(85 + Math.min(((elapsed - 15000) / 10000) * 10, 10));
  }
};

useEffect(() => {
  if (loading || builderLoading || updateLoading || rfiLoading || trackChangesLoading) {
    window.loadingStartTime = Date.now();
    
    const progressInterval = setInterval(() => {
      setProgressStage(prev => prev);
    }, 500);
    
    return () => clearInterval(progressInterval);
  }
}, [loading, builderLoading, updateLoading, rfiLoading, trackChangesLoading]);

  const handleDownloadPDF = (pdfData, fileName) => {
    if (!pdfData) return;
    const link = document.createElement("a");
    link.href = "data:application/pdf;base64," + pdfData;
    link.download = fileName || "Report.pdf";
    link.click();
  };

  const handleDownloadWord = (reportData, fileName) => {
    if (!reportData) return;
    
    const mhtmlHeader = `MIME-Version: 1.0
Content-Type: multipart/related; boundary="----=_NextPart_01D7E3C3.3C8B6E30"

This is a multi-part message in MIME format.

------=_NextPart_01D7E3C3.3C8B6E30
Content-Type: text/html
Content-Transfer-Encoding: quoted-printable

<!DOCTYPE html>
<html>
<head>
    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
    <title>AECWorks AI Report</title>
    <style>
        body {
            font-family: 'Calibri', sans-serif;
            font-size: 11pt;
            line-height: 1.15;
            margin: 1in;
            color: #000;
        }
        h1 {
            color: #D4AF37;
            font-size: 16pt;
            font-weight: bold;
            margin: 24pt 0 12pt 0;
            border-bottom: 2px solid #D4AF37;
            padding-bottom: 6pt;
        }
        h2 {
            color: #D4AF37;
            font-size: 14pt;
            font-weight: bold;
            margin: 18pt 0 9pt 0;
        }
        h3 {
            color: #000000;
            font-size: 12pt;
            font-weight: bold;
            margin: 12pt 0 6pt 0;
        }
        table {
            border-collapse: collapse;
            width: 100%;
            margin: 12pt 0;
            font-size: 10pt;
        }
        th {
            background-color: #D4AF37;
            color: #000000;
            font-weight: bold;
            padding: 8pt 6pt;
            border: 1px solid #000000;
            text-align: left;
            vertical-align: top;
        }
        td {
            padding: 6pt;
            border: 1px solid #000000;
            vertical-align: top;
            text-align: left;
        }
        .status-missing {
            background-color: #ffebee;
            color: #c62828;
            font-weight: bold;
        }
        .status-compliant {
            background-color: #e8f5e8;
            color: #2e7d32;
            font-weight: bold;
        }
        .status-incomplete {
            background-color: #fff8e1;
            color: #f57c00;
            font-weight: bold;
        }
        .status-needs-review {
            background-color: #fffbeb;
            color: #D4AF37;
            font-weight: bold;
        }
        p {
            margin: 6pt 0;
        }
        .section-header {
            font-weight: bold;
            font-size: 12pt;
            margin: 12pt 0 6pt 0;
            color: #000000;
        }
        ul {
            margin: 6pt 0 6pt 20pt;
        }
        li {
            margin: 3pt 0;
        }
        .highlight-gold {
            background-color: #fffbeb;
            border-left: 3px solid #D4AF37;
            padding: 6pt 12pt;
            margin: 6pt 0;
        }
    </style>
</head>
<body>
    <h1>AECWorks AI Platform Report</h1>`;

    const lines = reportData.split('\n');
    let htmlBody = '';
    let inTable = false;
    let tableHeaders = [];
    
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        if (!line) {
            if (inTable) {
                htmlBody += '</tbody></table>\n';
                inTable = false;
                tableHeaders = [];
            }
            htmlBody += '<br>\n';
            continue;
        }

        if (line.includes('|') && line.split('|').length >= 3) {
            const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
            
            if (cells.every(cell => /^[\-\s]*$/.test(cell))) {
                continue;
            }

            if (!inTable) {
                inTable = true;
                tableHeaders = cells;
                htmlBody += '<table>\n<thead>\n<tr>';
                cells.forEach(cell => {
                    const cleanCell = cell.replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    htmlBody += `<th>${cleanCell}</th>`;
                });
                htmlBody += '</tr>\n</thead>\n<tbody>\n';
            } else {
                htmlBody += '<tr>';
                cells.forEach((cell, index) => {
                    let className = '';
                    const cellLower = cell.toLowerCase();
                    
                    if (cellLower.includes('missing') || cellLower.includes('not provided')) {
                        className = ' class="status-missing"';
                    } else if (cellLower.includes('compliant') || cellLower.includes('provided') || cellLower.includes('ok')) {
                        className = ' class="status-compliant"';
                    } else if (cellLower.includes('incomplete') || cellLower.includes('partial')) {
                        className = ' class="status-incomplete"';
                    } else if (cellLower.includes('needs review') || cellLower.includes('verify') || cellLower.includes('clarify')) {
                        className = ' class="status-needs-review"';
                    }
                    
                    const cleanCell = cell.replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    htmlBody += `<td${className}>${cleanCell}</td>`;
                });
                htmlBody += '</tr>\n';
            }
        } 
        else {
            if (inTable) {
                htmlBody += '</tbody></table>\n';
                inTable = false;
                tableHeaders = [];
            }

            if (line.startsWith('###') || line.match(/^[A-Z\s]+:$/)) {
                const headerText = line.replace(/^###\s*/, '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                htmlBody += `<h2>${headerText}</h2>\n`;
            }
            else if (line.startsWith('##') || line.match(/^\d+\.\s+[A-Z]/)) {
                const headerText = line.replace(/^##\s*/, '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                htmlBody += `<h3>${headerText}</h3>\n`;
            }
            else if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
                const bulletText = line.replace(/^[•\-\*]\s*/, '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                
                const prevLine = i > 0 ? lines[i-1].trim() : '';
                const nextLine = i < lines.length - 1 ? lines[i+1].trim() : '';
                const isFirstBullet = !prevLine.match(/^[•\-\*]/);
                const isLastBullet = !nextLine.match(/^[•\-\*]/);
                
                if (isFirstBullet) htmlBody += '<ul>\n';
                htmlBody += `<li>${bulletText}</li>\n`;
                if (isLastBullet) htmlBody += '</ul>\n';
            }
            else if (line.toLowerCase().includes('conclusion') || 
                     line.toLowerCase().includes('recommendation') || 
                     line.toLowerCase().includes('important') ||
                     line.toLowerCase().includes('note:')) {
                const cleanLine = line.replace(/</g, '&lt;').replace(/>/g, '&gt;');
                htmlBody += `<div class="highlight-gold"><p><strong>${cleanLine}</strong></p></div>\n`;
            }
            else {
                const cleanLine = line.replace(/</g, '&lt;').replace(/>/g, '&gt;');
                
                if (line.match(/^[A-Z][a-z\s]+[A-Z]/) && line.length < 80) {
                    htmlBody += `<p class="section-header">${cleanLine}</p>\n`;
                } else {
                    htmlBody += `<p>${cleanLine}</p>\n`;
                }
            }
        }
    }
    
    if (inTable) {
        htmlBody += '</tbody></table>\n';
    }
    
    const mhtmlFooter = `
</body>
</html>

------=_NextPart_01D7E3C3.3C8B6E30--`;
    
    const fullMhtml = mhtmlHeader + htmlBody + mhtmlFooter;
    const blob = new Blob([fullMhtml], { type: "application/msword" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const docFilename = fileName ? fileName.replace('.pdf', '.doc') : "Report.doc";
    link.download = docFilename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

// 5. Update handleClearAll to also clear temporary manufacturers:
const handleClearAll = () => {
  // Stop any active polling
  clearPolling();

    // Clear drawing files
  setDrawingFiles([]);
  setDrawingThumbnails([]);
  setEnableDrawingAnalysis(false);
  
  
  // Reset job tracking states
  setJobId(null);
  setQueueName(null);
  setJobStatus(null);
  setQueuePosition(0);
  setEstimatedTime('');

  // Clear multiple specs
  setSpecFiles([]);
  setSpecThumbnails([]);
  
  // Clear single submittal
  setSubmittalFile(null);
  setSubmittalThumbnail(null);
  
  // Clear Spec Check states
  setLoading(false);
  setReport(null);
  setPdfBase64(null);
  setFilename(null);
  setProcessingError(null);
  setShowClearButton(false);
  
  // Clear Spec Builder
  setMasterSpecFile(null);
  setMasterSpecThumbnail(null);
  setTemplateFile(null);
  setTemplateThumbnail(null);
  setBuilderLoading(false);
  setBuilderReport(null);
  setBuilderPdfBase64(null);
  setBuilderFilename(null);
  setBuilderError(null);
  setShowBuilderClearButton(false);
  setRequiredSections('');
  setShowSectionsDialog(false);
  
  // Clear Spec Update
  setUpdateSpecFile(null);
  setUpdateSpecThumbnail(null);
  setUpdateLoading(false);
  setUpdateReport(null);
  setUpdatePdfBase64(null);
  setUpdateFilename(null);
  setUpdateError(null);
  setShowUpdateClearButton(false);
  setEnableWebCheck(false);
  
  // Clear RFI Check
  setRfiFile(null);
  setRfiThumbnail(null);
  setProjectDocsFile(null);
  setProjectDocsThumbnail(null);
  setRfiLoading(false);
  setRfiReport(null);
  setRfiPdfBase64(null);
  setRfiFilename(null);
  setRfiError(null);
  setShowRfiClearButton(false);
  setIncludeCostAnalysis(false);

    // Clear Track Changes states - ADD THESE LINES
  setTrackChangesMasterFile(null);
  setTrackChangesMasterThumbnail(null);
  setTrackChangesCustomerFile(null);
  setTrackChangesCustomerThumbnail(null);
  setTrackChangesLoading(false);
  setTrackChangesReport(null);
  setTrackChangesPdfBase64(null);
  setTrackChangesFilename(null);
  setTrackChangesError(null);
  setShowTrackChangesClearButton(false);

  // Clear Shop Drawing Check states
  setShopDrawingFiles([]);
  setShopDrawingThumbnails([]);
  setShopDrawingProjectName('');
  setShopDrawingTrade('mechanical');
  setShopDrawingNotes('');
  
  // Clear temporary manufacturers
  setTemporaryManufacturers([]);
  
  // Clear shared progress states
  setProgressStage("");
  setProgressDetails("");
  setEstimatedChunks({ spec: 0, submittal: 0 });
  setCurrentComparison({ spec: 0, submittal: 0, total: 0 });
  
  // Reset recent docs panel
  setShowRecentDocs(false);
};

// Queue Settings Component
const QueueSettings = () => (
  <div style={{
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: '#f0f9ff',
    border: '1px solid #0ea5e9',
    borderRadius: '8px'
  }}>
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px'
    }}>
      <h4 style={{
        fontSize: '14px',
        fontWeight: '600',
        color: '#0c4a6e',
        margin: 0
      }}>
        Processing Mode
      </h4>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer'
      }}>
        <input
          type="checkbox"
          checked={useQueue}
          onChange={(e) => setUseQueue(e.target.checked)}
          style={{ width: '16px', height: '16px' }}
        />
        <span style={{ fontSize: '14px', fontWeight: '500', color: '#0c4a6e' }}>
          Use Queue System (Recommended)
        </span>
      </label>
    </div>
    <div style={{ fontSize: '12px', color: '#0369a1', lineHeight: '1.4' }}>
      {useQueue ? (
        <>
          <strong>Queue Mode:</strong> Jobs are processed in order with real-time status updates. 
          Better for handling multiple concurrent users and provides more reliable processing.
        </>
      ) : (
        <>
          <strong>Direct Mode:</strong> Immediate processing without queueing. 
          May experience delays during high traffic periods.
        </>
      )}
    </div>
    
    {jobStatus && (
      <div style={{
        marginTop: '12px',
        padding: '12px',
        backgroundColor: 'white',
        border: '1px solid #bae6fd',
        borderRadius: '6px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <strong style={{ color: '#0c4a6e', fontSize: '13px' }}>
            Job Status: {jobStatus.state}
          </strong>
          {jobStatus.state === 'active' && (
            <span style={{ 
              fontSize: '12px', 
              color: '#059669',
              fontWeight: '600' 
            }}>
              {jobStatus.progress}% Complete
            </span>
          )}
        </div>
        <div style={{ fontSize: '12px', color: '#0369a1' }}>
          Job ID: {jobId}
        </div>
        {estimatedTime && (
          <div style={{ fontSize: '12px', color: '#0369a1' }}>
            Estimated Time: {estimatedTime}
          </div>
        )}
        {queuePosition > 0 && jobStatus.state === 'waiting' && (
          <div style={{ fontSize: '12px', color: '#dc2626' }}>
            Queue Position: {queuePosition}
          </div>
        )}
      </div>
    )}
  </div>
);

// Enhanced progress tracking for queued jobs
const QueueProgressTracker = ({ loading, builderLoading, updateLoading, rfiLoading }) => {
  if (!useQueue || (!loading && !builderLoading && !updateLoading && !rfiLoading)) {
    return null;
  }

  return (
    <div style={styles.progressContainer}>
      <div style={styles.progressHeader}>
        <h3 style={styles.progressTitle}>
          {jobStatus?.state === 'waiting' ? 'Queued for Processing' : 
           jobStatus?.state === 'active' ? 'Processing Job' : 
           'Submitting Job'}
        </h3>
        {jobStatus?.state === 'active' && (
          <span style={styles.progressPercentage}>
            {jobStatus.progress || 0}%
          </span>
        )}
      </div>
      
      <div style={styles.progressBar}>
        <div 
          style={{
            ...styles.progressFill,
            width: `${jobStatus?.state === 'active' ? jobStatus.progress || 0 : 10}%`
          }}
        ></div>
      </div>
      
      <div>
        <p style={styles.progressStage}>
          {progressStage || "Processing..."}
        </p>
        {progressDetails && (
          <p style={styles.progressDetails}>
            {progressDetails}
          </p>
        )}
        
        {jobStatus && (
          <div style={{ 
            marginTop: '8px', 
            padding: '8px', 
            backgroundColor: '#f8fafc',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#64748b'
          }}>
            <div><strong>Job ID:</strong> {jobId}</div>
            <div><strong>Queue:</strong> {queueName}</div>
            <div><strong>Status:</strong> {jobStatus.state}</div>
            {jobStatus.createdOn && (
              <div><strong>Started:</strong> {new Date(jobStatus.createdOn).toLocaleTimeString()}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
    container: {
      paddingTop: '52px',
      minHeight: '100vh',
      backgroundColor: '#f9fafb'
    },
    card: {
      backgroundColor: 'white',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      borderRadius: '12px',
      padding: '32px',
      width: '100%',
      maxWidth: '1100px',
      margin: '0 auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '32px'
    },
    logo: {
      maxWidth: '320px',
      height: 'auto',
      marginBottom: '24px'
    },
    title: {
      fontSize: '1.875rem',
      fontWeight: 'bold',
      color: '#000000',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: '16px'
    },
    buttonContainer: {
      display: 'flex',
      gap: '12px',
      marginBottom: '24px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px',
      marginBottom: '24px'
    },
    dropBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      padding: '24px',
      cursor: 'pointer',
      backgroundColor: '#f9fafb',
      transition: 'all 0.2s',
      minHeight: '120px',
      justifyContent: 'center'
    },
    dropBoxActive: {
      borderColor: '#D4AF37',
      backgroundColor: '#fffbeb',
      transform: 'scale(1.02)',
      boxShadow: '0 4px 12px rgba(212, 175, 55, 0.2)'
    },
    dropBoxLabel: {
      fontWeight: '600',
      marginBottom: '12px',
      color: '#000000',
      fontSize: '16px'
    },
    dropBoxText: {
      color: '#6b7280',
      fontSize: '14px',
      textAlign: 'center'
    },
    button: {
      flex: 1,
      backgroundColor: '#D4AF37',
      color: '#000000',
      fontWeight: '600',
      padding: '12px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    buttonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
      color: '#ffffff'
    },
    clearButton: {
      backgroundColor: '#000000',
      color: '#D4AF37',
      fontWeight: '600',
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    progressContainer: {
      marginTop: '24px',
      padding: '24px',
      backgroundColor: '#fffbeb',
      border: '1px solid #D4AF37',
      borderRadius: '8px'
    },
    progressHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px'
    },
    progressTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#000000'
    },
    progressPercentage: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#D4AF37'
    },
    progressBar: {
      width: '100%',
      backgroundColor: '#f3f4f6',
      borderRadius: '9999px',
      height: '8px',
      marginBottom: '16px'
    },
    progressFill: {
      backgroundColor: '#D4AF37',
      height: '8px',
      borderRadius: '9999px',
      transition: 'width 0.5s ease-out'
    },
    progressStage: {
      color: '#000000',
      fontWeight: '500',
      marginBottom: '8px'
    },
    progressDetails: {
      color: '#6b7280',
      fontSize: '14px',
      marginBottom: '12px'
    },
    resultContainer: {
      marginTop: '32px',
      padding: '24px',
      backgroundColor: '#fffbeb',
      border: '1px solid #D4AF37',
      borderRadius: '8px'
    },
    resultTitle: {
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: '12px',
      color: '#000000'
    },
    reportBox: {
      fontSize: '14px',
      whiteSpace: 'pre-wrap',
      backgroundColor: 'white',
      padding: '16px',
      borderRadius: '6px',
      border: '1px solid #e5e7eb',
      maxHeight: '384px',
      overflowY: 'auto'
    },
    downloadContainer: {
      display: 'flex',
      gap: '16px',
      marginTop: '16px',
      justifyContent: 'center',
      flexWrap: 'wrap'
    },
    downloadButton: {
      padding: '8px 16px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      color: 'white',
      fontWeight: '500',
      transition: 'background-color 0.2s'
    },
    pdfButton: {
      backgroundColor: '#D4AF37',
      color: '#000000'
    },
    wordButton: {
      backgroundColor: '#000000',
      color: '#D4AF37'
    },
    errorText: {
      color: '#dc2626',
      marginTop: '16px',
      textAlign: 'center'
    },
    dialogOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    dialogContent: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '32px',
      maxWidth: '500px',
      width: '100%',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
    },
    textarea: {
      width: '100%',
      minHeight: '120px',
      padding: '12px',
      border: '2px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      fontFamily: 'inherit',
      resize: 'vertical',
      marginBottom: '20px'
    },
    dialogButtons: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },
    dialogButton: {
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '14px',
      transition: 'background-color 0.2s'
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={{ color: '#dc2626', textAlign: 'center' }}>Authentication Error</h2>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>{error.message}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen onLogin={loginWithRedirect} />;
  }

  function renderDropBox(label, file, dropzone, styles, thumbnail = null) {
  const { getRootProps, getInputProps, isDragActive } = dropzone;

  return (
    <div
      {...getRootProps()}
      style={{
        ...styles.dropBox,
        ...(isDragActive ? styles.dropBoxActive : {}),
        position: 'relative'
      }}
    >
      <input {...getInputProps()} />
      <p style={styles.dropBoxLabel}>{label}</p>
      
      {file && thumbnail ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div 
            style={{
              position: 'relative',
              cursor: 'pointer'
            }}
            title={`${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)`}
          >
            <img
              src={thumbnail}
              alt={`${file.name} thumbnail`}
              style={{
                width: '80px',
                height: '100px',
                objectFit: 'cover',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                e.target.style.zIndex = '10';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
                e.target.style.zIndex = '1';
              }}
            />
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#000000', 
              margin: '0 0 4px 0',
              wordBreak: 'break-word'
            }}>
              {file.name.length > 20 ? file.name.substring(0, 17) + '...' : file.name}
            </p>
            <p style={{ 
              fontSize: '12px', 
              color: '#6b7280', 
              margin: 0 
            }}>
              {(file.size / 1024 / 1024).toFixed(1)} MB
            </p>
          </div>
        </div>
      ) : (
        <p style={styles.dropBoxText}>
          {isDragActive 
            ? "Drop files here..." 
            : file 
              ? `${file.name}` 
              : "Drag & drop file here or click to select"
          }
        </p>
      )}
      
      {isDragActive && (
        <p style={{ 
          color: '#D4AF37', 
          fontSize: '12px', 
          marginTop: '8px',
          fontWeight: '500'
        }}>
          Release to upload
        </p>
      )}
    </div>
  );
}

// Recent Documents Panel Component
const RecentDocumentsPanel = ({ activeTab }) => {
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [loadingDocId, setLoadingDocId] = useState(null);
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState(null);

  if (getFlag && !getFlag('platformFiles')?.enabled) return null;

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return 'Permanent';
    const remaining = new Date(expiresAt) - new Date();
    if (remaining < 0) return 'Expired';
    const hours = Math.floor(remaining / 3600000);
    if (hours < 1) return 'Less than 1hr';
    if (hours < 24) return hours + 'hr remaining';
    return Math.floor(hours / 24) + 'd ' + (hours % 24) + 'hr remaining';
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  // Get slot options based on active tab
  const getSlotOptions = () => {
    switch (activeTab) {
      case 'spec-check':
        return [
          { id: 'spec', label: 'Specifications' },
          { id: 'submittal', label: 'Submittal' },
          { id: 'drawing', label: 'Drawing' }
        ];
      case 'spec-builder':
        return [
          { id: 'masterSpec', label: 'Master Spec' },
          { id: 'template', label: 'Template' }
        ];
      case 'spec-update':
        return [{ id: 'updateSpec', label: 'Spec Sheet' }];
      case 'rfi-check':
        return [
          { id: 'rfi', label: 'RFI Document' },
          { id: 'projectDocs', label: 'Project Docs' },
          { id: 'drawing', label: 'Drawing' }
        ];
      case 'track-changes':
        return [
          { id: 'trackMaster', label: 'Master Spec' },
          { id: 'trackCustomer', label: 'Customer Spec' }
        ];
      case 'drawing-compare':
        return [
          { id: 'newDrawing', label: 'New Drawing' },
          { id: 'baselineDrawing', label: 'Baseline Drawing' }
        ];
      case 'design-vs-shop':
        return [
          { id: 'designDrawing', label: 'Design Drawing' },
          { id: 'shopDrawingForReview', label: 'Shop Drawing' }
        ];
      default:
        return [];
    }
  };

  const handleUseDoc = async (doc) => {
    const slots = getSlotOptions();
    if (slots.length === 1) {
      setLoadingDocId(doc._id);
      await loadCachedDocument(doc, slots[0].id);
      setLoadingDocId(null);
    } else if (selectedTarget) {
      setLoadingDocId(doc._id);
      await loadCachedDocument(doc, selectedTarget);
      setLoadingDocId(null);
      setSelectedTarget(null);
    } else {
      setSelectedTarget('picking-' + doc._id);
    }
  };

  const getDocTypeIcon = (docType) => {
    switch (docType) {
      case 'spec': return '📄';
      case 'submittal': return '📋';
      case 'drawing': return '🖼️';
      case 'rfi': return '❓';
      default: return '📁';
    }
  };

  if (activeTab === 'faq') return null;

  return (
    <div style={{
      marginBottom: '20px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      <button
        onClick={() => {
          setShowRecentDocs(!showRecentDocs);
        }}
        style={{
          width: '100%',
          padding: '14px 20px',
          backgroundColor: showRecentDocs ? '#fffbeb' : '#f9fafb',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'background-color 0.2s'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '18px' }}>📂</span>
          <span style={{ 
            fontSize: '15px', 
            fontWeight: '600', 
            color: '#000000' 
          }}>
            Recent Documents
          </span>
          {cachedDocuments.length > 0 && (
            <span style={{
              backgroundColor: '#D4AF37',
              color: '#000000',
              fontSize: '11px',
              fontWeight: '700',
              padding: '2px 8px',
              borderRadius: '10px'
            }}>
              {cachedDocuments.length}
            </span>
          )}
        </div>
        <span style={{
          fontSize: '16px',
          color: '#D4AF37',
          transform: showRecentDocs ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }}>
          ▼
        </span>
      </button>

      {showRecentDocs && (
        <div style={{ 
          padding: '16px 20px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: 'white'
        }}>
          {cachedDocsLoading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              Loading recent documents...
            </div>
          ) : cachedDocsError ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#dc2626' }}>
              {cachedDocsError}
              <button 
                onClick={fetchCachedDocuments}
                style={{
                  marginLeft: '12px',
                  color: '#D4AF37',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  textDecoration: 'underline'
                }}
              >
                Retry
              </button>
            </div>
          ) : cachedDocuments.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '20px', 
              color: '#6b7280',
              fontSize: '14px'
            }}>
              No cached documents found. Documents you upload during analysis will appear here for 48 hours.
            </div>
          ) : (
            <>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  Click a document to load it into an upload slot
                </span>
                <button
                  onClick={fetchCachedDocuments}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#D4AF37',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  🔄 Refresh
                </button>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '10px'
              }}>
                {cachedDocuments.map((doc) => (
                  <div key={doc._id} style={{
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    transition: 'border-color 0.2s'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>
                        {getDocTypeIcon(doc.docType)}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#000000',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {doc.originalName}
                        </div>
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#6b7280',
                          display: 'flex',
                          gap: '8px',
                          marginTop: '2px'
                        }}>
                          <span>{formatFileSize(doc.fileSize)}</span>
                          <span>•</span>
                          <span>{doc.docType || 'general'}</span>
                          <span>•</span>
                          <span>{formatExpiry(doc.expiresAt)}</span>
                        </div>
                        {doc.projectName && (
                          <div style={{ fontSize: '11px', color: '#D4AF37', marginTop: '2px' }}>
                            {doc.projectName}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                      {selectedTarget === 'picking-' + doc._id ? (
                        // Show slot picker
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', flex: 1 }}>
                          {getSlotOptions().map(slot => (
                            <button
                              key={slot.id}
                              onClick={async () => {
                                setLoadingDocId(doc._id);
                                setSelectedTarget(null);
                                await loadCachedDocument(doc, slot.id);
                                setLoadingDocId(null);
                              }}
                              style={{
                                padding: '4px 10px',
                                fontSize: '11px',
                                fontWeight: '600',
                                backgroundColor: '#D4AF37',
                                color: '#000000',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              → {slot.label}
                            </button>
                          ))}
                          <button
                            onClick={() => setSelectedTarget(null)}
                            style={{
                              padding: '4px 8px',
                              fontSize: '11px',
                              backgroundColor: '#e5e7eb',
                              color: '#6b7280',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => handleUseDoc(doc)}
                            disabled={loadingDocId === doc._id}
                            style={{
                              flex: 1,
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: loadingDocId === doc._id ? '#e5e7eb' : '#D4AF37',
                              color: '#000000',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: loadingDocId === doc._id ? 'not-allowed' : 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                          >
                            {loadingDocId === doc._id ? 'Loading...' : 'Use'}
                          </button>
                          <button
                            onClick={() => setDeleteConfirmDoc(doc)}
                            style={{
                              padding: '6px 10px',
                              fontSize: '12px',
                              backgroundColor: 'transparent',
                              color: '#dc2626',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmDoc && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '28px',
            maxWidth: '420px',
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <span style={{
                fontSize: '28px',
                width: '48px',
                height: '48px',
                backgroundColor: '#fef2f2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>🗑️</span>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#000000',
                margin: 0
              }}>
                Delete Cached Document
              </h3>
            </div>

            <div style={{
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>
                  {getDocTypeIcon(deleteConfirmDoc.docType)}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#000000',
                    marginBottom: '8px',
                    wordBreak: 'break-word'
                  }}>
                    {deleteConfirmDoc.originalName}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <div>
                      <strong>Size:</strong> {formatFileSize(deleteConfirmDoc.fileSize)}
                    </div>
                    <div>
                      <strong>Type:</strong> {deleteConfirmDoc.docType || 'general'}
                    </div>
                    <div>
                      <strong>Uploaded:</strong> {formatDate(deleteConfirmDoc.uploadedAt)}
                    </div>
                    <div>
                      <strong>Expires:</strong> {formatExpiry(deleteConfirmDoc.expiresAt)}
                    </div>
                    {deleteConfirmDoc.projectName && (
                      <div>
                        <strong>Project:</strong> {deleteConfirmDoc.projectName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <p style={{
              fontSize: '13px',
              color: '#6b7280',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              This will permanently remove this document from your cache. You'll need to re-upload it for future analyses.
            </p>

            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setDeleteConfirmDoc(null)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteCachedDocument(deleteConfirmDoc._id);
                  setDeleteConfirmDoc(null);
                }}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                Delete Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── PLATFORM FILES PANEL ────────────────────────────────────────────────────
// Drop this component definition right after the RecentDocumentsPanel component
// (around line 5240 in App.js, just before the closing of RecentDocumentsPanel)
//
// Then add <PlatformFilesPanel activeTab={activeTab} user={user} integrationStatus={integrationStatus} onRefreshStatus={() => fetchIntegrationStatus(user)} /> right after EACH
// <RecentDocumentsPanel activeTab={activeTab} /> line (lines 3226, 3403, 5251, 5697, 5795)
//
// Also add these two state vars near the other cached docs states (around line 1585):
//   const [showPlatformFiles, setShowPlatformFiles] = useState(false);
//   const [platformFilesStatus, setPlatformFilesStatus] = useState(null);
// ─────────────────────────────────────────────────────────────────────────────

const PlatformFilesPanel = ({ activeTab, user, integrationStatus, onRefreshStatus }) => {
  const [isOpen, setIsOpen] = useState(false);
  // Use shared status from App.js — no local fetch needed
  const status = integrationStatus;
  const [activePlatform, setActivePlatform] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(null); // fileId being loaded
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [error, setError] = useState(null);

  const API_BASE = 'https://apispeccheck.zonetap.com';
  const userId = user?.email || user?.sub || 'anonymous';

  // Same slot options as RecentDocumentsPanel
  const getSlotOptions = () => {
    switch (activeTab) {
      case 'spec-check':
        return [
          { id: 'spec', label: 'Specifications' },
          { id: 'submittal', label: 'Submittal' },
          { id: 'drawing', label: 'Drawing' }
        ];
      case 'spec-builder':
        return [
          { id: 'masterSpec', label: 'Master Spec' },
          { id: 'template', label: 'Template' }
        ];
      case 'spec-update':
        return [{ id: 'updateSpec', label: 'Spec Sheet' }];
      case 'rfi-check':
        return [
          { id: 'rfi', label: 'RFI Document' },
          { id: 'projectDocs', label: 'Project Docs' },
          { id: 'drawing', label: 'Drawing' }
        ];
      case 'track-changes':
        return [
          { id: 'trackMaster', label: 'Master Spec' },
          { id: 'trackCustomer', label: 'Customer Spec' }
        ];
      case 'drawing-compare':
        return [
          { id: 'newDrawing', label: 'New Drawing' },
          { id: 'baselineDrawing', label: 'Baseline Drawing' }
        ];
      case 'design-vs-shop':
        return [
          { id: 'designDrawing', label: 'Design Drawing' },
          { id: 'shopDrawingForReview', label: 'Shop Drawing' }
        ];
      default:
        return [];
    }
  };

  const handleOpen = () => {
    setIsOpen(prev => !prev);
  };

  // Load projects for a platform
  const loadProjects = async (platform) => {
    setActivePlatform(platform);
    setSelectedProject(null);
    setFiles([]);
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/files/${platform}/projects?userId=${encodeURIComponent(userId)}`
      );
      if (!res.ok) throw new Error('Failed to load projects');
      const data = await res.json();
      setProjects(data.projects || []);
    } catch (err) {
      setError('Could not load projects.');
    } finally {
      setLoading(false);
    }
  };

  // Load files for a project
  const loadFiles = async (project) => {
    setSelectedProject(project);
    setFiles([]);
    setError(null);
    setLoading(true);
    try {
      const endpoint = activePlatform === 'procore'
        ? `${API_BASE}/api/files/procore/projects/${project.id}/documents?userId=${encodeURIComponent(userId)}`
        : `${API_BASE}/api/files/autodesk/projects/${project.id}/folders?userId=${encodeURIComponent(userId)}`;
      const res = await fetch(endpoint);
      if (!res.ok) throw new Error('Failed to load files');
      const data = await res.json();
      setFiles(data.documents || data.folders || []);
    } catch (err) {
      setError('Could not load files.');
    } finally {
      setLoading(false);
    }
  };

  // Load a platform file into an upload slot — downloads on demand
  const loadPlatformFile = async (file, slotId) => {
    setFileLoading(file.id);
    setSelectedTarget(null);
    try {
      // Download the file buffer from the platform via backend proxy
      const res = await fetch(`${API_BASE}/api/files/${activePlatform}/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          fileId: file.id,
          fileName: file.name,
          downloadUrl: file.downloadUrl || null
        })
      });
      if (!res.ok) throw new Error('Download failed');
      const data = await res.json();

      // Convert base64 to File object — same pattern as loadCachedDocument
      const byteString = atob(data.buffer);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: 'application/pdf' });
      const fileObj = new File([blob], file.name, { type: 'application/pdf' });

      // Route to slot
      switch (slotId) {
        case 'spec': await onDropSpec([fileObj]); break;
        case 'submittal': await onDropSubmittal([fileObj]); break;
        case 'drawing': await onDropDrawings([fileObj]); break;
        case 'masterSpec': await onDropMasterSpec([fileObj]); break;
        case 'template': await onDropTemplate([fileObj]); break;
        case 'updateSpec': await onDropUpdateSpec([fileObj]); break;
        case 'rfi': await onDropRfi([fileObj]); break;
        case 'projectDocs': await onDropProjectDocs([fileObj]); break;
        case 'trackMaster': await onDropTrackChangesMaster([fileObj]); break;
        case 'trackCustomer': await onDropTrackChangesCustomer([fileObj]); break;
        case 'newDrawing': setDrawingCompareNewFile(fileObj); break;
        case 'baselineDrawing': setDrawingCompareBaselineFile(fileObj); break;
        case 'designDrawing': setDesignVsShopDesignFile(fileObj); break;
        case 'shopDrawingForReview': setDesignVsShopShopFile(fileObj); break;
        default: break;
      }
    } catch (err) {
      console.error('Failed to load platform file:', err);
      alert('Failed to load file from platform. Please try again.');
    } finally {
      setFileLoading(null);
    }
  };

  const handleUseFile = (file) => {
    const slots = getSlotOptions();
    if (slots.length === 1) {
      loadPlatformFile(file, slots[0].id);
    } else {
      setSelectedTarget('picking-' + file.id);
    }
  };

  const connected = status
    ? [
        status.procore?.connected && { platform: 'procore', label: 'Procore', icon: '🏗️' },
        status.autodesk?.connected && { platform: 'autodesk', label: 'Autodesk', icon: '🔷' }
      ].filter(Boolean)
    : [];

  if (activeTab === 'faq' || activeTab === 'integrations') return null;
  if (!getFlag('integrationFileBrowsing')?.enabled) return null;

  return (
    <div style={{
      marginBottom: '20px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <button
        onClick={handleOpen}
        style={{
          width: '100%',
          padding: '14px 20px',
          backgroundColor: isOpen ? '#fffbeb' : '#f9fafb',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '18px' }}>🔗</span>
          <span style={{ fontSize: '15px', fontWeight: '600', color: '#000000' }}>
            Platform Files
          </span>
          {connected.length > 0 && (
            <div style={{ display: 'flex', gap: '6px' }}>
              {connected.map(c => (
                <span key={c.platform} style={{
                  backgroundColor: '#D4AF37',
                  color: '#000000',
                  fontSize: '11px',
                  fontWeight: '700',
                  padding: '2px 8px',
                  borderRadius: '10px'
                }}>
                  {c.icon} {c.label}
                </span>
              ))}
            </div>
          )}
          {connected.length === 0 && status && (
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
              No platforms connected — connect in AW Integrations tab
            </span>
          )}
        </div>
        <span style={{
          fontSize: '16px',
          color: '#D4AF37',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }}>▼</span>
      </button>

      {/* Body */}
      {isOpen && (
        <div style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb', backgroundColor: 'white' }}>

          {/* Not connected state */}
          {status && connected.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '14px' }}>
              Connect Procore or Autodesk in the AW Integrations tab to browse platform files here.
            </div>
          )}

          {/* Platform selector */}
          {connected.length > 0 && !activePlatform && (
            <div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                Select a platform to browse files:
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {connected.map(c => (
                  <button
                    key={c.platform}
                    onClick={() => loadProjects(c.platform)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#D4AF37',
                      color: '#000000',
                      fontWeight: '600',
                      fontSize: '13px',
                      border: 'none',
                      borderRadius: '7px',
                      cursor: 'pointer'
                    }}
                  >
                    {c.icon} Browse {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Browser UI */}
          {activePlatform && (
            <div>
              {/* Platform header + back */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#000000' }}>
                  {activePlatform === 'procore' ? '🏗️ Procore' : '🔷 Autodesk'}
                </span>
                {selectedProject && (
                  <>
                    <span style={{ color: '#9ca3af' }}>›</span>
                    <span style={{ fontSize: '13px', color: '#374151' }}>{selectedProject.name}</span>
                  </>
                )}
                <button
                  onClick={() => {
                    if (selectedProject) { setSelectedProject(null); setFiles([]); }
                    else { setActivePlatform(null); setProjects([]); }
                  }}
                  style={{
                    marginLeft: 'auto',
                    background: 'none',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '12px',
                    color: '#6b7280',
                    cursor: 'pointer'
                  }}
                >
                  ← Back
                </button>
                {connected.length > 1 && !selectedProject && (
                  <button
                    onClick={() => { setActivePlatform(null); setProjects([]); }}
                    style={{
                      background: 'none',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '4px 10px',
                      fontSize: '12px',
                      color: '#6b7280',
                      cursor: 'pointer'
                    }}
                  >
                    Switch Platform
                  </button>
                )}
              </div>

              {loading && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>
                  ⏳ Loading...
                </div>
              )}

              {error && (
                <div style={{ padding: '10px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '6px', fontSize: '13px' }}>
                  {error}
                </div>
              )}

              {/* Projects list */}
              {!loading && !error && !selectedProject && (
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                    {projects.length} project{projects.length !== 1 ? 's' : ''} — click to browse files
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {projects.map(proj => (
                      <button
                        key={proj.id}
                        onClick={() => loadFiles(proj)}
                        style={{
                          textAlign: 'left',
                          padding: '10px 14px',
                          backgroundColor: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '7px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#000000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          📁 {proj.name}
                        </span>
                        <span style={{ color: '#D4AF37', flexShrink: 0 }}>›</span>
                      </button>
                    ))}
                    {projects.length === 0 && (
                      <div style={{ fontSize: '13px', color: '#9ca3af', padding: '12px' }}>No projects found</div>
                    )}
                  </div>
                </div>
              )}

              {/* Files list */}
              {!loading && !error && selectedProject && (
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                    {files.length} file{files.length !== 1 ? 's' : ''} — click Use to load into a slot
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: '8px',
                    maxHeight: '240px',
                    overflowY: 'auto'
                  }}>
                    {files.map(file => (
                      <div key={file.id} style={{
                        padding: '10px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        backgroundColor: '#fafafa',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <span style={{ fontSize: '18px', flexShrink: 0 }}>📄</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#000000',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {file.name}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                              {activePlatform === 'procore' ? '🏗️ Procore' : '🔷 Autodesk'}
                            </div>
                          </div>
                        </div>

                        {/* Slot picker */}
                        {selectedTarget === 'picking-' + file.id ? (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {getSlotOptions().map(slot => (
                              <button
                                key={slot.id}
                                onClick={() => loadPlatformFile(file, slot.id)}
                                style={{
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  backgroundColor: '#D4AF37',
                                  color: '#000000',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                → {slot.label}
                              </button>
                            ))}
                            <button
                              onClick={() => setSelectedTarget(null)}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                backgroundColor: '#e5e7eb',
                                color: '#6b7280',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >✕</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleUseFile(file)}
                            disabled={fileLoading === file.id}
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: fileLoading === file.id ? '#e5e7eb' : '#D4AF37',
                              color: '#000000',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: fileLoading === file.id ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {fileLoading === file.id ? '⏳ Loading...' : 'Use'}
                          </button>
                        )}
                      </div>
                    ))}
                    {files.length === 0 && (
                      <div style={{ fontSize: '13px', color: '#9ca3af', padding: '12px' }}>No files found in this project</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
// ─── END PLATFORM FILES PANEL ─────────────────────────────────────────────────

  const renderSpecCheck = () => (
  <>
    {false && <QueueSettings />}
    
    <RecentDocumentsPanel activeTab={activeTab} />
    <PlatformFilesPanel activeTab={activeTab} user={user} integrationStatus={integrationStatus} onRefreshStatus={() => fetchIntegrationStatus(user)} />
    
    <div style={styles.grid}>
      <MultiSpecDropBox
        files={specFiles}
        thumbnails={specThumbnails}
        dropzone={specDropzone}
        styles={styles}
        onRemove={removeSpecFile}
      />
      <DropBoxComponent 
        label="Submittals Document" 
        file={submittalFile} 
        dropzone={submittalDropzone} 
        styles={styles} 
        thumbnail={submittalThumbnail} 
      />
    </div>

    {/* Drawing Analysis Section for Spec Check */}
    <div style={{
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: '#f0f9ff',
      border: '1px solid #0ea5e9',
      borderRadius: '8px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          <input
            type="checkbox"
            checked={enableDrawingAnalysis}
            onChange={(e) => {
              setEnableDrawingAnalysis(e.target.checked);
              if (!e.target.checked) {
                setDrawingFiles([]);
                setDrawingThumbnails([]);
              }
            }}
            style={{
              width: '16px',
              height: '16px',
              cursor: 'pointer'
            }}
          />
          <div style={{ color: '#0c4a6e' }}>
            Enable Visual Drawing Compliance Analysis
          </div>
        </label>
        
        {drawingFiles.length > 0 && (
          <span style={{
            fontSize: '12px',
            color: '#0369a1',
            fontWeight: '500'
          }}>
            {drawingFiles.length}/20 drawings uploaded
          </span>
        )}
      </div>
      
      <div style={{ 
        color: '#0369a1', 
        fontSize: '12px',
        lineHeight: '1.4',
        marginBottom: enableDrawingAnalysis ? '16px' : '0'
      }}>
        Upload architectural/construction drawings to verify visual compliance with specifications and submittals. 
        AECWorks AI will analyze dimensions, materials, details, and compare what's shown in drawings against spec requirements.
        <br />
        <strong>Supported formats:</strong> JPEG, PNG, WEBP, GIF, PDF (up to 20 files)
      </div>

      {enableDrawingAnalysis && (
        <div style={{ marginTop: '12px' }}>
          {drawingFiles.length === 0 ? (
            <div
              {...drawingsDropzone.getRootProps()}
              style={{
                ...styles.dropBox,
                ...(isDrawingDragActive ? styles.dropBoxActive : {}),
                backgroundColor: '#f8fafc',
                border: '2px dashed #0ea5e9',
                minHeight: '100px'
              }}
            >
              <input {...drawingsDropzone.getInputProps()} />
              <p style={{
                ...styles.dropBoxLabel,
                color: '#0c4a6e',
                fontSize: '14px'
              }}>
                Upload Drawing Files
              </p>
              <p style={{
                ...styles.dropBoxText,
                color: '#0369a1'
              }}>
                {isDrawingDragActive 
                  ? "Drop drawing files here..." 
                  : "Drag & drop drawing files here or click to select (up to 20 files)"
                }
              </p>
            </div>
          ) : (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0c4a6e',
                  margin: 0
                }}>
                  Drawing Files ({drawingFiles.length})
                </h4>
                <button
                  onClick={() => removeDrawingFile('all')}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '8px',
                marginBottom: '12px'
              }}>
                {drawingFiles.map((file, index) => (
                  <div key={`${file.name}-${index}`} style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '6px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #bae6fd'
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeDrawingFile(index);
                      }}
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                      }}
                    >
                      ×
                    </button>
                    
                    {drawingThumbnails[index] && (
                      <div
                        style={{
                          position: 'relative',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          setDrawingMousePosition({ x: e.clientX, y: e.clientY });
                          setPreviewDrawingFile(file);
                          setPreviewDrawingThumbnail(drawingThumbnails[index]);
                          setShowDrawingPreview(true);
                        }}
                        onMouseLeave={() => {
                          setShowDrawingPreview(false);
                          setPreviewDrawingFile(null);
                          setPreviewDrawingThumbnail(null);
                        }}
                        title={`Click to preview ${file.name}`}
                      >
                        <img
                          src={drawingThumbnails[index]}
                          alt={`${file.name} thumbnail`}
                          style={{
                            width: '50px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #bae6fd',
                            marginBottom: '4px',
                            transition: 'all 0.2s ease',
                            boxShadow: showDrawingPreview && previewDrawingFile === file ? '0 8px 16px rgba(14, 165, 233, 0.3)' : 'none',
                            transform: showDrawingPreview && previewDrawingFile === file ? 'scale(1.05)' : 'scale(1)'
                          }}
                        />
                      </div>
                    )}
                    
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ 
                        fontSize: '10px', 
                        fontWeight: '600', 
                        color: '#0c4a6e', 
                        margin: '0 0 2px 0',
                        wordBreak: 'break-word',
                        lineHeight: '1.2'
                      }}>
                        {file.name.length > 12 ? file.name.substring(0, 9) + '...' : file.name}
                      </p>
                      <p style={{ 
                        fontSize: '9px', 
                        color: '#0369a1', 
                        margin: 0 
                      }}>
                        {(file.size / 1024 / 1024).toFixed(1)}MB
                      </p>
                    </div>
                  </div>
                ))}
                
                {drawingFiles.length < 20 && (
                  <div 
                    {...drawingsDropzone.getRootProps()}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px',
                      backgroundColor: '#f0f9ff',
                      border: '2px dashed #0ea5e9',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      minHeight: '80px',
                      position: 'relative'
                    }}
                  >
                    <input 
                      {...drawingsDropzone.getInputProps()} 
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer'
                      }}
                    />
                    <p style={{
                      color: '#0ea5e9',
                      fontSize: '18px',
                      margin: '0 0 2px 0'
                    }}>+</p>
                    <p style={{
                      color: '#0ea5e9',
                      fontSize: '9px',
                      textAlign: 'center',
                      margin: 0,
                      lineHeight: '1.2'
                    }}>
                      Add More
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>

    {/* Drawing Preview Modal */}
    <FilePreviewModal
      file={previewDrawingFile}
      thumbnail={previewDrawingThumbnail}
      isVisible={showDrawingPreview}
      position={drawingMousePosition}
      onClose={() => setShowDrawingPreview(false)}
    />

    <UsageBar featureKey="specCheck" getFlag={getFlag} label="Spec Check" />
    <div style={{
  marginBottom: '16px',
  padding: '12px 16px',
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}}>
  <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Report Type:</span>
  <div style={{ display: 'flex', gap: '4px', padding: '3px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px' }}>
    <button
      type="button"
      onClick={() => setSpecCheckMode('standard')}
      style={{
        padding: '5px 12px', fontSize: '12px', fontWeight: '600', border: 'none', borderRadius: '4px', cursor: 'pointer',
        backgroundColor: specCheckMode === 'standard' ? '#D4AF37' : 'transparent',
        color: specCheckMode === 'standard' ? '#000000' : '#6b7280',
      }}
    >
      Standard
    </button>
    <button
      type="button"
      onClick={() => setSpecCheckMode('advanced')}
      style={{
        padding: '5px 12px', fontSize: '12px', fontWeight: '600', border: 'none', borderRadius: '4px', cursor: 'pointer',
        backgroundColor: specCheckMode === 'advanced' ? '#D4AF37' : 'transparent',
        color: specCheckMode === 'advanced' ? '#000000' : '#6b7280',
      }}
    >
      Advanced
    </button>
  </div>
  <span style={{ fontSize: '11px', color: '#9ca3af' }}>
    {specCheckMode === 'standard'
      ? 'Missing/non-compliant items only'
      : 'Full compliance analysis with scoring, conflicts, and QA review'}
  </span>
</div>
    <div style={styles.buttonContainer}>
      <button
        onClick={handleSpecCheckSubmit}
        disabled={loading}
        style={{
          ...styles.button,
          ...(loading ? styles.buttonDisabled : {})
        }}
      >
        {loading 
          ? (useQueue ? "Processing in Queue..." : "Processing...") 
          : `Analyze ${specFiles.length > 1 ? `${specFiles.length} Specs` : 'Spec'} vs Submittal${enableDrawingAnalysis ? ' (with Drawings)' : ''}`
        }
      </button>
      
      {showClearButton && (
        <button
          onClick={handleClearAll}
          style={styles.clearButton}
        >
          🗑️ Clear All
        </button>
      )}
    </div>

    {specFiles.length > 1 && (
      <div style={{
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#eff6ff',
        border: '1px solid #3b82f6',
        borderRadius: '8px'
      }}>
        <h4 style={{ 
          color: '#1d4ed8', 
          marginBottom: '8px', 
          fontSize: '14px',
          fontWeight: '600' 
        }}>
          Multiple Specifications Analysis
        </h4>
        <p style={{ 
          color: '#1e40af', 
          fontSize: '13px', 
          margin: 0,
          lineHeight: '1.4' 
        }}>
          AECWorks AI will cross-reference all {specFiles.length} specification documents against your submittal. 
          The analysis will identify conflicts between specs and ensure compliance with the most restrictive requirements.
        </p>
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#1e40af' }}>
          <strong>Files to analyze:</strong>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {specFiles.map((file, index) => (
              <li key={index}>{file.name}</li>
            ))}
          </ul>
        </div>
      </div>
    )}

    <QueueProgressTracker 
      loading={loading}
      builderLoading={false}
      updateLoading={false}
      rfiLoading={false}
    />

    {!useQueue && loading && (
      <div style={styles.progressContainer}>
        <div style={styles.progressHeader}>
          <h3 style={styles.progressTitle}>
            Processing {specFiles.length > 1 ? `${specFiles.length} Specifications` : 'Documents'}
            {enableDrawingAnalysis && drawingFiles.length > 0 && ` + ${drawingFiles.length} Drawings`}
          </h3>
          <span style={styles.progressPercentage}>{Math.round(getProgressPercentage())}%</span>
        </div>
        
        <div style={styles.progressBar}>
          <div 
            style={{
              ...styles.progressFill,
              width: `${getProgressPercentage()}%`
            }}
          ></div>
        </div>
        
        <div>
          <p style={styles.progressStage}>{progressStage}</p>
          {progressDetails && (
            <p style={styles.progressDetails}>
              {progressDetails}
              {enableDrawingAnalysis && drawingFiles.length > 0 && ` (analyzing ${drawingFiles.length} architectural drawings)`}
            </p>
          )}
        </div>
      </div>
    )}

    {processingError && <p style={styles.errorText}>{processingError}</p>}

    {report && !loading && (
      <div style={styles.resultContainer}>
        <h3 style={styles.resultTitle}>
          Analysis Complete! 
          {specFiles.length > 1 && ` (${specFiles.length} Specs Reviewed)`}
          {enableDrawingAnalysis && drawingFiles.length > 0 && ` (${drawingFiles.length} drawings analyzed)`}
        </h3>
        <pre style={styles.reportBox}>{report}</pre>

        <div style={styles.downloadContainer}>
          {pdfBase64 && (
            <button
              onClick={() => handleDownloadPDF(pdfBase64, filename)}
              style={{...styles.downloadButton, ...styles.pdfButton}}
            >
              Download PDF Report
            </button>
          )}
          <button
            onClick={() => handleDownloadWord(report, filename)}
            style={{...styles.downloadButton, ...styles.wordButton}}
          >
            Download Word (.doc)
          </button>
        </div>
      </div>
    )}
  </>
);


  const renderSpecBuilder = () => (
    <>

    {false && <QueueSettings />}

    <RecentDocumentsPanel activeTab={activeTab} />
    <PlatformFilesPanel activeTab={activeTab} user={user} integrationStatus={integrationStatus} onRefreshStatus={() => fetchIntegrationStatus(user)} />

<div style={styles.grid}>
  <DropBoxComponent
    label="Master Spec Sheet"
    file={masterSpecFile}
    dropzone={masterSpecDropzone}
    styles={styles}
    thumbnail={masterSpecThumbnail}
  />
  <DropBoxComponent
    label="Outline Template"
    file={templateFile}
    dropzone={templateDropzone}
    styles={styles}
    thumbnail={templateThumbnail}
  />
</div>

      <div style={styles.buttonContainer}>
    <UsageBar featureKey="specBuilder" getFlag={getFlag} label="Spec Builder" />
        <button
          onClick={handleSpecBuilderSubmit}
          disabled={builderLoading}
          style={{
            ...styles.button,
            ...(builderLoading ? styles.buttonDisabled : {})
          }}
        >
          {builderLoading ? "Building..." : "Build Spec Outline"}
        </button>
        
        {showBuilderClearButton && (
          <button
            onClick={handleClearAll}
            style={styles.clearButton}
          >
            🗑️ Clear All
          </button>
        )}
      </div>

      {builderLoading && (
        <div style={styles.progressContainer}>
          <div style={styles.progressHeader}>
            <h3 style={styles.progressTitle}>Building Spec Outline</h3>
            <span style={styles.progressPercentage}>{Math.round(getProgressPercentage())}%</span>
          </div>
          
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: `${getProgressPercentage()}%`
              }}
            ></div>
          </div>
          
          <div>
            <p style={styles.progressStage}>{progressStage}</p>
            {progressDetails && (
              <p style={styles.progressDetails}>{progressDetails}</p>
            )}
          </div>
        </div>
      )}

      {builderError && <p style={styles.errorText}>{builderError}</p>}

      {builderReport && !builderLoading && (
        <div style={styles.resultContainer}>
          <h3 style={styles.resultTitle}>Spec Outline Generated!</h3>
          <pre style={styles.reportBox}>{builderReport}</pre>

          <div style={styles.downloadContainer}>
            {builderPdfBase64 && (
              <button
                onClick={() => handleDownloadPDF(builderPdfBase64, builderFilename)}
                style={{...styles.downloadButton, ...styles.pdfButton}}
              >
                Download PDF
              </button>
            )}
            <button
              onClick={() => handleDownloadWord(builderReport, builderFilename)}
              style={{...styles.downloadButton, ...styles.wordButton}}
            >
              Download Word (.doc)
            </button>
          </div>
        </div>
      )}
    </>
  );

  const renderSpecUpdate = () => (
  <>
    {false && <QueueSettings />}
    
    <RecentDocumentsPanel activeTab={activeTab} />
    <PlatformFilesPanel activeTab={activeTab} user={user} integrationStatus={integrationStatus} onRefreshStatus={() => fetchIntegrationStatus(user)} />
    
    <ManufacturerCoverage 
      onAddManufacturer={() => setShowAddManufacturerModal(true)}
      temporaryManufacturers={temporaryManufacturers}
    />

    <div style={{ ...styles.grid, gridTemplateColumns: '1fr' }}>
      <DropBoxComponent
        label="Spec Sheet to Update"
        file={updateSpecFile}
        dropzone={updateSpecDropzone}
        styles={styles}
        thumbnail={updateSpecThumbnail}
      />
    </div>

    <div style={{
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '8px'
    }}>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
      }}>
        <input
          type="checkbox"
          checked={enableWebCheck}
          onChange={(e) => setEnableWebCheck(e.target.checked)}
          style={{
            width: '16px',
            height: '16px',
            cursor: 'pointer'
          }}
        />
        <div>
          <div style={{ color: '#000000', marginBottom: '4px' }}>
            Enable Live Manufacturer Verification
          </div>
          <div style={{ 
            color: '#6b7280', 
            fontSize: '12px',
            lineHeight: '1.4'
          }}>
            Check manufacturer websites for discontinued products, updated specifications, and new datasheets. 
            This may increase processing time but provides more accurate results.
          </div>
        </div>
      </label>
    </div>

    <div style={styles.buttonContainer}>
    <UsageBar featureKey="specUpdate" getFlag={getFlag} label="Spec Update" />
      <button
        onClick={handleSpecUpdateSubmit}
        disabled={updateLoading}
        style={{
          ...styles.button,
          ...(updateLoading ? styles.buttonDisabled : {})
        }}
      >
        {updateLoading ? "Updating..." : enableWebCheck ? "Check for Updates (with Web Data)" : "Check for Updates"}
      </button>
      
      {showUpdateClearButton && (
        <button
          onClick={handleClearAll}
          style={styles.clearButton}
        >
          🗑️ Clear All
        </button>
      )}
    </div>

    <QueueProgressTracker 
      loading={false}
      builderLoading={false}
      updateLoading={updateLoading}
      rfiLoading={false}
    />

    {!useQueue && updateLoading && (
      <div style={styles.progressContainer}>
        <div style={styles.progressHeader}>
          <h3 style={styles.progressTitle}>
            {enableWebCheck ? "Checking Manufacturer Updates & Web Data" : "Checking Manufacturer Updates"}
          </h3>
          <span style={styles.progressPercentage}>{Math.round(getProgressPercentage())}%</span>
        </div>
        
        <div style={styles.progressBar}>
          <div 
            style={{
              ...styles.progressFill,
              width: `${getProgressPercentage()}%`
            }}
          ></div>
        </div>
        
        <div>
          <p style={styles.progressStage}>
            {enableWebCheck && progressStage ? progressStage.replace("Checking Manufacturer Updates", "Checking Manufacturer Updates & Web Data") : progressStage}
          </p>
          {progressDetails && (
            <p style={styles.progressDetails}>
              {enableWebCheck ? progressDetails + " (including live web verification)" : progressDetails}
            </p>
          )}
        </div>
      </div>
    )}

    {updateError && <p style={styles.errorText}>{updateError}</p>}

    {updateReport && !updateLoading && (
      <div style={styles.resultContainer}>
        <h3 style={styles.resultTitle}>
          Update Check Complete! {enableWebCheck && "(with Web Verification)"}
        </h3>
        <pre style={styles.reportBox}>{updateReport}</pre>

        <div style={styles.downloadContainer}>
          {updatePdfBase64 && (
            <button
              onClick={() => handleDownloadPDF(updatePdfBase64, updateFilename)}
              style={{...styles.downloadButton, ...styles.pdfButton}}
            >
              Download PDF
            </button>
          )}
          <button
            onClick={() => handleDownloadWord(updateReport, updateFilename)}
            style={{...styles.downloadButton, ...styles.wordButton}}
          >
            Download Word (.doc)
          </button>
        </div>
      </div>
    )}
  </>
);

const getActiveTabContent = () => {
  switch (activeTab) {
    case 'spec-check':
      return renderSpecCheck();
    case 'spec-builder':
      return renderSpecBuilder();
    case 'spec-update':
      return renderSpecUpdate();
    case 'rfi-check':
      return renderRfiCheck();
    case 'shop-drawing-check':
      return renderShopDrawingCheck();
    case 'track-changes':
      return renderTrackChanges();
    case 'drawing-compare':
      return (
        <>
                    <RecentDocumentsPanel activeTab={activeTab} getFlag={getFlag} />
          <PlatformFilesPanel activeTab={activeTab} user={user} integrationStatus={integrationStatus} onRefreshStatus={() => fetchIntegrationStatus(user)} />
          <DrawingCompare user={user} externalNewFile={drawingCompareNewFile} externalBaselineFile={drawingCompareBaselineFile} onExternalNewFileConsumed={() => setDrawingCompareNewFile(null)} onExternalBaselineFileConsumed={() => setDrawingCompareBaselineFile(null)} />
        </>
      );
    case 'design-vs-shop':
      return (
        <>
          <RecentDocumentsPanel activeTab={activeTab} getFlag={getFlag} />
          <PlatformFilesPanel activeTab={activeTab} user={user} integrationStatus={integrationStatus} onRefreshStatus={() => fetchIntegrationStatus(user)} />
          <DesignVsShopDrawing
            user={user}
            externalDesignFile={designVsShopDesignFile}
            externalShopFile={designVsShopShopFile}
            onExternalDesignFileConsumed={() => setDesignVsShopDesignFile(null)}
            onExternalShopFileConsumed={() => setDesignVsShopShopFile(null)}
            getFlag={getFlag}
          />
        </>
      );
      case 'ca-plus':
      return (
        <>
          <RecentDocumentsPanel activeTab={activeTab} />
          <CaPlusTab user={user} getFlag={getFlag} />
        </>
      );
    case 'integrations': {
      const intFlag = getFlag('integrations');
      if (intFlag.visible === false) return null;
      if (!intFlag.enabled) return (
        <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: '16px', padding: '48px 40px', maxWidth: '600px', margin: '40px auto', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>Platform Integrations Unavailable</h2>
          <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>Contact your AECWorks AI administrator to upgrade your account.</p>
        </div>
      );
      return <IntegrationsTab user={user} getAuthToken={() => localStorage.getItem('auth0_token')} onStatusChange={() => fetchIntegrationStatus(user)} getFlag={getFlag} />;
    }

case 'settings':
  return (
    <div>
      <FirmProfileSettings user={user} />
      <div style={{ marginTop: '32px' }}>
        <HouseRulesSettings user={user} />
      </div>
      <div style={{ marginTop: '32px' }}>
        <BillingSettings user={user} />
      </div>
      <div style={{ marginTop: '32px', padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#000000', marginBottom: '8px' }}>
          Getting Started Guide
        </h3>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
          Replay the onboarding walkthrough to get a quick tour of AW AI Tools.
        </p>
        <button
          onClick={() => {
            localStorage.removeItem('zt_onboarded_v1');
            window.location.reload();
          }}
          style={{
            backgroundColor: '#D4AF37',
            color: '#000000',
            fontWeight: '600',
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ▶ Replay Onboarding Tour
        </button>
      </div>
    </div>
  );

    case 'faq':
      return <FAQSection getFlag={getFlag} />;
    case 'general':
      return (
        <div style={{ maxWidth: '640px', margin: '60px auto', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>💬</div>
          <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#000000', marginBottom: '8px' }}>
            General Consultation
          </h2>
          <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6', marginBottom: '24px' }}>
            Ask anything about specs, codes, submittals, or construction practice — or attach a document
            and discuss it directly. This mode won't run a formal tool automatically; it's built for
            open-ended, ad-hoc conversation.
          </p>
          <div style={{ textAlign: 'left', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px 20px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
              Try asking
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#374151', lineHeight: '2' }}>
              <li>"What's the typical wind-load requirement for a rooftop unit curb?"</li>
              <li>"I've attached a shop drawing — does anything look off to you?"</li>
              <li>"Summarize the key differences between ASCE 7-16 and 7-22 for this."</li>
            </ul>
          </div>
        </div>
      );
    default:
      return renderSpecCheck();
  }
};
const getTabDescription = () => {
  switch (activeTab) {
    case 'spec-check':
      return "Upload specifications and submittal — will cross-reference and flag every compliance gap.";
    case 'spec-builder':
      return "Upload a Master Spec Sheet and Template to generate a custom specification outline.";
    case 'spec-builder':
      return "Upload a master spec or FF&E material list and a template to generate a complete specification section — CSI-formatted or interior finish schedule style.";
    case 'spec-update':
      return "Upload your spec sheet to check for manufacturer updates and discrepancies.";
    case 'rfi-check':
      return "Upload an RFI and your project documentation to generate response analysis and draft answers.";
    case 'shop-drawing-check':
      return "Upload any submittal package — shop drawings, FF&E schedules, finish schedules, cut sheets, or Excel files. AECWorks AI cross-references between files and flags red flags across all document types.";
    case 'design-vs-shop':
      return "Upload your design / contract drawings and the contractor's shop drawings — AECWorks AI cross-references both sets to identify deviations from design intent.";
    case 'track-changes':
      return "Upload master spec with track changes and customer spec to identify gaps and missing requirements.";
    case 'drawing-compare':
        return "Compare any two drawing sets — floor plans vs. elevations, layouts vs. RCPs, or revision-to-revision. Every change identified automatically, no manual overlay required.";
    case 'faq':
      return "Learn how to interpret compliance scores, status indicators, and report sections.";
    case 'settings':
     return "Configure firm-specific compliance rules and preferences applied to every spec review.";
    default:
      return "Connect to your preferred productivity platform - Procore & Autodesk.";
    case 'ca-plus':
      return "Review Submittals, RFIs, and Field Reports with AI-drafted comments.";
  }
};

  // Domain not authorized
  if (isAuthenticated && termsAccepted && appAccess === false) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: '#fff', border: '2px solid #D4AF37', borderRadius: '16px', padding: '48px 40px', maxWidth: '480px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,.1)' }}>
          <img src="https://s3.us-east-1.amazonaws.com/technolica.com/AECWORKS-LOGO.png?format=1500w" alt="AECWorks AI" style={{ height: '36px', marginBottom: '32px' }} />
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>Access Not Authorized</h2>
          <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6', marginBottom: '24px' }}>
            Your organization (<strong>{user?.email?.split('@')[1]}</strong>) does not currently have access to AECWorks AI Tools.
          </p>
          <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '32px' }}>Contact your administrator or <strong>hello@aecworks.ai</strong></p>
          <button onClick={() => logout({ returnTo: window.location.origin })} style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (isAuthenticated && termsAccepted) {
    return (
      <div style={{
        ...styles.container,
        ...(activeMode === 'manager' ? {
          overflow: 'hidden',
          height: '100vh',
          position: 'fixed',
          width: '100%'
        } : {})
      }}>
      <TopBar user={user} activeMode={activeMode} onModeChange={(mode) => {
          if (mode === 'manager') setManagerKey(k => k + 1);
          if (mode === 'traditional' && activeTab === 'general') setActiveTab('spec-check');
          setActiveMode(mode);
        }} activeTab={activeTab} onLogout={logout} tier={licenseTier} managerLocked={managerModeLocked} />
        {false && (
          <UserProfile user={user} onLogout={logout} />
        )}

        {activeMode === 'manager' && (
          <LeftNav
            activeTab={activeTab}
            onTabChange={setActiveTab}
            getFlag={getFlag}
            cachedDocs={cachedDocuments.map(d => ({
              id: d._id,
              name: d.originalName,
              docType: d.docType,
              mimeType: d.mimeType || 'application/pdf'
            }))}
            usageCount={getFlag('specCheck').usedThisMonth || 0}
            usageLimit={getFlag('specCheck').monthlyLimit || 100}
            activeMode={activeMode}
            vaultDrawings={vaultDrawings}
            onToolSelect={(label) => {
              setManagerInputPrefill(`Run ${label}`);
            }}
          />
        )}

        {activeMode === 'manager' && (
        <ManagerSidebar
            key={managerKey}
            activeTab={activeTab}
            user={user}
            width={sidebarWidthForLayout}
            resizable={activeTab === 'general'}
            onWidthChange={handleGeneralSidebarWidthChange}
            cachedDocs={cachedDocuments.map(d => ({
              id: d._id,
              name: d.originalName,
              docType: d.docType,
              mimeType: d.mimeType || 'application/pdf'
            }))}
            vaultDrawings={vaultDrawings}
            onResult={(result) => {
              setManagerResults(prev => ({ ...prev, [activeTab]: result }));
              setLastResultSummary({
                toolLabel: result.toolLabel,
                timestamp: result.timestamp,
                snippet: (result.report || '').split('\n').filter(l => l.trim()).slice(0, 5).join(' '),
                fullReport: result.report || ''
              });
            }}
            lastResultSummary={lastResultSummary}
            tier={licenseTier}
            inputPrefill={managerInputPrefill}
            onInputPrefillUsed={() => setManagerInputPrefill('')}
            onVaultChange={() => fetchVaultDrawings(user)}
            onCacheChange={() => fetchCachedDocuments()}
            onLoadingChange={setManagerRunning}
          />
        )}

        <div style={{
          ...styles.card,
          ...(activeMode === 'manager' ? {
            marginLeft: '220px',
            marginRight: `${sidebarWidthForLayout}px`,
            maxWidth: 'none',
            width: 'auto',
            boxSizing: 'border-box',
            padding: 0,
            background: 'transparent',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            borderRadius: 0,
            marginTop: 0
          } : {
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: '1100px',
            width: '100%'
          })
        }}>
          {activeMode === 'manager'
            ? FUNCTION_TABS.includes(activeTab)
              ? (
                <div style={{
                  background: '#ffffff',
                  height: 'calc(100vh - 52px)',
                  overflowY: 'auto',
                }}>
                  {getActiveTabContent()}
                </div>
              )
: <ResultsPanel
    result={managerResults[activeTab] || null}
    activeTab={activeTab}
    onClear={() => setManagerResults(prev => { const n = { ...prev }; delete n[activeTab]; return n; })}
    cachedDocs={cachedDocuments.map(d => ({ id: d._id, name: d.originalName, docType: d.docType }))}
    vaultDrawings={vaultDrawings}
    isRunning={managerRunning}
  />
            : (
              <>
                <div style={styles.header}>
                  <img src={branding?.logo || "https://zonetap-ai.s3.us-east-1.amazonaws.com/AECWORKS-LOGO.png?format=1500w"} alt={branding?.name || "AECWorks AI"} style={styles.logo} />
                  <h1 style={styles.title}>AECWorks AI Platform</h1>
                  <p style={styles.subtitle}>{getTabDescription()}</p>
                </div>
                <TabNavigation
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  getFlag={getFlag}
                />
                {getActiveTabContent()}
              </>
            )
          }
        </div>

                <OnboardingWalkthrough />


        <AddManufacturerModal
          isOpen={showAddManufacturerModal}
          onClose={() => setShowAddManufacturerModal(false)}
          onSubmit={handleAddManufacturer}
        />

        {showSectionsDialog && (
          <div style={styles.dialogOverlay}>
            <div style={styles.dialogContent}>
              <h3 style={{ marginBottom: '16px', color: '#000000' }}>
                Specify Required Sections
              </h3>
              <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
                List the specific sections you need in your specification outline:
              </p>
              <textarea
                value={requiredSections}
                onChange={(e) => setRequiredSections(e.target.value)}
                placeholder="Example:
1. General Requirements and Scope
2. Products and Materials 
3. Execution and Installation
4. Quality Control and Testing
5. Submittal Requirements
6. Warranty and Maintenance
7. Safety Requirements
8. Environmental Considerations

(Customize these sections based on your project needs)"
                style={{...styles.textarea, minHeight: '180px'}}
              />
              <div style={styles.dialogButtons}>
                <button
                  onClick={() => {
                    setShowSectionsDialog(false);
                    setRequiredSections('');
                  }}
                  style={{
                    ...styles.dialogButton,
                    backgroundColor: '#6b7280',
                    color: 'white'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBuilderProcess}
                  style={{
                    ...styles.dialogButton,
                    backgroundColor: '#D4AF37',
                    color: '#000000'
                  }}
                >
                  Generate Spec Outline
                </button>
               </div>
            </div>
          </div>
        )}

        {showIdleWarning && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              backgroundColor: 'white', borderRadius: '12px', padding: '28px 32px',
              maxWidth: '380px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏱️</div>
              <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#000000', marginBottom: '8px' }}>
                Still there?
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                You'll be logged off due to inactivity in <strong>{idleSecondsLeft}s</strong>.
              </p>
              <button
                onClick={stayLoggedIn}
                style={{
                  backgroundColor: '#D4AF37', color: '#000000', fontWeight: '700',
                  padding: '10px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px',
                }}
              >
                Stay Logged In
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <TermsModal 
        isOpen={showTermsModal}
        onAccept={handleAcceptTerms}
        onDecline={handleDeclineTerms}
      />
      {isAuthenticated && !termsAccepted && !showTermsModal && <LoadingSpinner />}
    </>
  );
  }

